/**
 * The sketch's editable state, and the operations on it.
 *
 * This sits between the raw geometry engine (src/geometry, pure functions) and
 * the DOM (canvas.js, table.js). It holds the mutable working state — strokes
 * being drawn, the cleaned wall chain, the current selection — and exposes
 * intent-level operations (cleanup, edit a length, split a wall) that call the
 * engine and keep everything consistent.
 *
 * It is deliberately DOM-free so it can be unit tested. Rendering reads from it;
 * it never touches rendering.
 *
 * Persistence note: this is the in-memory working copy. When the sync engine
 * exists, these same wall objects map onto sketch.walls rows (id, sequenceIndex,
 * wallNumber, angleDeg, lengthUnits, isMeasured, segmentKind) — the field names
 * here were chosen to match the schema so that mapping stays trivial.
 */
import {
  advance,
  buildWallHeightProfile,
  distance,
  formatLength,
  fromCvAngle,
  isClosed,
  isCvAngle,
  median,
  orientAndNumber,
  parseLength,
  profileIsFlat,
  profileMaxHeight,
  solveClosure,
  strokeFormsClosedLoop,
  strokesToWalls,
} from '../geometry/index.js';
import { layoutSections } from '../geometry/section-layout.js';
import { carcassExtent, placeVerticalStack, stackGapUnits } from '../geometry/stacking.js';
import { soffitCapOver } from '../geometry/soffit.js';
import { ensureInteriorElementIds, interiorBand, interiorCounts, isSpaceKind, normalizeDoors, normalizeInterior, solveInterior } from '../geometry/interior.js';
import { nearestLegalPanelHeight } from '../geometry/system-holes.js';
import { ceilingOpeningGeometry } from '../geometry/ceiling-opening.js';
import { applyCountsToInterior, defaultInteriorForPreset, interiorSolveOpts } from '../config/business-rules.js';

/** How many per-sketch custom helper lines the designer may set (owner,
 *  2026-07-28 — was a single line). */
export const MAX_CUSTOM_HELPER_LINES = 3;

/**
 * Numbers obstructions for the drawings: `O1`, `O2`, `W1`, `S1` … (owner,
 * 2026-08-05 — "just the code").
 *
 * A drawing has room for a key, not a description. Beside a 3"-wide outlet even
 * the word "Outlet" is wider than the thing it names, and two of them collide.
 * The full wall number, description and measurements belong in the measurement
 * sheet's per-wall table, which has columns for them.
 *
 * Numbered ACROSS THE SKETCH, not per wall, so a code is unique on its own. The
 * table already carries the wall number, so per-wall numbering would put two
 * different `O1`s on one plan and force every reference to name a wall to mean
 * anything.
 *
 * Order is wall number, then position along the wall — the way someone walks a
 * room — so the numbering is stable and reads in the order it is measured. Ties
 * fall back to the id purely so the result can never depend on array order.
 *
 * A type with no code is skipped rather than given a made-up one: an unlabelled
 * fixture on the drawing is honest, an invented code is not.
 *
 * @param {Array<{id:string, code?:string, wallNumber?:number|null,
 *   offsetAlongWall?:number}>} items
 * @returns {Map<string, string>} obstruction id → display code
 */
export function assignObstructionCodes(items) {
  const list = (Array.isArray(items) ? items : []).filter((o) => o && o.id && o.code);
  const sorted = list.slice().sort((a, b) => {
    // Unnumbered hosts (partition faces) sort after the numbered walls.
    const aw = Number.isFinite(a.wallNumber) ? a.wallNumber : Number.MAX_SAFE_INTEGER;
    const bw = Number.isFinite(b.wallNumber) ? b.wallNumber : Number.MAX_SAFE_INTEGER;
    if (aw !== bw) return aw - bw;
    const ao = Number.isFinite(a.offsetAlongWall) ? a.offsetAlongWall : 0;
    const bo = Number.isFinite(b.offsetAlongWall) ? b.offsetAlongWall : 0;
    if (ao !== bo) return ao - bo;
    return String(a.id).localeCompare(String(b.id));
  });
  const seen = new Map();
  const out = new Map();
  for (const item of sorted) {
    const code = String(item.code).toUpperCase();
    const next = (seen.get(code) ?? 0) + 1;
    seen.set(code, next);
    out.set(item.id, `${code}${next}`);
  }
  return out;
}

export function createSketchModel(rules, options = {}) {
  const makeId = options.makeId ?? (() => crypto.randomUUID());

  const state = {
    /** Completed raw strokes awaiting cleanup: array of point arrays. */
    strokes: [],
    /** The stroke currently being drawn (points), or null. */
    current: null,
    /** Cleaned, oriented, numbered wall chain. */
    walls: [],
    /** id of the selected wall, or null. */
    selectedWallId: null,
    /** Whether this room should reconcile the plan perimeter into a closed display. */
    snapClosed: true,
    /** Durable topology intent: cleanup proved that this 3+ wall perimeter was drawn as a loop. */
    closedPerimeter: false,
    /** Closure diagnostics from the last solve. */
    closure: { residualGapUnits: 0, closed: true, degenerate: false, solved: false },
    /** px-per-unit from the last solve; used to size a door in real units. */
    pxPerUnit: null,
    /** Durable plan scale established by the initial longest-wall measurement. */
    scalePxPerUnit: null,
    /** Room-level heights for elevations, in the room's unit of measure. */
    ceilingHeightUnits: rules.defaultCeilingHeightUnits,
    /** Flat is the common case; stored slope points remain dormant when true. */
    ceilingFlat: true,
    /**
     * Sloped ceilings (ADR 0044). Heights attach to the chain's CORNERS, not to
     * walls: two walls meeting at a corner share that point, and the ceiling is
     * continuous across it, so they cannot physically differ there. One shared
     * number per corner makes that true by construction — and means editing a
     * corner slopes BOTH walls that touch it, which is the "wouldn't it have to
     * auto adjust?" the owner asked for (2026-07-29).
     *
     * Keyed by the id of the segment whose END the corner is, so a closed chain
     * of N segments has N corners. Missing = the room's ceiling height, which is
     * why a flat room needs no entries at all.
     */
    cornerHeights: {},
    /**
     * The height at an OPEN chain's very first start — the one corner no
     * segment's end lands on. Ignored for a closed chain, where the last
     * segment's end IS the first's start.
     */
    chainStartHeightUnits: null,
    /**
     * Interior height breakpoints per wall, `{ [wallId]: [{atUnits, heightUnits}] }`
     * — the "level for the first 3 feet, then slope" case. Measured from the
     * wall's start. Interior only: the ends are corners, and a corner's height
     * is shared, so it always wins.
     */
    wallHeightBreaks: {},
    doorHeightUnits: rules.defaultDoorHeightUnits,
    /** Ceiling-hosted architectural openings, measured from the plan origin. */
    ceilingOpenings: [],
    /**
     * Per-sketch show/hide choices for elevation helper lines, keyed by label:
     * { '84" panel': true } means "show it here" regardless of the rule's
     * default_visible. Only EXPLICIT designer choices live in this map — an
     * absent label falls through to the rule default (currently: only the 96"
     * panel shows). Storing the choice rather than a hidden-list means a later
     * change to the shop default never silently flips a sketch the designer
     * already decided about.
     */
    helperLineOverrides: {},
    /**
     * Up to MAX_CUSTOM_HELPER_LINES extra helper lines at heights the designer
     * types (owner, 2026-07-28 — was a single line). Each slot is
     * { units: number|null, visible: boolean }; units null = empty slot (not
     * drawn). Kept separate from the rule-driven lines because it is per-sketch
     * data, not a shop-wide default. A pre-2026-07-28 sketch's single custom
     * line migrates into slot 0 on hydrate.
     */
    customHelperLines: [],
    /**
     * Items placed on walls (outlets, switches, windows, …). Each references a
     * wall by id and is positioned WALL-RELATIVE — offsetAlongWall from the
     * wall's start, offsetFromFloor to the item's bottom edge — never in canvas
     * coordinates, which would drift every time the closure solver moved a wall
     * (ADR 0008).
     */
    obstructions: [],
    /**
     * Segment id the designer pinned as Wall 1, overriding the automatic
     * bottom-left-corner rule. Null means "use the rule". Stored as an id rather
     * than a number because the number is an OUTPUT of numbering — pinning "W3"
     * would be circular, since renumbering is exactly what changes it.
     */
    wallOneOverrideId: null,
    /**
     * Freehand annotation strokes drawn OVER the room and elevations (the
     * design-sketching layer, ADR 0023). Each stroke is independent of the
     * geometry — erasing one never touches a wall. A stroke:
     *   { id, surface: 'plan'|'elevation', color, width, points: [{x,y}, …] }
     * Points live in the surface's own viewport coordinate space, so they pan
     * and zoom with the drawing they annotate.
     */
    annotations: [],
    /**
     * Per-wall DEPTH helper lines on the plan (ADR 0024): a thin line drawn
     * parallel to a wall, `offsetUnits` into the room from its inside face —
     * so a designer can keep a cabinet run (e.g. 24" base, 12" upper) to scale.
     * Each: { id, wallId, offsetUnits }. Keyed by wall like obstructions, and
     * pruned when the wall no longer exists.
     */
    planHelperLines: [],
    /** Whether depth helper lines are shown (per sketch). Hides all at once
     *  without deleting them, like the elevation helper-line toggles. */
    planHelperLinesVisible: true,
    /** Whether the to-scale 6" grid is shown (per sketch). ON by default
     *  (owner, 2026-08-09: "the default should be the grid being on"). */
    gridVisible: true,
    /** Whether SHELF SPACING dimensions are drawn on the elevation, and
     *  whether DRAWER FACE heights are (ADR 0061 item E — two independent
     *  toggles, per sketch, exactly like the two flags either side of them).
     *  Off by default: they are extra figures over the drawing. */
    shelfSpacingDimsVisible: false,
    drawerFaceDimsVisible: false,
    /** Whether CROWN is added, per sketch (owner, 2026-08-10). Off by default:
     *  crown is an upgrade, base trim is not. Priced per linear foot of the
     *  sections it runs across — see crownPricePerLinearFoot. */
    crownEnabled: false,
    /** Whether BASE TRIM is added, per sketch. Like crown, an explicit toggle
     *  (owner: "the ability to ADD crown and base … turned on/off"). Which
     *  sections it covers is then per-section — see `bottomShelf`. */
    baseTrimEnabled: false,
    /** Whether the storage-summary table is shown (per sketch). Off frees the
     *  screen space it occupies over the drawing. */
    storageSummaryVisible: true,
    /**
     * Interior / partition walls (ADR 0032): a two-sided wall that sticks INTO
     * the room. Adding one SPLITS the host wall into wall / partition / wall, so
     * the two flanking walls are ordinary, independently measurable walls — the
     * designer types each side's clear width and the app carries the partition's
     * own thickness. The footprint is a chain `opening` segment (drawn solid);
     * only its protrusion depth is stored here, keyed by that segment's id:
     *   { segmentId, depthUnits }
     * Thickness = the footprint segment's length. Pruned when the segment goes.
     */
    partitions: [],
    /**
     * Standard section-type LABELS anchored to a wall (ADR 0034): short codes
     * (DH, LH, …) that show on BOTH the plan and the elevation at the same
     * relative position on the wall. Wall-anchored like obstructions —
     *   { id, wallId, offsetAlongWall, offsetFromFloor, text, color }
     * offsetFromFloor only matters on the elevation; the plan uses just the
     * along-wall position. Pruned when the wall is gone.
     */
    wallLabels: [],
    /**
     * Wall SECTIONS (ADR 0037): typed cabinetry units (DH, LH, hamper…) laid
     * out along a wall between vertical panels, per-wall ordered by `order`:
     *   { id, wallId, order, typeCode, widthUnits, locked, fullPanelAfter,
     *     allowWide, depthUnits, heightUnits }
     * `widthUnits` is the OPENING width and only meaningful when `locked`;
     * unlocked sections auto-fill (src/geometry/section-layout.js) and their
     * computed opening is never stored — the layout is derived, always fresh.
     * Hosts are walls OR partition faces (resolveHost). Pruned with the host.
     */
    sections: [],
    /**
     * Shared-space split (owner, 2026-07-27): an A/B closet divides its
     * storage between two people. When enabled, sections can be ASSIGNED to a
     * person (index into `persons`; null = unassigned, counts only toward the
     * total) and the storage summary breaks out per-person columns.
     *   { enabled: boolean, persons: [nameA, nameB] }
     */
    sharedSpace: { enabled: false, persons: ['A', 'B'] },
    /**
     * The CLIENT's storage requirements, per storage-summary row (ADR 0043).
     *
     * Keyed exactly like a summary row (`${kind}:${code}`) so need and
     * capacity line up 1:1 and can never drift apart. Hang rows key by
     * CATEGORY (`hang:short` — ADR 0051 step 2, executed in ADR 0057); shoes
     * by type (`shoes:SS`); drawers collapse to `drawers:DRW`. Docs saved
     * before the re-keying carry `hang:<TYPE>` keys — migrated on hydrate by
     * migrateStorageNeedKeys() (business-rules.js). Each entry is
     * `{ existing: {space, a, b}, needed: {space, a, b}, shoeGroups: [] }` in
     * that row's own unit. Shoe groups are free-form footwear descriptions with
     * pair counts, pair width, and toe-to-heel length; their counts roll up to
     * the shoes row while their dimensions remain available for shelf design.
     *
     * TWO numbers because the designer captures what the client HAS today and,
     * only when it differs, what they WANT (owner, 2026-07-29): `needed` falls
     * back to `existing`, so entering one number is the common case.
     *
     * Both the whole-space slot and the per-person slots are stored, so turning
     * the shared-space toggle off and on again cannot destroy what was typed.
     */
    storageNeeds: {},
    storageNeedsSource: null,
    /**
     * Which requirement the storage summary measures the design against
     * (owner, 2026-07-29): `'need'` (what the client asked for) or `'current'`
     * (what they have today, so the box reads as "better/worse than their
     * current closet"). Sketch-level, persisted, default 'need'.
     *
     * The owner's words are Current/Need; the stored values match them, so the
     * code and the screen can't drift apart. Sketches written in the first hours
     * of this feature used `has`/`wants` and are normalised on load.
     */
    storageCompareTo: 'need',
    /**
     * Space-level section height override (owner, 2026-07-27): an 84" space
     * sets it once instead of per section. Resolution order for a section's
     * height: its own heightUnits (explicit pin) → this → the type's
     * defaultHeightUnits → the global rule. null = no override.
     */
    spaceHeightUnits: null,
    /**
     * Platform height for the whole space (ADR 0040 — garage systems): 0 =
     * on the floor (the default, ~95% of jobs); a positive value (typ. 3 or 6)
     * stands the cabinetry on a platform, shifting section geometry up per each
     * type's anchor (see geometry/platform.js). Sketch/space-wide, not per wall.
     */
    platformHeightUnits: 0,
    /**
     * Space-level countertop thickness (ADR 0041): the gap left above a BASE
     * section for its countertop when a unit stacks on it. Applies to every base
     * in the space; a section may override it. 0/null = no countertop.
     */
    countertopThicknessUnits: 0,
    /**
     * "To target height" mode (ADR 0041, revised 2026-07-29): stacks run up to a
     * designer-set datum, so a stack's TOP unit autosizes to fill.
     *
     * It was "to the ceiling", derived from `ceilingHeightUnits`. Once a room
     * can have walls of DIFFERENT heights (sloped tops), "the ceiling" stops
     * being one number and the autosize would have to guess which one — so the
     * designer states the target instead (owner, 2026-07-29: "typically, but not
     * always, the height of the shortest wall").
     */
    toTargetHeight: false,
    /**
     * The datum stacks run to. null = the room's ceiling height, which is what
     * every pre-2026-07-29 sketch meant by "to the ceiling", so those load
     * unchanged.
     */
    stackTargetHeightUnits: null,
    /**
     * Per-WALL overrides of that datum, `{ [wallId]: units }` (owner,
     * 2026-08-04). SPARSE on purpose: a wall absent from this map follows the
     * space, so with no overrides the whole room is the same, and editing the
     * space figure still moves every wall that never disagreed with it.
     */
    wallStackTargets: {},
    /** Crown-molding allowance below the target (default from the rule, 3"). */
    crownAllowanceUnits: null,
    /** Crown/base VISIBLE heights, per space (owner, 2026-08-28: defaults
     *  "may vary/need to be overridden on occasion"). null = the admin rule
     *  (`crown_moulding_height_units` / `base_trim_height_units`). Distinct
     *  from `crownAllowanceUnits`, which is headroom, not trim. */
    crownMouldingHeightUnits: null,
    baseTrimHeightUnits: null,
    /**
     * Space pricing choices + the last computed totals (ADR 0058, plan §5/4e).
     * `level` is the material level picked for the space (1..the multiplier
     * rule's levels; default 1); `countertopMaterial` a materialCode from the
     * countertop rule, or null = none picked. `low`/`high`/`needsPricing` are
     * the STAMPED summary of the last computation (src/pricing/space-pricing.js
     * derives them fresh every render; the stamp exists so the totals persist
     * with the doc and the preferred version carries them — ADR 0021/0058).
     */
    pricing: { level: 1, countertopMaterial: null, low: null, high: null, needsPricing: 0 },
    /**
     * Basic mode (ADR 0059; the portal repo's docs/ROOM_SKETCH_UI_PLAN.md).
     * The Basic/Detailed toggle is a VIEW change, never a data migration —
     * the sketch canvas stays available in both modes; Basic simplifies the
     * SECTIONS panel into staged bucket cards.
     *   mode       'basic' | 'detailed' (persisted per sketch).
     *   spaceType  key into the basic_space_types rule (filters the bucket
     *              palette via the availability matrix); null = the rule's
     *              first type.
     *   bounds     the designer-picked span bounds
     *              {lowHeightIn, lowDepthIn, highHeightIn, highDepthIn}, or
     *              null = the basic_default_bounds rule (84×12 → 95×16).
     *   cards      staged bucket cards [{id, bucketKey, qty, drawers, wallId}]
     *              — wallId is an optional wall ASSIGNMENT (spatial context
     *              only; it never narrows the card's span). Placement is the
     *              separate conversion below (placeBucketUnit). `drawers` is
     *              the DRAWER ADDER count PER UNIT, and is part of the card
     *              MERGE KEY alongside bucket and wall, because merging two
     *              different per-unit counts would silently reprice both.
     */
    basic: { mode: 'detailed', spaceType: null, bounds: null, cards: [] },
    /**
     * Woven wall numbers for partition FACES, `{ [footprintId]: [numA, numB] }`,
     * recomputed each renumber. A partition's two faces take the next two
     * numbers AT the footprint's position in the traversal, so numbering runs
     * continuously — left flank, face A, face B, right flank — instead of the
     * faces being tacked on at the end (owner, ADR 0032).
     */
    partitionFaceNumbers: {},
  };

  /**
   * Re-orients and renumbers the chain, honouring a pinned Wall 1.
   *
   * Every path that changes the set of segments goes through here, so the pin
   * cannot be silently forgotten by one of them. A pin that no longer resolves
   * (its wall was merged into a neighbour, or removed) is cleared here rather
   * than left to rot, so the UI never claims a pin that is not in effect.
   */
  function renumber(segments) {
    const next = orientAndNumber(segments, {
      numberingDirection: rules.numberingDirection,
      wallOneId: state.wallOneOverrideId,
    });
    if (state.wallOneOverrideId && !next.some((s) => s.id === state.wallOneOverrideId)) {
      state.wallOneOverrideId = null;
    }
    return next;
  }

  /**
   * Rewrites `wallNumber` on the ordered chain so a partition's two FACES take
   * the next two numbers AT the footprint's position — the developed walk order
   * (left flank → face A → face B → right flank) — rather than being numbered
   * after the whole loop. Boundary walls past a partition are bumped accordingly.
   * The face numbers are stashed in `state.partitionFaceNumbers` for
   * `partitionFaceWalls()` to read, so plan and elevation agree. With no
   * partitions this reproduces the plain 1..n numbering exactly.
   */
  function weavePartitionNumbers(orderedChain) {
    const partitionIds = new Set(state.partitions.map((p) => p.segmentId));
    const faceNumbers = {};
    let counter = 1;
    for (const seg of orderedChain) {
      if ((seg.segmentKind ?? 'wall') === 'wall') {
        seg.wallNumber = counter++;
      } else if (partitionIds.has(seg.id)) {
        faceNumbers[seg.id] = [counter, counter + 1]; // face A, then face B
        counter += 2;
        seg.wallNumber = null; // the footprint itself stays unnumbered
      } else {
        seg.wallNumber = null; // a door opening consumes no number
      }
    }
    state.partitionFaceNumbers = faceNumbers;
  }

  /**
   * Pins a wall as Wall 1 and renumbers everything else from it in the
   * configured direction. The automatic rule is a heuristic about room shape and
   * misfires on irregular plans; this lets the designer state the answer
   * directly rather than fight the geometry.
   *
   * @returns {boolean} false if the id is not a wall (openings cannot be Wall 1)
   */
  function setWallOne(id) {
    const wall = state.walls.find((w) => w.id === id);
    if (!wall || (wall.segmentKind ?? 'wall') !== 'wall') return false;
    state.wallOneOverrideId = id;
    state.walls = renumber(state.walls);
    resolve();
    return true;
  }

  /** Drops the pin, handing Wall 1 back to the automatic rule. */
  function clearWallOne() {
    if (!state.wallOneOverrideId) return false;
    state.wallOneOverrideId = null;
    state.walls = renumber(state.walls);
    resolve();
    return true;
  }

  /** True when the pin is set AND actually in effect on the current chain. */
  function isWallOnePinned() {
    return Boolean(state.wallOneOverrideId);
  }

  /**
   * Every model point currently on the PLAN — for fit-view. Includes the walls
   * AND the freehand plan notes, which are often drawn OUTSIDE the walls; fit
   * must frame the whole used canvas so a margin note is never cropped (owner,
   * 2026-07-25). Elevation annotations are excluded: their points are
   * panel-relative units (ADR 0026), not plan coordinates.
   */
  function allPoints() {
    const pts = [];
    for (const w of state.walls) {
      pts.push(w.start, w.end);
    }
    if (state.walls.length && state.pxPerUnit > 0) {
      for (const opening of state.ceilingOpenings) {
        const geometry = ceilingOpeningGeometry(state.walls, opening, { pxPerUnit: state.pxPerUnit });
        if (!geometry) continue;
        pts.push(...geometry.points);
        if (geometry.ladder && opening.showLadderExtended !== false) pts.push(geometry.ladder.end);
      }
    }
    for (const a of state.annotations ?? []) {
      if (a.surface === 'plan' && Array.isArray(a.points)) {
        for (const p of a.points) if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) pts.push(p);
      }
    }
    if (pts.length === 0) {
      for (const s of state.strokes) for (const p of s) pts.push(p);
      if (state.current) for (const p of state.current) pts.push(p);
    }
    return pts;
  }

  function beginStroke(modelPoint) {
    state.current = [modelPoint];
  }

  function extendStroke(modelPoint) {
    if (!state.current) return;
    state.current.push(modelPoint);
  }

  /** Commits the in-progress stroke if it is substantial enough to be real. */
  function endStroke() {
    if (state.current && state.current.length > 2) {
      state.strokes.push(state.current.slice());
    }
    state.current = null;
  }

  /**
   * Records a completed stroke drawn elsewhere (the canvas owns the in-progress
   * gesture and hands over the finished points). Kept separate from the
   * begin/extend/end trio so either input path can be used.
   */
  function addStroke(points) {
    if (Array.isArray(points) && points.length > 2) {
      state.strokes.push(points.slice());
    }
  }

  function undoStroke() {
    state.strokes.pop();
  }

  function clear() {
    state.strokes = [];
    state.current = null;
    state.walls = [];
    state.closedPerimeter = false;
    state.selectedWallId = null;
    state.closure = { residualGapUnits: 0, closed: true, degenerate: false, solved: false };
    state.pxPerUnit = null;
    state.scalePxPerUnit = null;
    state.ceilingHeightUnits = rules.defaultCeilingHeightUnits;
    state.ceilingFlat = true;
    state.cornerHeights = {};
    state.chainStartHeightUnits = null;
    state.wallHeightBreaks = {};
    state.doorHeightUnits = rules.defaultDoorHeightUnits;
    state.helperLineOverrides = {};
    state.customHelperLines = [];
    state.obstructions = [];
    state.ceilingOpenings = [];
    state.wallOneOverrideId = null;
    state.annotations = [];
    state.planHelperLines = [];
    state.planHelperLinesVisible = true;
    state.gridVisible = true;
    state.shelfSpacingDimsVisible = false;
    state.drawerFaceDimsVisible = false;
    state.storageSummaryVisible = true;
    state.partitions = [];
    state.partitionFaceNumbers = {};
    state.wallLabels = [];
    state.sections = [];
    state.sharedSpace = { enabled: false, persons: ['A', 'B'] };
    state.spaceHeightUnits = null;
    state.platformHeightUnits = 0;
    state.countertopThicknessUnits = 0;
    state.toTargetHeight = false;
    state.stackTargetHeightUnits = null;
    state.wallStackTargets = {};
    state.crownAllowanceUnits = null;
    state.crownMouldingHeightUnits = null;
    state.baseTrimHeightUnits = null;
    state.pricing = { level: 1, countertopMaterial: null, low: null, high: null, needsPricing: 0 };
    state.basic = { mode: 'detailed', spaceType: null, bounds: null, cards: [] };
    // The client's storage figures belong to THEIR sketch. A new sketch that
    // inherited them would quietly measure the design against the previous
    // client's closet (owner report, 2026-08-09). Copies are unaffected:
    // duplication clones the whole doc rather than clearing state.
    state.storageNeeds = {};
    state.storageNeedsSource = null;
    state.storageCompareTo = 'need';
  }

  // ---- annotations: the freehand design-sketching layer (ADR 0023) ---------

  /**
   * Adds a finished freehand stroke on a surface.
   *
   * PLAN strokes are in plan viewport coordinates (the plan never re-flows).
   * ELEVATION strokes are ANCHORED to a wall (`wallId`) and their points are in
   * UNITS relative to that wall's panel — x along the wall from the panel's left,
   * y up from the floor — so that hiding/showing other walls (which re-flows and
   * rescales the elevation) moves the stroke with its panel (ADR 0026).
   *
   * @returns {string|null} the new stroke's id, or null if too short to keep.
   */
  function addAnnotation({ surface, color, width, points, wallId = null }) {
    if (!Array.isArray(points) || points.length < 2) return null;
    const stroke = {
      id: makeId(),
      surface: surface === 'elevation' ? 'elevation' : 'plan',
      color: typeof color === 'string' ? color : '#e5484d',
      width: Number.isFinite(width) && width > 0 ? width : 2,
      wallId: typeof wallId === 'string' ? wallId : null,
      points: points.map((p) => ({ x: p.x, y: p.y })),
    };
    state.annotations.push(stroke);
    return stroke.id;
  }

  /**
   * Adds a TYPED TEXT note (as opposed to a freehand stroke) at a single anchor
   * point on a surface. Same anchoring rules as strokes: a PLAN note is in plan
   * viewport coordinates; an ELEVATION note is anchored to a wall panel in units
   * (ADR 0026/0033). Stored as an annotation with `kind: 'text'`.
   *
   * @returns {string|null} the note's id, or null if the text is empty.
   */
  function addTextAnnotation({ surface, text, color, point, fontSize, wallId = null }) {
    const value = String(text ?? '').trim();
    if (!value || !point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;
    const note = {
      id: makeId(),
      surface: surface === 'elevation' ? 'elevation' : 'plan',
      kind: 'text',
      text: value.slice(0, 200),
      color: typeof color === 'string' ? color : '#e5484d',
      // Size in DRAWING units, so a note keeps its size relative to the room as
      // the designer zooms — the same space the strokes live in. Absent means
      // "whatever the stylesheet says", which is every note written before the
      // size picker existed (owner, 2026-07-29).
      fontSize: Number.isFinite(fontSize) && fontSize > 0 ? fontSize : null,
      wallId: typeof wallId === 'string' ? wallId : null,
      points: [{ x: point.x, y: point.y }],
    };
    state.annotations.push(note);
    return note.id;
  }

  /**
   * Resizes an existing text note. Reached from the note's own context menu, so
   * a note that came out too small does not have to be deleted and retyped.
   */
  function setTextAnnotationSize(id, fontSize) {
    const note = state.annotations.find((a) => a.id === id && a.kind === 'text');
    if (!note) return false;
    if (!Number.isFinite(fontSize) || fontSize <= 0) return false;
    note.fontSize = fontSize;
    return true;
  }

  /**
   * Repositions a text note's anchor (ADR 0033). `point` is in the surface's
   * own coordinates — plan viewport px, or panel-relative units for the
   * elevation, in which case `wallId` re-anchors it to whatever panel it was
   * dragged onto. Returns false if the id is not a text note.
   */
  function moveTextAnnotation(id, point, wallId) {
    const a = state.annotations.find((x) => x.id === id && x.kind === 'text');
    if (!a || !point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
    a.points = [{ x: point.x, y: point.y }];
    if (wallId !== undefined) a.wallId = typeof wallId === 'string' ? wallId : null;
    return true;
  }

  /** Strokes on a given surface, in draw order. */
  function annotationsFor(surface) {
    return state.annotations.filter((a) => a.surface === surface);
  }

  /** Removes one stroke by id. Returns true if something was removed. */
  function removeAnnotation(id) {
    const before = state.annotations.length;
    state.annotations = state.annotations.filter((a) => a.id !== id);
    return state.annotations.length !== before;
  }

  /** Clears annotations — all of them, or just one surface's. */
  function clearAnnotations(surface) {
    if (surface) state.annotations = state.annotations.filter((a) => a.surface !== surface);
    else state.annotations = [];
  }

  /**
   * Clears only the FREEHAND strokes on a surface, keeping typed text notes —
   * text is a separate layer, so "Clear sketch" must not wipe notes (owner,
   * ADR 0033).
   */
  function clearStrokeAnnotations(surface) {
    state.annotations = state.annotations.filter((a) => !(a.surface === surface && a.kind !== 'text'));
  }

  /**
   * Snaps the accumulated strokes into walls, appends them to any existing
   * walls, re-orients and re-numbers the whole chain, then DISCARDS the raw
   * strokes (they have done their job — keeping them would show two drifting
   * copies of the same edge). Confirmed intended behavior by the project owner.
   */
  function cleanup() {
    // Capture the designer's topology intent BEFORE discarding the raw stroke.
    // A hand-drawn loop can stop near its start yet no longer meet after each
    // side is independently snapped to a 45° direction. Marking it closed now
    // lets solveClosure adjust only the still-auto lengths into a real polygon.
    const drewClosedLoop = state.strokes.some((stroke) => strokeFormsClosedLoop(stroke));
    const fresh = strokesToWalls(state.strokes, {
      snapIncrementDegrees: rules.snapIncrementDegrees,
      makeId,
    });
    if (fresh.length === 0) {
      // Nothing usable was drawn; leave existing walls untouched.
      state.strokes = [];
      state.current = null;
      return state.walls;
    }

    const combined = state.walls.concat(fresh);
    state.walls = renumber(combined);
    state.selectedWallId = state.walls[0]?.id ?? null;

    state.strokes = [];
    state.current = null;
    // Cleanup can replace walls (merges, renumbering), so anything pinned to a
    // wall that no longer exists is dropped rather than left dangling.
    pruneObstructions();
    if (state.walls.length > 2 && drewClosedLoop) state.closedPerimeter = true;
    resolve();
    state.closedPerimeter = state.walls.length > 2 && state.closure.openChain !== true;
    return state.walls;
  }

  function selectWall(id) {
    state.selectedWallId = id;
  }

  /**
   * Records a user-entered length for a wall and re-solves closure. Passing an
   * empty/blank value un-measures the wall, handing it back to the solver as a
   * free wall. Returns true when the input was understood.
   */
  function writeWallLength(id, rawValue, { measuredHere = false } = {}) {
    const wall = state.walls.find((w) => w.id === id);
    if (!wall) return false;

    const trimmed = String(rawValue ?? '').trim();
    if (trimmed === '') {
      wall.isMeasured = false;
      wall.measurementState = null;
      wall.lengthUnits = null;
      resolve();
      return true;
    }

    const parsed = parseLength(trimmed);
    if (parsed === null || parsed <= 0) return false; // reject rather than guess

    wall.isMeasured = true;
    wall.lengthUnits = parsed;
    if (measuredHere) wall.measurementState = 'measured';
    else if (wall.measurementState !== 'inherited') wall.measurementState = null;
    resolve();
    return true;
  }

  /**
   * A typed edit fixes geometry but is not evidence that this physical space
   * was measured. In particular, correcting an inherited copied value by hand
   * leaves it inherited until a capture device supplies a real reading.
   */
  function setWallLength(id, rawValue) {
    return writeWallLength(id, rawValue);
  }

  /** Records an actual Field Capture reading and clears copied provenance. */
  function setWallMeasuredLength(id, rawValue) {
    return writeWallLength(id, rawValue, { measuredHere: true });
  }

  /**
   * Confirms an auto (solver-derived) wall's length, locking in the value the
   * closure solver produced WITHOUT the designer re-typing it — the plan's
   * click-to-confirm chips call this. Stores the exact geometric length (not the
   * rounded display value) so confirming several walls can't accumulate rounding
   * error and open a residual gap. No-op (false) for a wall that is already
   * measured, an opening/partition, or before the room is solved.
   *
   * @param {string} id
   * @returns {boolean} true if the wall was confirmed
   */
  function confirmWallLength(id) {
    const wall = state.walls.find((w) => w.id === id);
    if (!wall) return false;
    if (wall.isMeasured) return false;
    if ((wall.segmentKind ?? 'wall') !== 'wall') return false;
    if (isPartition(wall.id)) return false;
    if (!state.pxPerUnit || state.pxPerUnit <= 0) return false;
    const units = wallLengthUnits(wall);
    if (!(units > 0)) return false;
    wall.isMeasured = true;
    wall.measurementState = null;
    wall.lengthUnits = units;
    resolve();
    return true;
  }

  /**
   * Corrects a segment's direction. Cleanup snaps to the nearest 45°, and it
   * sometimes snaps to the wrong one; this lets the designer pick the right
   * Cabinet Vision angle. Only the eight CV angles are accepted (the DB enforces
   * the same set). Changing a direction re-walks and re-closes the chain via the
   * solver, exactly like editing a length — the geometry is recomputed from
   * angles + lengths from Wall 1's anchor (ADR 0005), so downstream walls shift
   * and free walls absorb the correction.
   *
   * @param {string} id
   * @param {number} cvAngle one of CV_ANGLES (e.g. 0, 45, -90)
   * @returns {boolean} false if the id is unknown or the angle is not a CV angle
   */
  function setWallAngle(id, cvAngle) {
    const wall = state.walls.find((w) => w.id === id);
    if (!wall) return false;
    const angle = Number(cvAngle);
    if (!isCvAngle(angle)) return false;
    if (angle === wall.angleDeg) return true; // no change
    wall.angleDeg = angle;
    wall.internalAngle = fromCvAngle(angle);
    resolve();
    return true;
  }

  /** Sets whether the plan should reconcile a room into a snapped-closed display. */
  function setSnapClosed(value) {
    state.snapClosed = Boolean(value);
    return state.snapClosed;
  }

  /**
   * Keeps a physical wall's displayed length fixed while visual snap-close
   * reconciles measured variance. This never changes its canonical measured
   * value; it only removes that wall from the display solver's adjustable set.
   */
  function setWallSnapLocked(id, value) {
    const wall = state.walls.find((candidate) => candidate.id === id);
    if (!wall || (wall.segmentKind ?? 'wall') !== 'wall') return false;
    wall.snapLengthLocked = Boolean(value);
    return true;
  }

  /** Runs the closure solver over the current walls and stores the result. */
  function resolve() {
    if (state.walls.length === 0) {
      state.closure = { residualGapUnits: 0, closed: true, degenerate: false, solved: false, openChain: false };
      state.partitionFaceNumbers = {};
      return;
    }
    // Establish the drawing's scale ONCE. For a fresh sketch, the longest
    // physical wall becomes a real measured wall at the admin default (120"
    // today). For a legacy document, existing measured walls establish the
    // compatible median scale. From this point on, editing another wall changes
    // geometry without changing the pixels-per-unit ratio (owner, 2026-08-16).
    if (!(state.scalePxPerUnit > 0)) {
      const physical = state.walls.filter((wall) => (wall.segmentKind ?? 'wall') === 'wall');
      const measured = physical.filter((wall) => wall.isMeasured && wall.lengthUnits > 0);
      const anchor = physical.reduce((best, wall) => (
        !best || distance(wall.start, wall.end) > distance(best.start, best.end) ? wall : best
      ), null);
      if (measured.length) {
        // Documents saved before scalePxPerUnit existed may have several field
        // measurements. Preserve the old solver's displayed scale once, then
        // make that result durable for every later edit and template copy.
        state.scalePxPerUnit = median(measured.map((wall) => (
          distance(wall.start, wall.end) / wall.lengthUnits
        )));
      } else if (anchor && rules.defaultLongestWallUnits > 0) {
        anchor.isMeasured = true;
        anchor.lengthUnits = rules.defaultLongestWallUnits;
        state.scalePxPerUnit = distance(anchor.start, anchor.end) / anchor.lengthUnits;
      }
    }
    // The tolerance rule goes IN, so the solver can tell a loop that needs
    // closing from an open run whose end-to-start distance is deliberate
    // (owner, 2026-07-29) — see solveClosure's `openChain`.
    const result = solveClosure(state.walls, {
      closureToleranceUnits: rules.closureToleranceUnits,
      defaultLongestWallUnits: rules.defaultLongestWallUnits,
      fixedPxPerUnit: state.scalePxPerUnit,
      closedLoopIntent: state.closedPerimeter === true,
    });
    state.walls = result.walls;
    state.pxPerUnit = result.pxPerUnit;
    if (!(state.scalePxPerUnit > 0)) state.scalePxPerUnit = result.pxPerUnit;
    state.closure = {
      residualGapUnits: result.residualGapUnits,
      degenerate: result.degenerate,
      solved: result.solved,
      // An OPEN run is not an unclosed room: its walls were never meant to meet,
      // so `closed` stays false but nothing may report it as a defect.
      openChain: result.openChain === true,
      closed: !result.solved || isClosed(result.residualGapUnits, rules.closureToleranceUnits),
    };
    // Weave partition face numbers here (not in renumber): resolve runs after
    // every edit AND on load (applyDoc), so a reloaded sketch's faces get their
    // woven numbers too — renumber never runs on load (owner bug: "Wnull").
    weavePartitionNumbers(state.walls);
  }

  /** Minimum drawable length (px) for a wall segment left after a door split. */
  const MIN_SEGMENT_PX = 8;

  /**
   * Splits a wall around a centered DOOR — the wall becomes
   * wall / opening / wall, where the opening is a connecting segment with no
   * wall number (ADR 0010) and a default width the designer can edit.
   *
   * Numbering follows from re-running orientAndNumber: when the split wall is
   * the one at the bottom-left corner, its LEFT part becomes Wall 1 and its
   * RIGHT part is numbered last in the clockwise loop (the opening sits at the
   * seam between them) — exactly the owner's template layout. For any other
   * wall the two parts simply take their clockwise positions.
   *
   * The opening is created MEASURED at the default door width, because a door
   * is a fixed size and must not be stretched by the solver to close the room
   * (ADR 0010). The door's pixel width is sized from the current scale so it is
   * a real 30" (or the room's default) rather than an arbitrary gap.
   *
   * @returns {boolean} true if the door was added
   */
  function addDoor(wallId) {
    const index = state.walls.findIndex((w) => w.id === wallId);
    if (index < 0) return false;
    const wall = state.walls[index];
    if ((wall.segmentKind ?? 'wall') !== 'wall') return false; // can't door an opening

    const wallPx = distance(wall.start, wall.end);
    const maxDoorPx = wallPx - 2 * MIN_SEGMENT_PX;
    if (maxDoorPx < 6) return false; // wall too short to hold a door plus returns

    const scale = state.pxPerUnit && state.pxPerUnit > 0 ? state.pxPerUnit : null;
    const doorUnits = rules.defaultDoorWidthUnits;
    // Size the door in real units when we have a scale; otherwise a sensible
    // fraction of the wall. Clamp so both wall segments stay drawable.
    const desiredPx = scale ? doorUnits * scale : wallPx / 3;
    const doorPx = Math.max(6, Math.min(desiredPx, maxDoorPx));
    const segmentPx = (wallPx - doorPx) / 2;

    const a0 = { ...wall.start };
    const a1 = advance(a0, wall.internalAngle, segmentPx);
    const a2 = advance(a1, wall.internalAngle, doorPx);
    const a3 = { ...wall.end };
    const shared = { internalAngle: wall.internalAngle, angleDeg: wall.angleDeg };

    const include = wall.includeInElevation !== false;
    const leftSegment = { ...shared, id: makeId(), start: a0, end: a1, segmentKind: 'wall', isMeasured: false, lengthUnits: null, includeInElevation: include };
    const doorSegment = { ...shared, id: makeId(), start: a1, end: a2, segmentKind: 'opening', isMeasured: true, lengthUnits: doorUnits };
    const rightSegment = { ...shared, id: makeId(), start: a2, end: a3, segmentKind: 'wall', isMeasured: false, lengthUnits: null, includeInElevation: include };

    // Splitting the pinned Wall 1 must not drop the pin. It moves to the part
    // DOWNSTREAM of the door in traversal order, which puts the door at the
    // loop seam and gives the other part the last number — the same shape the
    // automatic rule produces when ITS Wall 1 is split (and, for a bottom wall,
    // the same piece as the owner's "the left segment would be wall 1").
    if (state.wallOneOverrideId === wall.id) state.wallOneOverrideId = rightSegment.id;

    const next = [
      ...state.walls.slice(0, index),
      leftSegment,
      doorSegment,
      rightSegment,
      ...state.walls.slice(index + 1),
    ];
    state.walls = renumber(next);
    state.selectedWallId = doorSegment.id; // select the door so its width is easy to edit
    resolve();
    return true;
  }

  /**
   * Removes a door, restoring the whole wall.
   *
   * The opening's two neighbors in the CYCLIC chain are the wall parts that
   * flank it (a door added to Wall 1 puts them at opposite ends of the array,
   * with the opening at the seam — so cyclic, not just linear, adjacency). When
   * they are collinear walls, all three are fused into one wall spanning from
   * the first part's start to the second part's end. The generic collinear
   * merge (ADR 0013) can't do this itself because those parts sit a full
   * wall-length apart, which its seam-proximity guard correctly rejects.
   *
   * @returns {boolean} true if a door was removed
   */
  function removeDoor(openingId) {
    const chain = state.walls;
    const n = chain.length;
    const index = chain.findIndex((w) => w.id === openingId);
    if (index < 0 || chain[index].segmentKind !== 'opening') return false;

    const prev = chain[(index - 1 + n) % n];
    const next = chain[(index + 1) % n];
    const bothWalls =
      prev !== next &&
      (prev.segmentKind ?? 'wall') === 'wall' &&
      (next.segmentKind ?? 'wall') === 'wall' &&
      prev.internalAngle === next.internalAngle;

    let rebuilt;
    if (bothWalls) {
      const merged = {
        internalAngle: prev.internalAngle,
        angleDeg: prev.angleDeg,
        id: makeId(),
        start: { ...prev.start },
        end: { ...next.end },
        segmentKind: 'wall',
        isMeasured: false,
        lengthUnits: null,
        includeInElevation: prev.includeInElevation !== false || next.includeInElevation !== false,
      };
      const drop = new Set([prev.id, openingId, next.id]);
      rebuilt = [];
      for (const seg of chain) {
        if (seg.id === prev.id) rebuilt.push(merged);
        else if (!drop.has(seg.id)) rebuilt.push(seg);
      }
      // If either half held the Wall 1 pin, the restored wall inherits it.
      if (state.wallOneOverrideId === prev.id || state.wallOneOverrideId === next.id) {
        state.wallOneOverrideId = merged.id;
      }
    } else {
      rebuilt = chain.filter((w) => w.id !== openingId);
    }

    state.walls = renumber(rebuilt);
    state.selectedWallId = state.walls[0]?.id ?? null;
    resolve();
    return true;
  }

  /**
   * Deletes a WALL from the chain (owner, 2026-08-04).
   *
   * Until now the only remedy for a bad snap was to redraw the whole space:
   * cleanup regularly emits stub segments — an out-and-back spike where a
   * stroke ended shakily is the common one — and nothing could remove them.
   *
   * The geometry heals itself. `solveClosure` re-lays the chain head-to-tail
   * from an anchor, so dropping a segment out of the array IS the edit: every
   * remaining wall keeps its angle and its typed length, and the two
   * neighbours simply meet. In a closed room the loop necessarily opens by the
   * deleted wall's run, and the closure solver reports that as a residual gap
   * rather than silently stretching the survivors to cover it.
   *
   * Two collinear neighbours are FUSED into one wall, the way `removeDoor`
   * restores a split wall — two abutting walls at the same angle ARE one wall,
   * and leaving both would put a phantom corner in the table. Their lengths add
   * when both were measured; if either was auto the total is unknown, so the
   * merged wall goes back to the solver.
   *
   * Everything anchored to the wall (sections, obstructions, labels, depth
   * lines, height breaks, corner height) goes with it.
   *
   * @returns {boolean} true if a wall was deleted
   */
  function deleteWall(id) {
    const chain = state.walls;
    const index = chain.findIndex((w) => w.id === id);
    if (index < 0) return false;
    const victim = chain[index];
    // Doors and partitions are removed through their own paths, which know how
    // to put the split wall back together.
    if ((victim.segmentKind ?? 'wall') !== 'wall') return false;
    // Never delete the LAST wall: emptying the sketch is "Clear everything",
    // a deliberate and separately-confirmed action.
    if (chain.filter((w) => (w.segmentKind ?? 'wall') === 'wall').length <= 1) return false;

    const n = chain.length;
    const prev = chain[(index - 1 + n) % n];
    const next = chain[(index + 1) % n];
    const collinear = prev !== next && prev !== victim && next !== victim
      && (prev.segmentKind ?? 'wall') === 'wall'
      && (next.segmentKind ?? 'wall') === 'wall'
      && prev.internalAngle === next.internalAngle;

    let rebuilt = chain.filter((w) => w.id !== id);
    if (collinear) {
      const bothMeasured = prev.isMeasured && next.isMeasured
        && prev.lengthUnits > 0 && next.lengthUnits > 0;
      const merged = {
        internalAngle: prev.internalAngle,
        angleDeg: prev.angleDeg,
        id: makeId(),
        start: { ...prev.start },
        end: { ...next.end },
        segmentKind: 'wall',
        isMeasured: bothMeasured,
        lengthUnits: bothMeasured ? prev.lengthUnits + next.lengthUnits : null,
        includeInElevation: prev.includeInElevation !== false || next.includeInElevation !== false,
      };
      rebuilt = rebuilt.flatMap((seg) => {
        if (seg.id === prev.id) return [merged];
        return seg.id === next.id ? [] : [seg];
      });
      if (state.wallOneOverrideId === prev.id || state.wallOneOverrideId === next.id) {
        state.wallOneOverrideId = merged.id;
      }
    }

    if (state.wallOneOverrideId === id) state.wallOneOverrideId = null; // back to the automatic rule
    delete state.wallHeightBreaks[id];
    delete state.cornerHeights[id];
    state.walls = renumber(rebuilt);
    state.closedPerimeter = false;
    pruneObstructions();
    state.selectedWallId = state.walls.find((w) => (w.segmentKind ?? 'wall') === 'wall')?.id ?? null;
    resolve();
    return true;
  }

  /**
   * What a wall would take with it — so the UI can ask before destroying work
   * and stay silent when there is none to destroy (a stub from a bad snap is
   * always empty, and confirming that is friction for no gain).
   */
  function wallDeletionImpact(id) {
    const hosts = new Set([id]);
    return {
      sections: state.sections.filter((s) => hosts.has(s.wallId)).length,
      obstructions: state.obstructions.filter((o) => hosts.has(o.wallId)).length,
      labels: state.wallLabels.filter((l) => hosts.has(l.wallId)).length,
      helperLines: state.planHelperLines.filter((h) => hosts.has(h.wallId)).length,
    };
  }

  /** The currently selected segment, or null. */
  function selectedSegment() {
    return state.walls.find((w) => w.id === state.selectedWallId) ?? null;
  }

  /**
   * Toggles whether a wall gets a front elevation. Geometry is unchanged, so no
   * re-solve — only the elevation view and the wall row's checkbox reflect it.
   */
  function toggleElevation(wallId, include) {
    const wall = state.walls.find((w) => w.id === wallId);
    if (!wall || (wall.segmentKind ?? 'wall') !== 'wall') return false;
    wall.includeInElevation = include === undefined ? !(wall.includeInElevation !== false) : !!include;
    return true;
  }

  /**
   * Sets a per-wall thickness override, in inches (parsed like a length so
   * "4 1/2" and "4.5" both work), or CLEARS it when the value is blank so the
   * wall falls back to the business-rule default. Render-only: the wall band is
   * drawn from thicknessUnits, but geometry/closure never read it, so there is
   * no re-solve. Rejects a non-positive value (the DB CHECK forbids <= 0).
   * Openings have no thickness — they are voids.
   */
  function setWallThickness(id, rawValue) {
    const wall = state.walls.find((w) => w.id === id);
    if (!wall || (wall.segmentKind ?? 'wall') !== 'wall') return false;
    const raw = String(rawValue ?? '').trim();
    if (raw === '') {
      delete wall.thicknessUnits; // revert to the default
      return true;
    }
    const parsed = parseLength(raw);
    if (parsed === null || parsed <= 0) return false;
    wall.thicknessUnits = parsed;
    return true;
  }

  /** The override thickness for the table input, or null when it uses the default. */
  function displayThickness(wall) {
    // A partition's thickness IS its footprint segment's length — always shown.
    if (isPartition(wall.id)) return formatLength(wall.lengthUnits, rules.measurementLabelFormat);
    return Number.isFinite(wall.thicknessUnits) && wall.thicknessUnits > 0
      ? formatLength(wall.thicknessUnits, rules.measurementLabelFormat)
      : null;
  }

  /** Sets the room's ceiling height (parsed like a wall length). */
  function setCeilingHeight(rawValue) {
    const parsed = parseLength(rawValue);
    if (parsed === null || parsed <= 0) return false;
    state.ceilingHeightUnits = parsed;
    return true;
  }

  /** Enables the simple flat-ceiling mode without destroying saved slope data. */
  function setCeilingFlat(flat) {
    state.ceilingFlat = flat !== false;
    return true;
  }

  // ---- sloped ceilings: corner heights (ADR 0044) ---------------------------
  //
  // The height belongs to the CORNER, not the wall. Two walls meeting at a
  // corner share that point and the ceiling is continuous across it, so they
  // cannot differ there — storing one number per corner makes disagreement
  // impossible rather than something to reconcile. A corner is identified by
  // the segment whose END lands on it, so a closed chain of N segments has N
  // corners; an open run has one extra, its very first start.

  /** The chain segment BEFORE this one, or null at the head of an open run. */
  function previousSegment(segmentId) {
    const i = state.walls.findIndex((w) => w.id === segmentId);
    if (i < 0) return null;
    if (i > 0) return state.walls[i - 1];
    // Head of the chain: on a CLOSED loop the last segment's end is this start.
    if (state.closure?.openChain === true) return null;
    return state.walls.length > 1 ? state.walls[state.walls.length - 1] : null;
  }

  /**
   * The ceiling height at one end of a segment. Unset corners read the room's
   * ceiling height, so a flat room needs no stored corner at all.
   *
   * @param {string} segmentId
   * @param {'start'|'end'} which
   */
  function cornerHeightUnits(segmentId, which) {
    const fallback = state.ceilingHeightUnits > 0 ? state.ceilingHeightUnits : 0;
    if (which === 'end') {
      const own = state.cornerHeights[segmentId];
      return own > 0 ? own : fallback;
    }
    const prev = previousSegment(segmentId);
    if (prev) {
      const shared = state.cornerHeights[prev.id];
      return shared > 0 ? shared : fallback;
    }
    return state.chainStartHeightUnits > 0 ? state.chainStartHeightUnits : fallback;
  }

  /**
   * Sets the ceiling height at one end of a wall. Because the value is stored
   * once per corner, this necessarily moves the neighbouring wall's matching end
   * too — that is the point, not a side effect. Blank clears it back to the
   * room's ceiling height.
   *
   * @returns {boolean} false when the value could not be read
   */
  function setCornerHeight(segmentId, which, rawValue) {
    const segment = state.walls.find((w) => w.id === segmentId);
    if (!segment) return false;
    const text = String(rawValue ?? '').trim();
    const parsed = text === '' ? null : parseLength(text);
    if (text !== '' && (parsed === null || parsed <= 0)) return false;
    const write = (key) => {
      if (parsed === null) delete state.cornerHeights[key];
      else state.cornerHeights[key] = parsed;
    };
    state.ceilingFlat = false;
    if (which === 'end') { write(segmentId); return true; }
    const prev = previousSegment(segmentId);
    if (prev) { write(prev.id); return true; }
    state.chainStartHeightUnits = parsed;
    return true;
  }

  /** A wall's interior height breakpoints, nearest the start first. */
  function wallHeightBreaksFor(wallId) {
    const list = state.wallHeightBreaks[wallId];
    return Array.isArray(list) ? [...list].sort((a, b) => a.atUnits - b.atUnits) : [];
  }

  /**
   * Adds an interior breakpoint — "level to here, then slope on" (owner). Both
   * figures are lengths. Rejected outside the wall, because a point at either
   * end would contradict the corner height, which is shared and always wins.
   */
  function addWallHeightBreak(wallId, atRaw, heightRaw) {
    const wall = resolveHost(wallId);
    if (!wall) return false;
    const at = parseLength(atRaw);
    const height = parseLength(heightRaw);
    if (at === null || height === null || height <= 0) return false;
    const length = wallLengthUnits(wall);
    if (!(at > 0) || !(at < length)) return false;
    const list = state.wallHeightBreaks[wallId] ?? [];
    // One point per spot: re-adding at the same distance edits it.
    const existing = list.find((b) => Math.abs(b.atUnits - at) < 0.01);
    if (existing) existing.heightUnits = height;
    else list.push({ atUnits: at, heightUnits: height });
    state.wallHeightBreaks[wallId] = list;
    state.ceilingFlat = false;
    return true;
  }

  /** Removes the breakpoint at (about) `atUnits` along a wall. */
  function removeWallHeightBreak(wallId, atUnits) {
    const list = state.wallHeightBreaks[wallId];
    if (!Array.isArray(list)) return false;
    const next = list.filter((b) => Math.abs(b.atUnits - atUnits) >= 0.01);
    if (next.length === list.length) return false;
    if (next.length) state.wallHeightBreaks[wallId] = next;
    else delete state.wallHeightBreaks[wallId];
    return true;
  }

  /**
   * A wall's resolved height profile: its two corner heights plus whatever
   * breakpoints it carries, normalised (see geometry/wall-height.js).
   *
   * A PARTITION FACE is not a chain segment and has no corners of its own, so
   * it reads flat at the room's ceiling height — the spur's own slope is not
   * modelled (ADR 0044, accepted limitation).
   */
  function wallHeightProfileFor(wallId) {
    const wall = resolveHost(wallId);
    if (!wall) return [];
    const inChain = state.walls.some((w) => w.id === wallId);
    const lengthUnits = wallLengthUnits(wall);
    if (!inChain || state.ceilingFlat !== false) {
      const flat = state.ceilingHeightUnits > 0 ? state.ceilingHeightUnits : 0;
      return buildWallHeightProfile({ lengthUnits, startHeightUnits: flat, endHeightUnits: flat });
    }
    return buildWallHeightProfile({
      lengthUnits,
      startHeightUnits: cornerHeightUnits(wallId, 'start'),
      endHeightUnits: cornerHeightUnits(wallId, 'end'),
      breakpoints: wallHeightBreaksFor(wallId),
    });
  }

  /**
   * The single height the whole room shares, or null when it varies — what the
   * elevation uses to decide between one dimension for the room and one per
   * wall (owner: "if same wall height for entire room it can just be displayed
   * once").
   */
  function uniformWallHeightUnits() {
    const walls = state.walls.filter((w) => (w.segmentKind ?? 'wall') === 'wall');
    if (!walls.length) return null;
    let common = null;
    for (const w of walls) {
      const profile = wallHeightProfileFor(w.id);
      if (!profileIsFlat(profile)) return null;
      const h = profileMaxHeight(profile);
      if (common === null) common = h;
      else if (Math.abs(common - h) > 0.01) return null;
    }
    return common;
  }

  /** Sets the door-opening height used in elevations. */
  function setDoorHeight(rawValue) {
    const parsed = parseLength(rawValue);
    if (parsed === null || parsed <= 0) return false;
    state.doorHeightUnits = parsed;
    return true;
  }

  /** Show or hide an elevation helper (reference) line by its label. */
  function setHelperLineVisible(label, visible) {
    state.helperLineOverrides = { ...state.helperLineOverrides, [label]: Boolean(visible) };
  }

  /**
   * Whether a helper line is currently shown: the designer's explicit
   * per-sketch choice when they made one, the rule's default_visible
   * otherwise. Lines the rule doesn't know (older docs, renamed labels)
   * default to visible so nothing silently disappears.
   */
  function isHelperLineVisible(label) {
    const override = state.helperLineOverrides[label];
    if (typeof override === 'boolean') return override;
    const line = (rules.elevationReferenceLines ?? []).find((l) => l.label === label);
    return line ? line.default_visible !== false : true;
  }

  /** Ensures a custom-line slot exists (inactive until it has a height). */
  function customSlot(index) {
    if (!Number.isInteger(index) || index < 0 || index >= MAX_CUSTOM_HELPER_LINES) return null;
    while (state.customHelperLines.length <= index) {
      state.customHelperLines.push({ units: null, visible: false });
    }
    return state.customHelperLines[index];
  }

  /**
   * The custom helper lines, always padded to MAX_CUSTOM_HELPER_LINES slots so
   * the UI can render a fixed set of rows. Each is { units: number|null,
   * visible: boolean }; a null units is an empty slot (not drawn).
   */
  function customHelperLines() {
    const out = [];
    for (let i = 0; i < MAX_CUSTOM_HELPER_LINES; i += 1) {
      const s = state.customHelperLines[i];
      const units = Number.isFinite(s?.units) ? s.units : null;
      out.push({ units, visible: units !== null && s?.visible !== false });
    }
    return out;
  }

  /**
   * Sets custom helper line `index`'s height. A blank value clears that line;
   * anything unparseable is rejected so a typo can't silently move a reference
   * the designer is drawing against.
   */
  function setCustomHelperLine(index, rawValue) {
    const slot = customSlot(index);
    if (!slot) return false;
    const text = String(rawValue ?? '').trim();
    if (text === '') {
      slot.units = null;
      slot.visible = false;
      return true;
    }
    const parsed = parseLength(text);
    if (parsed === null || parsed <= 0) return false;
    slot.units = parsed;
    slot.visible = true;
    return true;
  }

  /** Show or hide custom helper line `index`. */
  function setCustomHelperVisible(index, visible) {
    const slot = customSlot(index);
    if (!slot) return false;
    slot.visible = !!visible;
    return true;
  }

  // ---- obstructions --------------------------------------------------------

  /** Length of a wall in room units, from its measured value or solved geometry. */
  function wallLengthUnits(wall) {
    if (wall.isMeasured && wall.lengthUnits != null) return wall.lengthUnits;
    const px = distance(wall.start, wall.end);
    const scale = state.pxPerUnit && state.pxPerUnit > 0 ? state.pxPerUnit : 1;
    return px / scale;
  }

  /** The wall a fixture may legally sit on, or null. Openings cannot host one. */
  function hostWall(wallId) {
    const wall = state.walls.find((w) => w.id === wallId);
    return wall && (wall.segmentKind ?? 'wall') === 'wall' ? wall : null;
  }

  /** Fan centers require two reproducible orthogonal tape origins. */
  function referenceWallsArePerpendicular(referenceWallIds, tolerance = 0.05) {
    const [first, second] = (referenceWallIds ?? []).map((id) => hostWall(id));
    if (!first || !second) return false;
    const a = { x: first.end.x - first.start.x, y: first.end.y - first.start.y };
    const b = { x: second.end.x - second.start.x, y: second.end.y - second.start.y };
    const denominator = Math.hypot(a.x, a.y) * Math.hypot(b.x, b.y);
    return denominator > 0 && Math.abs((a.x * b.x + a.y * b.y) / denominator) <= tolerance;
  }

  /**
   * A fixture/label host resolved from an id: a real wall, OR a partition FACE
   * (a derived two-sided-wall face, ADR 0032/0035). Faces aren't in the closure
   * chain, so obstructions and labels can still anchor to them by id — the face
   * behaves like a measured wall whose length is the partition's depth.
   */
  function resolveHost(wallId) {
    return hostWall(wallId) ?? partitionFaceWalls().find((f) => f.id === wallId) ?? null;
  }

  /** The two face ids of every partition, valid whether or not the room is solved. */
  function partitionFaceIds() {
    const ids = new Set();
    for (const p of state.partitions) {
      ids.add(`${p.segmentId}~A`);
      ids.add(`${p.segmentId}~B`);
    }
    return ids;
  }

  /**
   * Keeps a fixture inside the wall it belongs to and above the floor.
   *
   * Dragging is a coarse gesture — on a tablet a fingertip covers several
   * inches — so a drop near an edge would otherwise leave a window hanging off
   * the end of a wall. Clamping means the drop is always a *valid* placement
   * and the numeric fields are only ever fine-tuning, never repair work.
   */
  function clampPlacement(wall, item, offsetAlongWall, offsetFromFloor) {
    const span = Math.max(0, wallLengthUnits(wall) - item.widthUnits);
    return {
      offsetAlongWall: Math.min(Math.max(0, offsetAlongWall), span),
      offsetFromFloor: Math.max(0, offsetFromFloor),
    };
  }

  /**
    * Adds a ceiling-hosted architectural opening or measured fixture. It is
   * hosted by two reference walls but never becomes part of their topology:
   * Reference 1 locates its width inward and Reference 2 locates its length.
   */
  function addCeilingOpening({
    kind = 'attic_hatch', referenceWallIds, xUnits, yUnits, widthUnits, lengthUnits,
    heightUnits = 1, diameterUnits = null, fromFloorUnits = null,
    hatchStyle, ladderFloorOffsetUnits = null, ladderProjectionUnits = null, showLadderExtended = true,
  } = {}) {
    const kinds = new Set(['attic_hatch', 'attic_stairs', 'ceiling_fan', 'ceiling_light', 'ceiling_sprinkler']);
    const refs = Array.isArray(referenceWallIds) ? referenceWallIds : [];
    const fanDiameter = kind === 'ceiling_fan'
      ? (Number.isFinite(diameterUnits) ? diameterUnits : widthUnits)
      : null;
    const storedWidth = kind === 'ceiling_fan' ? fanDiameter : widthUnits;
    const storedLength = kind === 'ceiling_fan' ? fanDiameter : lengthUnits;
    const storedHeight = kind === 'ceiling_fan' ? 1 : heightUnits;
    if (!kinds.has(kind) || ![xUnits, yUnits, storedWidth, storedLength, storedHeight].every(Number.isFinite)
      || xUnits < 0 || yUnits < 0 || storedWidth <= 0 || storedLength <= 0
      || storedHeight <= 0
      || (kind === 'ceiling_fan' && (!Number.isFinite(fromFloorUnits) || fromFloorUnits < 0))
      || refs.length !== 2 || refs[0] === refs[1] || !refs.every((id) => hostWall(id))
      || (kind === 'ceiling_fan' && !referenceWallsArePerpendicular(refs))) return null;
    const item = {
      id: makeId(), kind, referenceWallIds: [...refs], xUnits, yUnits,
      widthUnits: storedWidth, lengthUnits: storedLength, heightUnits: storedHeight,
      ...(kind === 'ceiling_fan' ? { diameterUnits: fanDiameter, fromFloorUnits } : {}),
      hatchStyle: hatchStyle ?? (kind === 'attic_stairs' ? 'drop_down' : 'hinged'),
      ladderFloorOffsetUnits: Number.isFinite(ladderFloorOffsetUnits) && ladderFloorOffsetUnits >= 0 ? ladderFloorOffsetUnits : null,
      // Read-only compatibility for sketches saved before the floor point was
      // measured directly from Reference 2.
      ladderProjectionUnits: Number.isFinite(ladderProjectionUnits) && ladderProjectionUnits >= 0 ? ladderProjectionUnits : null,
      showLadderExtended: kind === 'attic_stairs' ? showLadderExtended !== false : false,
    };
    if (!ceilingOpeningGeometry(state.walls, item, { pxPerUnit: state.pxPerUnit > 0 ? state.pxPerUnit : 1 })) return null;
    state.ceilingOpenings.push(item);
    return item.id;
  }

  /** Updates one ceiling-hosted opening without ever touching perimeter topology. */
  function updateCeilingOpening(id, patch = {}) {
    const item = state.ceilingOpenings.find((opening) => opening.id === id);
    if (!item) return false;
    const next = { ...item, ...patch };
    if (!['attic_hatch', 'attic_stairs', 'ceiling_fan', 'ceiling_light', 'ceiling_sprinkler'].includes(next.kind)) return false;
    if (!Array.isArray(next.referenceWallIds) || next.referenceWallIds.length !== 2
      || next.referenceWallIds[0] === next.referenceWallIds[1]
      || !next.referenceWallIds.every((wallId) => hostWall(wallId))
      || (next.kind === 'ceiling_fan' && !referenceWallsArePerpendicular(next.referenceWallIds))) return false;
    if (next.kind === 'ceiling_fan') {
      const diameter = Number.isFinite(patch.diameterUnits)
        ? patch.diameterUnits
        : (Number.isFinite(next.diameterUnits) ? next.diameterUnits : next.widthUnits);
      next.diameterUnits = diameter;
      next.widthUnits = diameter;
      next.lengthUnits = diameter;
      next.heightUnits = 1;
      if (next.fromFloorUnits != null && (!Number.isFinite(next.fromFloorUnits) || next.fromFloorUnits < 0)) return false;
    } else {
      next.diameterUnits = null;
      next.fromFloorUnits = null;
    }
    for (const field of ['xUnits', 'yUnits', 'widthUnits', 'lengthUnits', 'heightUnits']) {
      if (!Number.isFinite(next[field]) || next[field] < 0 || (['widthUnits', 'lengthUnits'].includes(field) && next[field] <= 0)) return false;
    }
    if (next.heightUnits <= 0) return false;
    if (next.ladderFloorOffsetUnits != null && (!Number.isFinite(next.ladderFloorOffsetUnits) || next.ladderFloorOffsetUnits < 0)) return false;
    if (next.ladderProjectionUnits != null && (!Number.isFinite(next.ladderProjectionUnits) || next.ladderProjectionUnits < 0)) return false;
    if (!ceilingOpeningGeometry(state.walls, next, { pxPerUnit: state.pxPerUnit > 0 ? state.pxPerUnit : 1 })) return false;
    Object.assign(item, next, {
      hatchStyle: next.kind === 'attic_stairs' ? 'drop_down' : (next.hatchStyle || 'hinged'),
      ladderFloorOffsetUnits: next.kind === 'attic_stairs' ? (next.ladderFloorOffsetUnits ?? null) : null,
      ladderProjectionUnits: next.kind === 'attic_stairs' ? (next.ladderProjectionUnits ?? null) : null,
      showLadderExtended: next.kind === 'attic_stairs' ? next.showLadderExtended !== false : false,
    });
    return true;
  }

  /** Removes one ceiling opening; physical walls and their numbering are untouched. */
  function removeCeilingOpening(id) {
    const before = state.ceilingOpenings.length;
    state.ceilingOpenings = state.ceilingOpenings.filter((opening) => opening.id !== id);
    return state.ceilingOpenings.length !== before;
  }

  /**
   * Places an obstruction on a wall using the type's default size and mounting
   * height. Centered along the wall unless `placement` says otherwise — that is
   * the drag-and-drop path, where the drop point decides where it lands.
   * Everything is editable afterward.
   *
   * @param {{offsetAlongWall?:number, offsetFromFloor?:number}} [placement]
   * @returns {string|null} the new obstruction's id, or null if it could not be placed
   */
  function addObstruction(wallId, typeKey, placement = null) {
    const wall = resolveHost(wallId);
    if (!wall) return null;
    const type = (rules.obstructionTypes ?? []).find((t) => t.key === typeKey);
    if (!type) return null;

    const width = type.width ?? 12;
    const obstruction = {
      id: makeId(),
      wallId,
      type: type.key,
      label: type.label,
      offsetAlongWall: 0,
      offsetFromFloor: type.offsetFromFloor ?? 36,
      widthUnits: width,
      heightUnits: type.height ?? 12,
      depthUnits: type.depth ?? null,
      // Distance from the wall's inside face to the item's near face, on the
      // plan (ADR 0025). 0 = flush; positive = out into the room; negative =
      // recessed into the wall (e.g. a recessed window). Type maintenance uses
      // the friendlier positive `inset`, so translate it to this signed drawing
      // coordinate. Keep faceOffset as a compatibility path for older rules.
      faceOffsetUnits: Number.isFinite(type.inset) && type.inset >= 0
        ? -type.inset
        : (Number.isFinite(type.faceOffset) ? type.faceOffset : 0),
      // The designer may measure an obstruction from either physical wall end.
      // Geometry remains canonical (always offset from the wall start); this
      // records which edge supplied the field measurement for safe editing.
      referenceEdge: 'left',
      // All new primary openings are wall-attached objects, preserving one
      // physical host wall/header rather than splitting the perimeter chain.
      ...(type.key === 'wall_opening' ? {
        opening: {
          kind: 'swing', trim: 'cased', swingDirection: 'in', hingeSide: 'left',
          clearOpeningWidthUnits: width,
        },
      } : {}),
      locked: false,
    };

    const wantAlong = Number.isFinite(placement?.offsetAlongWall)
      ? placement.offsetAlongWall
      : (wallLengthUnits(wall) - width) / 2; // centered by default
    // A CEILING-anchored type (a soffit) hangs from the ceiling, so its height
    // off the floor depends on the room. The type's `offsetFromFloor` is only
    // the fallback for a room with no ceiling height at all — storing the drop
    // and deriving the rest keeps a soffit right when the ceiling is corrected.
    const ceilingAnchored = type.anchor === 'ceiling' && state.ceilingHeightUnits > 0;
    const wantFloor = Number.isFinite(placement?.offsetFromFloor)
      ? placement.offsetFromFloor
      : ceilingAnchored
        ? Math.max(0, state.ceilingHeightUnits - obstruction.heightUnits)
        : obstruction.offsetFromFloor;
    Object.assign(obstruction, clampPlacement(wall, obstruction, wantAlong, wantFloor));

    state.obstructions.push(obstruction);
    return obstruction.id;
  }

  /** Updates a wall-attached door/opening or window's cased clear-opening data. */
  function setWallOpening(id, patch = {}) {
    const item = state.obstructions.find((o) => o.id === id
      && (o.type === 'wall_opening' || String(o.type).startsWith('window')));
    if (!item) return false;
    const kinds = new Set(['swing', 'pocket', 'bifold_single', 'bifold_double', 'bypass', 'cased_opening', 'uncased_opening']);
    const next = {
      ...(item.opening ?? (String(item.type).startsWith('window')
        ? { kind: 'cased_opening', trim: 'cased' } : {})),
      ...patch,
    };
    if (!kinds.has(next.kind)) return false;
    next.trim = next.kind === 'uncased_opening' ? 'uncased' : (next.trim === 'uncased' ? 'uncased' : 'cased');
    for (const key of ['clearOpeningWidthUnits', 'clearOpeningHeightUnits']) {
      if (next[key] !== undefined && next[key] !== null
        && (!Number.isFinite(next[key]) || next[key] <= 0)) return false;
    }
    if (next.clearOpeningWidthUnits > item.widthUnits
      || next.clearOpeningHeightUnits > item.heightUnits) return false;
    if (next.kind === 'swing') {
      next.swingDirection = next.swingDirection === 'out' ? 'out' : 'in';
      next.hingeSide = next.hingeSide === 'right' ? 'right' : 'left';
      if (next.swingProjectionUnits !== undefined && next.swingProjectionUnits !== null
        && (!Number.isFinite(next.swingProjectionUnits) || next.swingProjectionUnits <= 0)) return false;
    }
    if (next.kind === 'pocket') next.slideDirection = next.slideDirection === 'right' ? 'right' : 'left';
    if (next.kind === 'bifold_single') next.foldSide = next.foldSide === 'right' ? 'right' : 'left';
    if (next.kind.startsWith('bifold') && !Number.isFinite(next.bifoldStackProjectionUnits)) {
      next.bifoldStackProjectionUnits = rules.bifoldFoldedProjectionUnits;
    }
    for (const key of ['bifoldStackProjectionUnits', 'bypassClearLeftUnits', 'bypassClearRightUnits']) {
      if (next[key] !== undefined && next[key] !== null && (!Number.isFinite(next[key]) || next[key] < 0)) return false;
    }
    item.opening = next;
    return true;
  }

  /**
   * Repositions an existing fixture, optionally onto a different wall — this is
   * what a drag on the elevation commits. Unlike updateObstruction() it takes
   * numbers, not typed text, because the source is a gesture rather than a
   * keyboard, and it clamps rather than rejecting: a drag that overshoots
   * should stick to the edge, not silently do nothing.
   *
   * @returns {boolean} false if the fixture or target wall does not exist
   */
  function moveObstruction(id, { wallId, offsetAlongWall, offsetFromFloor } = {}) {
    const item = state.obstructions.find((o) => o.id === id);
    if (!item) return false;
    // A locked fixture ignores drags entirely — the whole point of the lock is
    // that a stray finger on a placed outlet cannot nudge it. Its position is
    // still editable by explicitly typing in the fields (a deliberate act),
    // just not by dragging.
    if (item.locked) return false;
    const wall = resolveHost(wallId ?? item.wallId);
    if (!wall) return false;

    const next = clampPlacement(
      wall,
      item,
      Number.isFinite(offsetAlongWall) ? offsetAlongWall : item.offsetAlongWall,
      Number.isFinite(offsetFromFloor) ? offsetFromFloor : item.offsetFromFloor,
    );
    item.wallId = wall.id;
    item.offsetAlongWall = next.offsetAlongWall;
    item.offsetFromFloor = next.offsetFromFloor;
    return true;
  }

  function removeObstruction(id) {
    const before = state.obstructions.length;
    state.obstructions = state.obstructions.filter((o) => o.id !== id);
    return state.obstructions.length !== before;
  }

  /**
   * Lock or unlock a fixture. A locked fixture cannot be dragged (see
   * moveObstruction); its numeric fields still edit, since typing a value is a
   * deliberate act, not an accidental one.
   *
   * @param {boolean} [locked] explicit state; omit to toggle
   * @returns {boolean} the resulting locked state, or false if not found
   */
  function setObstructionLocked(id, locked) {
    const item = state.obstructions.find((o) => o.id === id);
    if (!item) return false;
    item.locked = locked === undefined ? !item.locked : Boolean(locked);
    return item.locked;
  }

  /**
   * Chooses the physical wall edge the designer is using as the manual
   * measurement origin. Canonical geometry stays start-relative, so changing
   * this selector never moves the obstruction.
   */
  function setObstructionReferenceEdge(id, edge) {
    if (edge !== 'left' && edge !== 'right') return false;
    const obstruction = state.obstructions.find((o) => o.id === id);
    if (!obstruction || !resolveHost(obstruction.wallId)) return false;
    obstruction.referenceEdge = edge;
    return true;
  }

  /** Distance from the selected physical wall edge to the matching obstruction edge. */
  function obstructionReferenceOffset(id) {
    const obstruction = state.obstructions.find((o) => o.id === id);
    const wall = obstruction && resolveHost(obstruction.wallId);
    if (!obstruction || !wall) return null;
    if (obstruction.referenceEdge !== 'right') return obstruction.offsetAlongWall;
    return Math.max(0, wallLengthUnits(wall) - obstruction.offsetAlongWall - obstruction.widthUnits);
  }

  /**
   * Applies a typed obstruction location measured from its chosen wall edge.
   * Left measures to the obstruction's left edge; right measures to its right
   * edge. Clamping preserves the no-hanging-off-a-wall invariant.
   */
  function updateObstructionReferenceOffset(id, rawValue) {
    const obstruction = state.obstructions.find((o) => o.id === id);
    const wall = obstruction && resolveHost(obstruction.wallId);
    const parsed = parseLength(String(rawValue ?? '').trim());
    if (!obstruction || !wall || parsed === null || parsed < 0) return false;
    const fromRight = obstruction.referenceEdge === 'right';
    const next = clampPlacement(
      wall,
      obstruction,
      fromRight ? wallLengthUnits(wall) - parsed - obstruction.widthUnits : parsed,
      obstruction.offsetFromFloor,
    );
    obstruction.offsetAlongWall = next.offsetAlongWall;
    return true;
  }

  /**
   * Updates one numeric field of an obstruction from typed text. Rejects
   * anything unparseable so a typo cannot silently move or resize a fixture.
   * `depthUnits` accepts blank, meaning "flush / unknown" (ADR 0008 — a null
   * depth is not the same claim as a measured zero).
   */
  function updateObstruction(id, field, rawValue) {
    const obstruction = state.obstructions.find((o) => o.id === id);
    if (!obstruction) return false;

    const text = String(rawValue ?? '').trim();
    if (field === 'depthUnits' && text === '') {
      obstruction.depthUnits = null;
      return true;
    }
    // faceOffsetUnits is SIGNED: negative = recessed into the wall (ADR 0025).
    // Blank means flush (0). Parse the magnitude and re-apply the sign, so the
    // usual positive-only parseLength still handles fractions.
    if (field === 'faceOffsetUnits') {
      if (text === '') {
        obstruction.faceOffsetUnits = 0;
        return true;
      }
      const negative = text.startsWith('-');
      const mag = parseLength(negative ? text.slice(1).trim() : text);
      if (mag === null) return false;
      obstruction.faceOffsetUnits = negative ? -mag : mag;
      return true;
    }
    const parsed = parseLength(text);
    if (parsed === null) return false;
    // Position may sit at zero; sizes must be positive.
    const allowsZero = field === 'offsetAlongWall' || field === 'offsetFromFloor';
    if (parsed < 0 || (!allowsZero && parsed <= 0)) return false;

    // If the designer measured from the right wall edge, resizing must keep the
    // observed right-edge distance stable rather than silently shifting it.
    if (field === 'widthUnits' && obstruction.referenceEdge === 'right') {
      const wall = resolveHost(obstruction.wallId);
      if (!wall) return false;
      const rightDistance = Math.max(0, wallLengthUnits(wall) - obstruction.offsetAlongWall - obstruction.widthUnits);
      obstruction.widthUnits = parsed;
      obstruction.offsetAlongWall = clampPlacement(
        wall, obstruction, wallLengthUnits(wall) - rightDistance - parsed, obstruction.offsetFromFloor,
      ).offsetAlongWall;
      return true;
    }

    obstruction[field] = parsed;
    return true;
  }

  /** Obstructions on a given wall, in placement order. */
  function obstructionsForWall(wallId) {
    return state.obstructions.filter((o) => o.wallId === wallId);
  }

  /**
   * The soffits on a wall, as along-the-wall spans with their undersides
   * (ADR 0053).
   *
   * A soffit is an obstruction whose TYPE says it caps the cabinetry below it
   * (`capsSectionHeight`), so its shape, placement, drawing and schedule entry
   * all come from the obstruction machinery already in place. Only the height
   * rule is new.
   *
   * The underside is the item's own `offsetFromFloor`: a ceiling-anchored type
   * is placed at `ceiling − height` when added, and a designer who drags it
   * lower means it.
   */
  function soffitSpansForWall(wallId) {
    const types = new Map((rules.obstructionTypes ?? []).map((t) => [t.key, t]));
    return obstructionsForWall(wallId)
      .filter((o) => types.get(o.type)?.capsSectionHeight === true)
      .map((o) => ({
        startUnits: o.offsetAlongWall,
        endUnits: o.offsetAlongWall + (o.widthUnits > 0 ? o.widthUnits : 0),
        bottomUnits: o.offsetFromFloor > 0 ? o.offsetFromFloor : 0,
      }));
  }

  /**
   * Display codes for every obstruction in the sketch, `id → "O1"`.
   *
   * Derived fresh, never stored: the codes renumber when an item is added,
   * removed or moved, and a stale code on a drawing is worse than none. The
   * TYPE supplies the letter (business rule `obstruction_types`), so renaming
   * or adding one is an Admin edit.
   */
  function obstructionCodes() {
    const types = new Map((rules.obstructionTypes ?? []).map((t) => [t.key, t]));
    const wallNumberOf = (wallId) => resolveHost(wallId)?.wallNumber ?? null;
    return assignObstructionCodes(state.obstructions.map((o) => ({
      id: o.id,
      code: types.get(o.type)?.code ?? null,
      wallNumber: wallNumberOf(o.wallId),
      offsetAlongWall: o.offsetAlongWall,
    })));
  }

  /** One obstruction's display code, or '' when its type has none. */
  function obstructionCode(id) {
    return obstructionCodes().get(id) ?? '';
  }

  // ---- measurement sheet (ADR 0045) -----------------------------------------

  /**
   * The obstruction schedule for the MEASUREMENT SHEET: what a field measurer
   * needs to reproduce the empty room, and nothing about the design.
   *
   * Locations show BOTH wall-end clearances (owner, 2026-08-16): `FL` from the
   * left edge to the item's left edge and `FR` from the item's right edge to the
   * wall's right edge. The second figure is derived from wall length, item
   * width, and the measured first position, so the sheet can be checked from
   * either direction. `FF` remains the height up from the floor.
   *
   * "Left" and "right" are the wall's own start→end direction, which is the
   * direction its elevation is drawn in — so a row reads the same way as the
   * drawing on the same page.
   *
   * Doors are NOT here: they are segments of the wall chain, not things mounted
   * on a wall (owner), and they draw as openings on both views.
   *
   * Ceiling openings are scheduled once on the plan because their two
   * perpendicular reference walls jointly locate them. Each affected wall row
   * also names the openings so its elevation is included even when it carries
   * no ordinary obstruction.
   *
   * @returns {{walls: Array<object>, ceilingOpenings: Array<object>, partitionObstructionCount: number}}
   */
  function measurementSheet() {
    const rows = [];
    // Partition FACES have no agreed numbering yet (owner deferred it), so their
    // obstructions are counted and reported rather than silently dropped — an
    // omission nobody can see is worse than one the sheet admits to.
    const faceIds = partitionFaceIds();
    let partitionObstructionCount = 0;
    for (const o of state.obstructions) {
      if (faceIds.has(o.wallId)) partitionObstructionCount += 1;
    }

    for (const wall of state.walls) {
      if ((wall.segmentKind ?? 'wall') !== 'wall') continue; // doors/openings
      const lengthUnits = wallLengthUnits(wall);
      // The SAME codes the drawings carry, so a row and a fixture are the same
      // thing named once (owner, 2026-08-05 — was a per-wall W1-A lettering,
      // which the drawing had no room to repeat).
      const codes = obstructionCodes();
      const items = obstructionsForWall(wall.id)
        .slice()
        .sort((a, b) => a.offsetAlongWall - b.offsetAlongWall)
        .map((o, i) => {
          const width = o.widthUnits > 0 ? o.widthUnits : 0;
          const fromLeft = o.offsetAlongWall;
          const fromRight = Math.max(0, lengthUnits - (o.offsetAlongWall + width));
          // Ties go LEFT (owner). Comparing with <= is the whole rule.
          const nearLeft = fromLeft <= fromRight;
          return {
            id: codes.get(o.id) || `W${wall.wallNumber ?? '?'}-${String.fromCharCode(65 + i)}`,
            // The model's own id, so the sheet can label the SAME item where it
            // is drawn on the elevation and the table can be read against it.
            obstructionId: o.id,
            type: o.label ?? o.type,
            widthUnits: width,
            heightUnits: o.heightUnits > 0 ? o.heightUnits : 0,
            // The measurement a tape actually takes: to the NEAR edge of the item.
            fromEnd: nearLeft ? 'FL' : 'FR',
            fromEndUnits: nearLeft ? fromLeft : fromRight,
            fromLeftUnits: fromLeft,
            fromRightUnits: fromRight,
            fromFloorUnits: o.offsetFromFloor ?? 0,
            offsetAlongWall: o.offsetAlongWall,
          };
        });
      rows.push({
        wallId: wall.id,
        wallNumber: wall.wallNumber ?? null,
        label: `W${wall.wallNumber ?? '?'}`,
        lengthUnits,
        obstructions: items,
        ceilingOpenings: [],
      });
    }
    // Low to high, the order the sheet lists them in (owner).
    rows.sort((a, b) => (a.wallNumber ?? 0) - (b.wallNumber ?? 0));
    const wallById = new Map(rows.map((row) => [row.wallId, row]));
    const ceilingOpenings = state.ceilingOpenings.map((opening, index) => {
      const [reference1Id, reference2Id] = opening.referenceWallIds ?? [];
      const reference1 = wallById.get(reference1Id);
      const reference2 = wallById.get(reference2Id);
      const item = {
        id: `C${index + 1}`,
        openingId: opening.id,
        type: ({ attic_stairs: 'Drop-down attic stairs', attic_hatch: 'Attic hatch', ceiling_fan: 'Ceiling fan', ceiling_light: 'Ceiling light', ceiling_sprinkler: 'Sprinkler' })[opening.kind] ?? 'Room obstruction',
        reference1Id,
        reference2Id,
        reference1: reference1?.label ?? 'W?',
        reference2: reference2?.label ?? 'W?',
        fromReference1Units: opening.xUnits,
        fromReference2Units: opening.yUnits,
        widthUnits: opening.widthUnits,
        lengthUnits: opening.lengthUnits,
        heightUnits: opening.heightUnits ?? 1,
        ...(opening.kind === 'ceiling_fan' ? {
          kind: 'ceiling_fan',
          diameterUnits: opening.diameterUnits ?? opening.widthUnits,
          fromFloorUnits: Number.isFinite(opening.fromFloorUnits)
            ? opening.fromFloorUnits
            : Math.max(0, state.ceilingHeightUnits - (opening.heightUnits ?? 1)),
        } : {}),
        ladderFloorOffsetUnits: opening.kind === 'attic_stairs' && Number.isFinite(opening.ladderFloorOffsetUnits)
          ? opening.ladderFloorOffsetUnits : null,
      };
      reference1?.ceilingOpenings.push(item);
      if (reference2 !== reference1) reference2?.ceilingOpenings.push(item);
      return item;
    });
    return { walls: rows, ceilingOpenings, partitionObstructionCount };
  }

  /** Drops obstructions whose wall no longer exists (e.g. after a re-cleanup). */
  function pruneObstructions() {
    const ids = new Set(state.walls.map((w) => w.id));
    // Partitions are keyed by their footprint SEGMENT — drop any orphaned ones.
    state.partitions = state.partitions.filter((p) => ids.has(p.segmentId));
    // Obstructions and labels may also be anchored to a partition FACE (a
    // two-sided-wall side, ADR 0035); those hosts are valid too.
    const hosts = new Set([...ids, ...partitionFaceIds()]);
    state.obstructions = state.obstructions.filter((o) => hosts.has(o.wallId));
    state.wallLabels = state.wallLabels.filter((l) => hosts.has(l.wallId));
    state.sections = state.sections.filter((s) => hosts.has(s.wallId));
    // Depth helper lines only live on real walls.
    state.planHelperLines = state.planHelperLines.filter((h) => ids.has(h.wallId));
    // A per-wall stack target outlives nothing: if the wall is gone the
    // override is meaningless, and leaving it would silently re-apply to a
    // future wall that happened to reuse the id.
    for (const key of Object.keys(state.wallStackTargets)) {
      if (!hosts.has(key)) delete state.wallStackTargets[key];
    }
    // An L-shelf's SECOND wall can be the one that vanished. Drop the leg
    // rather than leave the shelf reaching toward a wall that isn't there.
    for (const s of state.sections) {
      if (s.secondWallId && !hosts.has(s.secondWallId)) {
        s.secondWallId = null;
        s.legTwoUnits = null;
      }
    }
  }

  // ---- interior / partition walls (ADR 0032) -------------------------------

  /**
   * Adds an interior/partition wall by SPLITTING the host wall into
   * wall / partition / wall — exactly like a door, but the middle segment is a
   * solid partition footprint (thickness wide) that also sticks INTO the room.
   *
   * The point of the split: the two flanking walls are ordinary, independently
   * measurable walls, so the designer types each side's clear width directly
   * (e.g. 40 and 60) and never has to add the partition's thickness into a
   * combined host length themselves — the app carries the +thickness (owner,
   * 2026-07-25). The footprint segment is an `opening` (a legal, already-synced
   * segment kind, so no schema change) that the renderer draws SOLID; its
   * protrusion depth lives in `state.partitions`, keyed by the segment id.
   *
   * @returns {string|null} the partition footprint segment's id, or null
   */
  function addPartition(wallId) {
    const index = state.walls.findIndex((w) => w.id === wallId);
    if (index < 0) return null;
    const wall = state.walls[index];
    if ((wall.segmentKind ?? 'wall') !== 'wall') return null; // needs a real host wall

    const wallPx = distance(wall.start, wall.end);
    const maxMidPx = wallPx - 2 * MIN_SEGMENT_PX;
    if (maxMidPx < 4) return null; // too short to hold a partition plus its returns

    const scale = state.pxPerUnit && state.pxPerUnit > 0 ? state.pxPerUnit : null;
    const thicknessUnits = rules.defaultWallThicknessUnits;
    const desiredPx = scale ? thicknessUnits * scale : Math.min(wallPx / 5, 20);
    const midPx = Math.max(3, Math.min(desiredPx, maxMidPx));
    const segmentPx = (wallPx - midPx) / 2; // center the partition on the host by default

    const a0 = { ...wall.start };
    const a1 = advance(a0, wall.internalAngle, segmentPx);
    const a2 = advance(a1, wall.internalAngle, midPx);
    const a3 = { ...wall.end };
    const shared = { internalAngle: wall.internalAngle, angleDeg: wall.angleDeg };
    const include = wall.includeInElevation !== false;

    // Preserve a MEASURED host length by giving each new wall half of it, rather
    // than handing both back to the solver as "auto" and losing the number the
    // designer typed (owner). An unmeasured (auto) host stays auto — there is no
    // length to keep. The footprint is measured at the thickness.
    const half = wall.isMeasured && wall.lengthUnits > 0 ? wall.lengthUnits / 2 : null;
    const flankMeasured = half != null;
    const leftSegment = { ...shared, id: makeId(), start: a0, end: a1, segmentKind: 'wall', isMeasured: flankMeasured, lengthUnits: half, includeInElevation: include };
    const midSegment = { ...shared, id: makeId(), start: a1, end: a2, segmentKind: 'opening', isMeasured: true, lengthUnits: thicknessUnits };
    const rightSegment = { ...shared, id: makeId(), start: a2, end: a3, segmentKind: 'wall', isMeasured: flankMeasured, lengthUnits: half, includeInElevation: include };

    if (state.wallOneOverrideId === wall.id) state.wallOneOverrideId = rightSegment.id;

    // Register the partition BEFORE renumbering so the weave gives its faces
    // their numbers in place (renumber reads state.partitions).
    state.partitions.push({ segmentId: midSegment.id, depthUnits: rules.defaultPartitionLengthUnits, includeInElevation: true });
    const next = [
      ...state.walls.slice(0, index),
      leftSegment,
      midSegment,
      rightSegment,
      ...state.walls.slice(index + 1),
    ];
    state.walls = renumber(next);
    state.selectedWallId = midSegment.id;
    resolve();
    return midSegment.id;
  }

  /**
   * Removes a partition: drops its record and fuses the wall back together
   * (the same collinear merge a removed door does), so the host wall is whole
   * again. Returns false if the id is not a partition footprint.
   */
  function removePartition(segmentId) {
    if (!state.partitions.some((p) => p.segmentId === segmentId)) return false;
    state.partitions = state.partitions.filter((p) => p.segmentId !== segmentId);
    // Drop obstructions/labels that lived on this partition's (now-gone) faces.
    const faceIds = new Set([`${segmentId}~A`, `${segmentId}~B`]);
    state.obstructions = state.obstructions.filter((o) => !faceIds.has(o.wallId));
    state.wallLabels = state.wallLabels.filter((l) => !faceIds.has(l.wallId));
    state.sections = state.sections.filter((s) => !faceIds.has(s.wallId));
    removeDoor(segmentId); // merges the two flanking walls back into one
    return true;
  }

  /**
   * Sets how far a partition sticks INTO the room (its protrusion depth), in
   * inches (parsed like a length). Its THICKNESS is the footprint segment's
   * length — edited through the normal length field. Rejects non-positive.
   */
  function setPartitionDepth(segmentId, rawValue) {
    const partition = state.partitions.find((p) => p.segmentId === segmentId);
    if (!partition) return false;
    const parsed = parseLength(rawValue);
    if (parsed === null || parsed <= 0) return false;
    partition.depthUnits = parsed;
    return true;
  }

  /** True if a segment is a partition footprint (drawn solid + a spur, ADR 0032). */
  function isPartition(segmentId) {
    return state.partitions.some((p) => p.segmentId === segmentId);
  }

  /** A partition's protrusion depth, or null. */
  function partitionDepthFor(segmentId) {
    return state.partitions.find((p) => p.segmentId === segmentId)?.depthUnits ?? null;
  }

  /** Whether a partition's two faces appear in the elevation (default true). */
  function partitionElevationFor(segmentId) {
    const p = state.partitions.find((x) => x.segmentId === segmentId);
    return p ? p.includeInElevation !== false : false;
  }

  /** Include or exclude a partition's faces from the elevation. */
  function setPartitionElevation(segmentId, include) {
    const p = state.partitions.find((x) => x.segmentId === segmentId);
    if (!p) return false;
    p.includeInElevation = include === undefined ? !(p.includeInElevation !== false) : !!include;
    return true;
  }

  /** All partitions with their live footprint segment, orphans dropped. */
  function partitionSegments() {
    return state.partitions
      .map((p) => ({ segmentId: p.segmentId, depthUnits: p.depthUnits, segment: state.walls.find((w) => w.id === p.segmentId) }))
      .filter((p) => p.segment);
  }

  /**
   * The two FACES of each partition as derived, NUMBERED wall objects (ADR
   * 0032): a partition is a two-sided wall, so each long side is its own
   * numbered wall — it appears as a panel in the elevation and a labelled edge
   * on the plan. Faces are DERIVED (never in the closure loop): each runs from
   * the footprint edge INTO the room by the partition's depth, so its length is
   * the depth. Numbered AFTER the boundary walls (they continue the sequence).
   * Returns [] until the room is solved (needs pxPerUnit for the geometry).
   */
  function partitionFaceWalls() {
    const scale = state.pxPerUnit;
    if (!(scale > 0) || state.partitions.length === 0) return [];

    // Winding-based inward normal, matching the plan render and wall thickness.
    let area = 0;
    const pts = state.walls.map((w) => w.start);
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      area += a.x * b.y - b.x * a.y;
    }
    const winding = area >= 0 ? 1 : -1;

    const faces = [];
    for (const p of state.partitions) {
      const seg = state.walls.find((w) => w.id === p.segmentId);
      if (!seg) continue;
      // Woven numbers set by renumber(); fall back to a plain pair if absent.
      const [numA, numB] = state.partitionFaceNumbers[seg.id] ?? [null, null];
      const depthPx = p.depthUnits * scale;
      const dx = seg.end.x - seg.start.x;
      const dy = seg.end.y - seg.start.y;
      const len = Math.hypot(dx, dy) || 1;
      const ni = { x: (-dy / len) * winding, y: (dx / len) * winding }; // into the room
      const elevated = p.includeInElevation !== false;
      const mk = (base, side, wallNumber) => ({
        id: `${seg.id}~${side}`,
        partitionSegmentId: seg.id,
        side,
        segmentKind: 'wall',
        isPartitionFace: true,
        isMeasured: true,
        lengthUnits: p.depthUnits,
        wallNumber,
        includeInElevation: elevated,
        start: { x: base.x, y: base.y },
        end: { x: base.x + ni.x * depthPx, y: base.y + ni.y * depthPx },
      });
      faces.push(mk(seg.start, 'A', numA), mk(seg.end, 'B', numB));
    }
    return faces;
  }

  // ---- standard wall labels (ADR 0034) -------------------------------------

  /**
   * Adds a section-type label to a wall — a short code shown on BOTH the plan
   * and the elevation. `placement` (from a drag/drop) sets the position;
   * otherwise it lands centered along the wall at the default mounting height.
   * Returns the new label's id, or null (unknown/opening wall, or empty text).
   */
  function addWallLabel(wallId, text, placement = null) {
    const wall = resolveHost(wallId);
    const value = String(text ?? '').trim();
    if (!wall || !value) return null;
    const along = placement && Number.isFinite(placement.offsetAlongWall) ? placement.offsetAlongWall : wallLengthUnits(wall) / 2;
    const floor = placement && Number.isFinite(placement.offsetFromFloor) ? placement.offsetFromFloor : rules.defaultWallLabelHeightUnits;
    const label = {
      id: makeId(),
      wallId,
      offsetAlongWall: Math.max(0, along),
      offsetFromFloor: Math.max(0, floor),
      text: value.slice(0, 40),
      color: '#111827',
    };
    state.wallLabels.push(label);
    return label.id;
  }

  /**
   * Repositions a wall label. `patch` may set offsetAlongWall / offsetFromFloor
   * and re-anchor to another `wallId` (dragged onto a different panel). Clamped
   * to non-negative. Returns false if the id is unknown.
   */
  function moveWallLabel(id, patch = {}) {
    const label = state.wallLabels.find((l) => l.id === id);
    if (!label) return false;
    if (typeof patch.wallId === 'string' && resolveHost(patch.wallId)) label.wallId = patch.wallId;
    if (Number.isFinite(patch.offsetAlongWall)) label.offsetAlongWall = Math.max(0, patch.offsetAlongWall);
    if (Number.isFinite(patch.offsetFromFloor)) label.offsetFromFloor = Math.max(0, patch.offsetFromFloor);
    return true;
  }

  function removeWallLabel(id) {
    const before = state.wallLabels.length;
    state.wallLabels = state.wallLabels.filter((l) => l.id !== id);
    return state.wallLabels.length !== before;
  }

  /** Labels on a given wall. */
  function wallLabelsForWall(wallId) {
    return state.wallLabels.filter((l) => l.wallId === wallId);
  }

  // ---- wall sections (ADR 0037) ---------------------------------------------

  /**
   * Adds a typed section to a wall or partition face, seeded from its type
   * DEFINITION (ADR 0039): per-type default depth/height, shelf/drawer counts
   * for shelving/drawer types, and — for standard-size types like hampers
   * (defaultWidthUnits) — the section arrives LOCKED at that width. Types
   * without a standard width arrive UNLOCKED and auto-fill until the designer
   * types one. Appended after the wall's existing sections. Returns the id.
   */
  function addSection(wallId, typeCode) {
    const wall = resolveHost(wallId);
    const code = String(typeCode ?? '').trim().toUpperCase();
    if (!wall || !code) return null;
    const preset = (rules.sectionTypePresets ?? []).find((p) => String(p.code).toUpperCase() === code) ?? {};
    const flags = Array.isArray(preset.flags) ? preset.flags : [];
    const stdWidth = preset.defaultWidthUnits > 0 ? preset.defaultWidthUnits : null;
    const order = sectionsForWall(wallId).reduce((m, s) => Math.max(m, s.order + 1), 0);
    const section = {
      id: makeId(),
      wallId,
      order,
      typeCode: code.slice(0, 8),
      widthUnits: stdWidth,
      locked: stdWidth !== null,
      fullPanelAfter: false,
      allowWide: stdWidth !== null && stdWidth > rules.sectionMaxOpeningUnits, // a standard size IS the explicit intent
      depthUnits: preset.defaultDepthUnits > 0 ? preset.defaultDepthUnits : rules.sectionDefaultDepthUnits,
      // Height INHERITS (null): space override → type default → global rule,
      // resolved by sectionHeightUnits() at render. Typing an H pins it.
      heightUnits: null,
      shelfCount: flags.includes('shelving') && Number.isInteger(preset.defaultShelves) ? preset.defaultShelves : null,
      drawerCount: flags.includes('drawers') && Number.isInteger(preset.defaultDrawers) ? preset.defaultDrawers : null,
      // Shoes sections carry their OWN pair width (owner: her 8" pairs vs his
      // 9½", boots vs heels) — seeded from the global rule, edited per section.
      pairWidthUnits: flags.includes('shoes') ? (rules.shoePairWidthUnits > 0 ? rules.shoePairWidthUnits : 8) : null,
      // Hanging-into-hanging override (owner, 2026-07-27): this hanging
      // section extends to the corner, accepting the collision with the
      // perpendicular hang. Not advised — flagged with a warning while on.
      intoHanging: false,
      // The override belongs to a CORNER, not merely a section. A single
      // section can span both ends of a short wall, so keeping one flag made
      // choosing one corner silently alter the other. The aggregate legacy
      // field above remains for older documents; new edits use these ends.
      intoHangingStart: false,
      intoHangingEnd: false,
      // Vertical stacking (ADR 0041): null = this unit sits in the wall's
      // horizontal RUN. Non-null = it RESTS on the section with that id,
      // inheriting its horizontal position and width, and is excluded from the
      // run. A stack is a chain: base → unit(stackOnId=base) → unit(stackOnId=…).
      stackOnId: null,
      // Where a stacked unit sits along the base it rests on, from that base's
      // left edge (owner: the designer positions it). 0 = flush left.
      stackOffsetUnits: 0,
      // A "to height" pinned on THIS unit (owner, 2026-08-04): its top runs to
      // this elevation and its height is solved to reach it. null = take the
      // height from the unit/type, or from the stack target if it ends up top.
      targetTopUnits: null,
      // L-shelf (ADR 0042): the SECOND wall this corner unit attaches to, and
      // the leg it reaches along that wall (its host-wall leg is its width).
      // Both legs are independent in length and depth. null until attached.
      secondWallId: null,
      legTwoUnits: null,
      legTwoDepthUnits: null,
      elevCodeDyUnits: null,
      // Per-section countertop thickness override (owner, 2026-07-28). null =
      // inherit the space-level thickness. Only meaningful on a base (a
      // countertop-anchored type): it is the gap above it in a stack.
      countertopThicknessUnits: null,
      // Composable INTERIOR (ADR 0054, plan §5/4a): the ordered element list
      // (top → bottom) src/geometry/interior.js solves to real spans. Stamped
      // from the type's template so a new section IS its preset composition;
      // shelfCount/drawerCount above stay maintained as DERIVED MIRRORS of it.
      // Ids minted at PLACEMENT — the preset template is shared, so its
      // elements must not carry compiled-in identities (per-space doors
      // foundation, 2026-08-29).
      interior: ensureInteriorElementIds(defaultInteriorForPreset(preset, rules), makeId),
      // Section-level doors store covered space ids + style/hardware only;
      // every physical and pricing quantity derives later (ADR 0106).
      doors: null,
      // "Required holes only" (ADR 0061 item B, owner 2026-08-09): this
      // section's panels get the REQUIRED holes (drawer banks, the top/bottom
      // shelves) but NOT the full 32 mm rows, so its shelves take exact
      // designer positions instead of snapping to the ladder. Off by default:
      // full drilling is what the shop does unless someone says otherwise.
      requiredHolesOnly: false,
      // Whether this section HAS a bottom shelf. The owner changed the default
      // for UPPERS to OFF on 2026-08-29; every other structural class still
      // starts ON. `anchor` is the maintained type classification, so this also
      // covers admin-defined uppers rather than naming U36. Read by unitShelves
      // and the base-trim linear-foot total in src/pricing/space-pricing.js.
      bottomShelf: preset.anchor !== 'top',
      // Corner connecting-shelf treatment at each end of this wall run. The
      // reserved span is geometry and never disappears; these settings only
      // decide which boards occupy it and whether its depth inherits.
      connectingShelfStart: { top: true, bottom: true, depthUnits: null },
      connectingShelfEnd: { top: true, bottom: true, depthUnits: null },
    };
    // The counts are DERIVED MIRRORS of the interior (ADR 0054), so mirror them
    // from the stamped composition rather than from the preset's flags — the
    // two diverged the moment a rod started arriving with its structural
    // support shelf (ADR 0060 item 4), and the interior is the source of truth.
    mirrorInteriorCounts(section);
    state.sections.push(section);
    return section.id;
  }

  /** Adds a previously saved modular section component with a fresh identity. */
  function addSectionComponent(wallId, component) {
    if (!component || typeof component !== 'object') return null;
    const id = addSection(wallId, component.typeCode);
    const section = state.sections.find((item) => item.id === id);
    if (!section) return null;
    for (const key of ['locked', 'widthUnits', 'allowWide', 'fullPanelAfter', 'depthUnits', 'heightUnits',
      'shelfCount', 'drawerCount', 'pairWidthUnits', 'countertopThicknessUnits', 'interior', 'doors',
      'requiredHolesOnly', 'bottomShelf', 'connectingShelfStart', 'connectingShelfEnd']) {
      if (component[key] !== undefined) section[key] = structuredClone(component[key]);
    }
    mirrorInteriorCounts(section);
    return id;
  }

  // ---- composable interiors (ADR 0054) --------------------------------------

  /** The preset for a section's type code, or {} when the type is unknown. */
  function presetFor(section) {
    return (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(section?.typeCode ?? '').toUpperCase()) ?? {};
  }

  /**
   * A section's EFFECTIVE interior (ADR 0054). A section that carries one
   * returns it (fresh copy — edits go through setSectionInterior, never by
   * mutating the read). A LEGACY section (hydrated from a pre-interior doc,
   * interior null) derives its composition from its type preset + its own
   * shelf/drawer counts, so every old sketch reads as composed without being
   * rewritten on load — the stored doc only changes when the designer edits.
   */
  function sectionInterior(id) {
    const section = typeof id === 'object' && id !== null ? id : state.sections.find((s) => s.id === id);
    if (!section) return null;
    const stored = normalizeInterior(section.interior);
    if (stored) return stored;
    return defaultInteriorForPreset(presetFor(section), rules, {
      shelfCount: section.shelfCount,
      drawerCount: section.drawerCount,
    });
  }

  /**
   * Whether a section HANGS clothes — read from its EFFECTIVE composition
   * (ADR 0057, plan §5/4d): it hangs iff its interior holds at least one rod
   * element. This is what drives the corner clearance (ADR 0038/0048) and the
   * hanging-into-hanging affordance. A legacy section derives its composition
   * from its type's flags (sectionInterior → defaultInteriorForPreset), so a
   * hanging-flagged type still hangs — but a designer who removes every rod
   * from a DH in the interior editor genuinely stops needing the clearance,
   * and one who adds a rod to an SH starts. The type is not the truth; the
   * composition is.
   */
  function sectionHasHang(idOrSection) {
    const interior = sectionInterior(idOrSection);
    return Array.isArray(interior) && interior.some((el) => el.kind === 'rod');
  }

  /**
   * The front edge used to measure a WALKWAY past one section (C13, owner
   * 2026-08-29). A non-hanging section stops at its actual panel/shelf front.
   * A hanging section uses the nominal 24" hanging clearance — "despite actual
   * depth typically being less" — because clothes project beyond the carcass.
   *
   * Keep the owner's reason here: replacing the nominal figure with real depth
   * would look like a precision fix, but would make the walkway optimistic.
   */
  function sectionWalkwayDepthUnits(idOrSection) {
    const section = typeof idOrSection === 'object' && idOrSection !== null
      ? idOrSection : state.sections.find((s) => s.id === idOrSection);
    if (!section) return 0;
    const actual = section.depthUnits > 0 ? section.depthUnits : rules.sectionDefaultDepthUnits;
    if (!sectionHasHang(section)) return actual > 0 ? actual : 0;
    return rules.sectionHangClearanceUnits > 0 ? rules.sectionHangClearanceUnits : 24;
  }

  /** Whether a section's effective interior holds shoe shelves — gates the
   *  per-section pair-width affordance the same way sectionHasHang gates the
   *  clearance: by composition, not by type flag (ADR 0057). */
  function sectionHasShoeShelves(idOrSection) {
    const interior = sectionInterior(idOrSection);
    return Array.isArray(interior) && interior.some((el) => el.kind === 'shoeShelves' && el.count > 0);
  }

  /**
   * How a rod element REPORTS (ADR 0051 step 2, executed in ADR 0057): the
   * category on the ROD ELEMENT wins (ADR 0054 put it there); a rod that
   * carries none falls back to its section TYPE's hangCategory. Either is
   * honoured only when it names a key in the hang_categories rule — an
   * unknown category is not silently invented into the list. A rod that
   * resolves to nothing reports under the reserved 'uncategorized' bucket:
   * its inches stay VISIBLE in the summary (dropping capacity silently is
   * worse than an ugly row), but no requirement can be entered against it —
   * assigning the type a category in Admin is the fix.
   */
  function rodCategoryKey(el, preset) {
    const valid = new Set((rules.hangCategories ?? []).map((c) => String(c?.key ?? '')).filter(Boolean));
    if (typeof el?.hangCategory === 'string' && valid.has(el.hangCategory)) return el.hangCategory;
    if (typeof preset?.hangCategory === 'string' && valid.has(preset.hangCategory)) return preset.hangCategory;
    return 'uncategorized';
  }

  /** A hang category's display label, from the rule (key as last resort). */
  function hangCategoryLabel(key) {
    if (key === 'uncategorized') return 'Hang (uncategorized)';
    return (rules.hangCategories ?? []).find((c) => c?.key === key)?.label ?? key;
  }

  /**
   * A placed unit's SOLVED interior — the elevation feed (ADR 0055, plan
   * §5/4b). Takes a unit exactly as stacksForWall() emits it (`section`,
   * solved {bottomUnits, topUnits}, its `shelves`) so the interior is solved
   * in the same clear band the carcass drawing already shows: interiorBand()
   * carves the opening between the structural ¾" shelves, which keeps the
   * result platform-, stack-, soffit- and to-height-aware for free (ADR 0054).
   *
   * A LEGACY section (interior null) solves its DERIVED composition —
   * sectionInterior() falls back to the type preset + counts — so old
   * sketches gain interior fidelity on the elevation without being rewritten.
   *
   * @returns {{solved:object, doors:object|null, band:object}|null} null only
   *   when there is nothing at all (no elements AND no doors). An
   *   openSpace-only interior (e.g. OPEN) still returns a feed — it simply
   *   yields no marks, which is what "nothing inside" should look like.
   */
  function interiorForUnit(unit) {
    const section = unit?.section;
    if (!section || !(unit.topUnits > unit.bottomUnits)) return null;
    const interior = sectionInterior(section) ?? [];
    const doors = normalizeDoors(section.doors);
    if (interior.length === 0 && !doors) return null;
    const band = interiorBand(unit, unit.shelves ?? null);
    const solved = solveInterior(interior, band, interiorSolveOpts(rules, {
      requiredHolesOnly: section.requiredHolesOnly === true,
      // The hole ladder is measured from the PANEL's bottom edge, which is the
      // placed unit's own bottom — NOT the band bottom (ADR 0061 item A).
      panelBottomUnits: unit.bottomUnits,
    }));
    return { solved, doors, band };
  }

  /**
   * Keeps the legacy counts as DERIVED MIRRORS of the interior (ADR 0054):
   * the interior is the source of truth; shelfCount/drawerCount are written
   * from it so storageSummary()/storageNeedTypes() and the side panel's count
   * fields keep reading what they always read. A count stays null when the
   * composition has none of that thing AND the type never stated one — the
   * summary's preset-default fallback must not resurrect for a section whose
   * interior genuinely holds zero.
   */
  function mirrorInteriorCounts(section) {
    const counts = interiorCounts(section.interior);
    const flags = Array.isArray(presetFor(section).flags) ? presetFor(section).flags : [];
    section.shelfCount = flags.includes('shelving') || flags.includes('shoes') || counts.shelves > 0
      ? counts.shelves : null;
    section.drawerCount = flags.includes('drawers') || counts.drawers > 0
      ? counts.drawers : null;
  }

  /**
   * Replaces a section's interior composition wholesale (what the 4c editor
   * will call per edit). The list is normalized — junk elements drop, every
   * element gains its `accessories` placeholder — and the legacy counts are
   * re-derived from it. A non-array is rejected, not treated as "clear":
   * clearing is `[]`, explicitly.
   */
  function setSectionInterior(id, interior) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || !Array.isArray(interior)) return false;
    // Surviving elements keep their ids (a door attached to a space must
    // survive the split that spliced a new neighbour in); only id-less or
    // duplicated elements mint (per-space doors foundation, 2026-08-29).
    section.interior = ensureInteriorElementIds(interior, makeId);
    mirrorInteriorCounts(section);
    return true;
  }

  /**
   * Sets a section's "required holes only" mode (ADR 0061 item B). True = the
   * 32 mm rows are NOT drilled, so this section's shelves keep the exact
   * positions the designer states; the required holes (drawer banks, top and
   * bottom shelves) are drilled either way and the solved result says so.
   */
  function setSectionRequiredHolesOnly(id, on) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    section.requiredHolesOnly = on === true;
    return true;
  }

  /**
   * Sets or clears a section's doors. Current writes name the first and last
   * covered SPACE element ids; all physical geometry derives from that extent.
   * Legacy coverage records still load, but the setter will not create a new
   * positional record after persistent space identity exists.
   */
  function setSectionDoors(id, doors) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    if (doors === null) { section.doors = null; return true; }
    const cleaned = normalizeDoors(doors);
    if (!cleaned) return false;
    if (cleaned.fromElementId && cleaned.toElementId) {
      const spaces = (sectionInterior(section) ?? []).filter((el) => isSpaceKind(el?.kind));
      const ids = new Set(spaces.map((el) => el.id));
      if (!ids.has(cleaned.fromElementId) || !ids.has(cleaned.toElementId)) return false;
    }
    section.doors = cleaned;
    return true;
  }

  /**
   * Stacks a NEW typed unit on top of `belowId` (ADR 0041): it rests on that
   * unit plus the join gap (the below unit's countertop when it is a base, else
   * this unit's capshelf). Seeded exactly like a run section, then re-parented —
   * it inherits the horizontal position and width of the run section under it,
   * so it carries no width of its own. Returns the new id, or null.
   */
  /**
   * Whether a type may be STACKED as an upper unit (C1, owner 2026-08-29:
   * "stacked units are shelving sections (including L shelves, but not shoe
   * shelves) with or without door"). FLAGS, never codes (D110): shelving,
   * not shoes — SS carries BOTH flags, so a shelving-only check would admit
   * the type the owner excluded by name — and not countertop-anchored: a
   * base is a product at its anchor, never an upper. Doors are config, not
   * type, so they need no gate here.
   */
  /** A type's preset flags, for UI decisions that key off WHAT a type is. */
  function presetFlagsFor(typeCode) {
    const preset = (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(typeCode ?? '').toUpperCase());
    return Array.isArray(preset?.flags) ? preset.flags : [];
  }

  function isStackableType(typeCode) {
    const preset = (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(typeCode ?? '').toUpperCase());
    if (!preset) return false;
    const flags = Array.isArray(preset.flags) ? preset.flags : [];
    return flags.includes('shelving') && !flags.includes('shoes') && preset.anchor !== 'countertop';
  }

  function stackUnit(belowId, typeCode) {
    if (!isStackableType(typeCode)) return null; // the stack list is the owner's sentence
    const below = state.sections.find((s) => s.id === belowId);
    if (!below) return null;
    const id = addSection(below.wallId, typeCode);
    if (!id) return null;
    const unit = state.sections.find((s) => s.id === id);
    unit.stackOnId = belowId;
    unit.locked = false;
    unit.order = below.order; // shares the run slot, for stable ordering
    // Depth/width freedom depends on WHAT it rests on (owner, 2026-07-28):
    // over a BASE a unit may be shallower and narrower (12"/16" shelving over a
    // 22" base, 18" wide over a 30" base); over anything else it must match the
    // unit below. See stackedDepthUnits/stackedWidthUnits, which enforce this at
    // resolve time so a later type change can't leave a mismatch behind.
    unit.widthUnits = null; // null = follow the rule (own default or inherit)
    // Born EMPTY (owner, 2026-08-29): "when stacking shelves on top of
    // another unit, it should default to empty space then the designer can
    // add the number of shelving sections after." The preset's seeded
    // interior is the RUN-level starting point; a stacked unit starts as the
    // one thing every editor path starts from. Price unmoved: a stack prices
    // as its C1 config (ST|ShOp / ST|ShDr), never its shelf count.
    unit.interior = ensureInteriorElementIds([{ kind: 'openSpace' }], makeId);
    mirrorInteriorCounts(unit);
    return id;
  }

  /**
   * A stacked unit's EFFECTIVE depth (ADR 0041). Over a base it keeps its own
   * (a 22" base carries 12"/16" shelving); over a non-base it must match the
   * unit below, so the whole column reads as one carcass.
   */
  function stackedDepthUnits(unit, below) {
    const own = unit.depthUnits > 0 ? unit.depthUnits : rules.sectionDefaultDepthUnits;
    if (!below) return own;
    if (sectionAnchor(below) === 'countertop') return own; // over a base: free
    return below.depthUnits > 0 ? below.depthUnits : rules.sectionDefaultDepthUnits;
  }

  /** True when this unit may carry its own depth/width (it rests on a base). */
  function stackedDimsAreFree(unit) {
    if (!unit?.stackOnId) return true;
    const below = state.sections.find((s) => s.id === unit.stackOnId);
    return !!below && sectionAnchor(below) === 'countertop';
  }

  /**
   * A stacked unit's EFFECTIVE width and horizontal offset from the run
   * section's left edge (ADR 0041). Over a BASE the unit may be narrower than
   * what it sits on (an 18" unit over a 30" base) and the designer positions it
   * along the base; over a non-base it inherits the width and position of the
   * unit below, so the column stays flush. Clamped so a unit can never overhang.
   */
  function stackedSpan(unit, below, runWidthUnits, belowSpan) {
    if (!below || sectionAnchor(below) !== 'countertop') {
      // Above a non-base: match what's underneath.
      return belowSpan ?? { widthUnits: runWidthUnits, offsetUnits: 0 };
    }
    const host = belowSpan ?? { widthUnits: runWidthUnits, offsetUnits: 0 };
    const width = unit.widthUnits > 0 ? Math.min(unit.widthUnits, host.widthUnits) : host.widthUnits;
    const wanted = unit.stackOffsetUnits > 0 ? unit.stackOffsetUnits : 0;
    const offset = host.offsetUnits + Math.max(0, Math.min(wanted, host.widthUnits - width));
    return { widthUnits: width, offsetUnits: offset };
  }

  /**
   * Positions a stacked unit along the base it rests on (owner: the designer
   * places it). Measured from the base's left edge; clamped at render so it can
   * never overhang. Blank/0 = flush left.
   */
  function setStackOffset(id, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    const text = String(rawValue ?? '').trim();
    if (text === '') { section.stackOffsetUnits = 0; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed < 0) return false;
    section.stackOffsetUnits = parsed;
    return true;
  }

  /**
   * Pins a stacked unit's "to height" — the elevation its TOP runs to, with its
   * height solved to reach it (owner, 2026-08-04).
   *
   * The stack target already does this for whatever unit happens to be on top.
   * What it could not do is fix a MIDDLE unit: in a three-tier stack the
   * designer wants the middle to top out at, say, 60" and the unit above it to
   * fill from there to the stack target. Pinning the TOP rather than the height
   * is what the designer actually measures — off the floor, with a tape.
   *
   * Only meaningful on a stacked unit; the run section below is placed by its
   * class anchor and sized by its type. Blank clears the pin.
   */
  function setStackTargetTop(id, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || !section.stackOnId) return false;
    const text = String(rawValue ?? '').trim();
    if (text === '') { section.targetTopUnits = null; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed <= 0) return false;
    section.targetTopUnits = parsed;
    return true;
  }

  /**
   * Removes a stacked unit. Anything resting ON it drops down to rest on what
   * it rested on, so a stack never keeps a dangling reference.
   */
  function unstackUnit(id) {
    const unit = state.sections.find((s) => s.id === id);
    if (!unit || !unit.stackOnId) return false;
    const parent = unit.stackOnId;
    for (const s of state.sections) if (s.stackOnId === id) s.stackOnId = parent;
    state.sections = state.sections.filter((s) => s.id !== id);
    return true;
  }

  /** The units resting on `baseId`, bottom → top (follows the stack chain). */
  function stackChainFor(baseId) {
    const chain = [];
    let currentId = baseId;
    // Guard against a cycle in hand-edited/synced data: a stack can never be
    // longer than the section list.
    for (let guard = 0; guard < state.sections.length; guard += 1) {
      const next = state.sections.find((s) => s.stackOnId === currentId);
      if (!next) break;
      chain.push(next);
      currentId = next.id;
    }
    return chain;
  }

  // ---- shared-space split (owner, 2026-07-27) -------------------------------

  /** Turns the neutral A/B split on or off. Assignments survive a toggle-off. */
  function setSharedSpace(enabled) {
    state.sharedSpace.enabled = !!enabled;
    return true;
  }

  /** Renames person 0 or 1. Empty names fall back to the default label. */
  function setPersonName(index, name) {
    if (index !== 0 && index !== 1) return false;
    const value = String(name ?? '').trim();
    state.sharedSpace.persons[index] = value || (index === 0 ? 'A' : 'B');
    return true;
  }

  /** Assigns a section to person 0/1, or null = unassigned (total only). */
  function assignSection(id, personIndex) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    section.assignedTo = personIndex === 0 || personIndex === 1 ? personIndex : null;
    return true;
  }

  /**
   * Sets a section's shelf or drawer count (non-negative integer).
   *
   * On a section carrying a composed interior (ADR 0054) the edit flows INTO
   * the composition — resize the drawer bank, set the shoe-shelf count,
   * add/remove shelf elements — and the stored count is then re-derived from
   * it, so the interior stays the source of truth and the two can never
   * disagree. A legacy section (interior null) keeps the plain count write.
   */
  function setSectionCount(id, field, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || (field !== 'shelfCount' && field !== 'drawerCount')) return false;
    const parsed = Number(rawValue);
    if (!Number.isInteger(parsed) || parsed < 0) return false;
    const stored = normalizeInterior(section.interior);
    if (stored) {
      section.interior = applyCountsToInterior(stored, {
        shelfCount: field === 'shelfCount' ? parsed : null,
        drawerCount: field === 'drawerCount' ? parsed : null,
      }, rules);
      mirrorInteriorCounts(section);
      return true;
    }
    section[field] = parsed;
    return true;
  }

  /**
   * Locks a section to an exact opening width (parsed like a length, so 30,
   * 30.5 and 30 1/2 all work). Widths above the max-opening rule are accepted
   * only when the section already carries the explicit wide override — the
   * caller should offer that override rather than this silently failing, so
   * the return distinguishes: true = set; 'needs-override' = width is legal
   * but requires allowWide first; false = unparseable/unknown.
   */
  function setSectionWidth(id, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    const parsed = parseLength(rawValue);
    if (parsed === null || parsed <= 0) return false;
    if (parsed > rules.sectionMaxOpeningUnits && !section.allowWide) return 'needs-override';
    section.widthUnits = parsed;
    section.locked = true;
    return true;
  }

  /** Back to auto-fill: clears the lock (the typed width is forgotten). */
  function unlockSection(id) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    section.locked = false;
    section.widthUnits = null;
    return true;
  }

  /**
   * Explicitly allows (or revokes) a section wider than the max-opening rule
   * (owner: openings over 36" only by explicit override).
   */
  function setSectionAllowWide(id, allow) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    section.allowWide = !!allow;
    return true;
  }

  /** Toggles the join AFTER this section between a shared and a double panel. */
  function toggleSectionJoin(id) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    section.fullPanelAfter = !section.fullPanelAfter;
    return true;
  }

  /**
   * Sets a section's depth (plan protrusion) or height (elevation), > 0.
   * A BLANK height clears the per-section pin back to inheriting the space /
   * type default (depth stays concrete — it has no space-level override).
   */
  function setSectionDim(id, field, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || (field !== 'depthUnits' && field !== 'heightUnits' && field !== 'pairWidthUnits')) return false;
    // "Should not be overridden" (owner, on wall beds) is enforced here as well
    // as in `sectionHeightSource`. Refusing the write rather than ignoring it
    // matters: a stored `heightUnits` that the resolver then declines to use
    // would sit in the document disagreeing with the drawing, and the next
    // reader would have to work out which one was lying.
    if (field === 'heightUnits' && sectionHeightIsFixed(section)) return false;
    if (field === 'heightUnits' && String(rawValue ?? '').trim() === '') {
      section.heightUnits = null;
      return true;
    }
    const parsed = parseLength(rawValue);
    if (parsed === null || parsed <= 0) return false;
    if (field === 'heightUnits') {
      const snapped = systemPanelHeight(parsed);
      // D113: a base has a MAXIMUM. Refused, not clamped (the house rule):
      // the stored value is what the drawing shows, and a silently-clamped
      // entry would be a number the designer never typed.
      //
      // TRIPWIRE (Coordinator ruling, 2026-08-28): the comparison uses the
      // SNAPPED value deliberately — no stored height may exceed the cap —
      // but that ordering is currently UNTESTABLE, and correctness rests on
      // a numeric coincidence: with the System-32 ladder (pitch 32mm, first
      // offset 13mm) the steps beside today's cap are 34.764" and 36.024",
      // and nearest-snapping opens its snap-up window at ~35.394" — ABOVE
      // the 35" cap — so no input that parses under the cap can snap over
      // it, and no ladder step equals the cap for the epsilon to decide.
      // Comparing `parsed` instead of `snapped`, or dropping the epsilon,
      // survives every test TODAY. The condition that makes this live: a
      // catalog cap landing ON or JUST BELOW a ladder step. If you change a
      // priced base height and the new cap sits within one snap window of a
      // ladder step, this ordering becomes observable — pin it then.
      const max = sectionHeightMaxUnits(section);
      if (max !== null && snapped > max + 1e-6) return false;
      section.heightUnits = snapped;
      return true;
    }
    section[field] = parsed;
    return true;
  }

  /**
   * A height snapped to the nearest LEGAL System-32 panel height (ADR 0061
   * item A: a panel "stops at spacing where you have the top hole plus 9.5 mm",
   * i.e. 32n − 13 mm). Applied where a height is EDITED and nowhere else —
   * stored heights are never rewritten on load, so no existing drawing moves.
   * Off when `snap_panel_heights_to_system` is false.
   */
  function systemPanelHeight(units) {
    if (rules.snapPanelHeightsToSystem === false) return units;
    const legal = nearestLegalPanelHeight(units, {
      pitchMm: rules.systemHolePitchMm,
      firstOffsetMm: rules.systemHoleFirstOffsetMm,
    });
    return legal ? legal.heightUnits : units;
  }

  /**
   * The space-level section height (owner, 2026-07-27): one setting for an
   * 84" space instead of editing every section. Blank clears it.
   */
  function setSpaceHeight(rawValue) {
    if (String(rawValue ?? '').trim() === '') {
      state.spaceHeightUnits = null;
      return true;
    }
    const parsed = parseLength(rawValue);
    if (parsed === null || parsed <= 0) return false;
    // A space height is a PANEL height, so it obeys the same system rule as a
    // per-section one (ADR 0061) — on edit, never on load.
    state.spaceHeightUnits = systemPanelHeight(parsed);
    return true;
  }

  /**
   * The space platform height (ADR 0040): 0 = on the floor, or a positive value
   * (typ. 3 or 6) that stands the cabinetry on a platform. Accepts a number of
   * units; a non-positive/blank value clears it back to floor. Only the values
   * the shop offers (rules.platformHeights) are otherwise meaningful, but any
   * positive number is stored so the rule can change without a data migration.
   */
  function setPlatformHeight(units) {
    const n = Number(units);
    state.platformHeightUnits = Number.isFinite(n) && n > 0 ? n : 0;
    return state.platformHeightUnits;
  }

  /**
   * The space-level countertop thickness (ADR 0041) — the gap above every base
   * in the space. Blank/non-positive clears it; unparseable is rejected so a
   * typo can't silently move every stacked unit.
   */
  function setCountertopThickness(rawValue) {
    const text = String(rawValue ?? '').trim();
    if (text === '') { state.countertopThicknessUnits = 0; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed < 0) return false;
    state.countertopThicknessUnits = parsed;
    return true;
  }

  /** Per-SECTION countertop override (blank = inherit the space setting). */
  function setSectionCountertop(id, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    const text = String(rawValue ?? '').trim();
    if (text === '') { section.countertopThicknessUnits = null; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed < 0) return false;
    section.countertopThicknessUnits = parsed;
    return true;
  }

  /** Turns "to target height" on/off. Omit `on` to toggle. */
  function setToTargetHeight(on) {
    state.toTargetHeight = on === undefined ? !state.toTargetHeight : Boolean(on);
    return state.toTargetHeight;
  }

  /**
   * The datum stacks run to. Blank returns to the room's ceiling height, so
   * clearing it restores the old "to the ceiling" behaviour rather than
   * disabling the mode.
   */
  function setStackTargetHeight(rawValue) {
    const text = String(rawValue ?? '').trim();
    if (text === '') { state.stackTargetHeightUnits = null; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed <= 0) return false;
    state.stackTargetHeightUnits = parsed;
    return true;
  }

  /**
   * A per-WALL override of the target datum (owner, 2026-08-04: "the to height
   * would generally be set at a room level but it could be at the wall level").
   * Blank removes the override so the wall follows the space again — which is
   * why this is a sparse map and not a value stamped onto every wall: with no
   * overrides the whole room is the same by construction, and a later change to
   * the space figure moves every wall that never disagreed with it.
   */
  function setWallStackTarget(wallId, rawValue) {
    if (!resolveHost(wallId)) return false;
    const text = String(rawValue ?? '').trim();
    if (text === '') { delete state.wallStackTargets[wallId]; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed <= 0) return false;
    state.wallStackTargets[wallId] = parsed;
    return true;
  }

  /** A wall's own target, or null when it simply follows the space. */
  function wallStackTargetUnits(wallId) {
    const own = state.wallStackTargets[wallId];
    return own > 0 ? own : null;
  }

  /**
   * The resolved target datum: this wall's override, else the designer's
   * space-level figure, else the room ceiling.
   */
  function stackTargetHeightUnits(wallId = null) {
    if (wallId && state.wallStackTargets[wallId] > 0) return state.wallStackTargets[wallId];
    if (state.stackTargetHeightUnits > 0) return state.stackTargetHeightUnits;
    return state.ceilingHeightUnits > 0 ? state.ceilingHeightUnits : 0;
  }

  /** Crown-molding allowance below the target. Blank restores the rule default. */
  function setCrownAllowance(rawValue) {
    const text = String(rawValue ?? '').trim();
    if (text === '') { state.crownAllowanceUnits = null; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed < 0) return false;
    state.crownAllowanceUnits = parsed;
    return true;
  }

  /** The resolved crown allowance: the space override, else the rule default. */
  function crownAllowanceUnits() {
    if (state.crownAllowanceUnits > 0) return state.crownAllowanceUnits;
    return rules.crownAllowanceUnits > 0 ? rules.crownAllowanceUnits : 0;
  }

  /** Sets a per-space trim-height override; blank restores the rule default. */
  function setTrimHeight(field, rawValue) {
    const text = String(rawValue ?? '').trim();
    if (text === '') { state[field] = null; return true; }
    const parsed = parseLength(text);
    if (parsed === null || parsed <= 0) return false;
    state[field] = parsed;
    return true;
  }

  /** Crown moulding VISIBLE height (owner, 2026-08-28). Blank = rule default. */
  function setCrownMouldingHeight(rawValue) {
    return setTrimHeight('crownMouldingHeightUnits', rawValue);
  }

  /** The resolved crown height: the space override, else the rule, else 3". */
  function crownMouldingHeightUnits() {
    if (state.crownMouldingHeightUnits > 0) return state.crownMouldingHeightUnits;
    return rules.crownMouldingHeightUnits > 0 ? rules.crownMouldingHeightUnits : 3;
  }

  /** Base trim VISIBLE height. Blank = rule default. */
  function setBaseTrimHeight(rawValue) {
    return setTrimHeight('baseTrimHeightUnits', rawValue);
  }

  /** The resolved base trim height: the space override, else the rule, else 2.5". */
  function baseTrimHeightUnits() {
    if (state.baseTrimHeightUnits > 0) return state.baseTrimHeightUnits;
    return rules.baseTrimHeightUnits > 0 ? rules.baseTrimHeightUnits : 2.5;
  }

  /** A section's EFFECTIVE height: own pin → space override → type → rule. */
  /**
   * A section's height AND where that figure came from (RS-2a).
   *
   * The height is a four-level fallback — the section's own pinned value, else
   * the space's, else the type's default, else the shop-wide one — and until
   * now only the NUMBER came out. The row could therefore say what the height
   * is and never what it is inheriting FROM, which is the difference between
   * "95 1/4" and "95 1/4, from the LH type — change the type and this moves".
   *
   * `source` is a key, not a phrase: the wording belongs to whichever surface
   * is speaking, and the model should not own English.
   *
   * @returns {{units:number, source:'section'|'space'|'type'|'default'}}
   */
  function sectionHeightSource(section) {
    const preset = (rules.sectionTypePresets ?? []).find((p) => String(p.code).toUpperCase() === String(section.typeCode).toUpperCase());
    // A FIXED height outranks everything, including a per-section override.
    //
    // The owner, 2026-08-27: *"The height of wallbeds is fixed (for each type
    // of wallbed) and should not be overridden."* A wall bed is a manufactured
    // product with a catalogue height, exactly like its width — not a carcass
    // built to the room. Class WB is also EXACT-ONLY in `priceSection`, so a
    // bed solved to the space height does not price at all; in any space with
    // a height set, which is every real one, it reported "needs pricing".
    //
    // This is the one place the documented order (section → space → type →
    // shop) is departed from, and it took an owner ruling to do it — see
    // ADR 0099, which raised the seam and deliberately did not decide it.
    if (preset?.fixedHeight === true && preset.defaultHeightUnits > 0) {
      return { units: preset.defaultHeightUnits, source: 'type-fixed' };
    }
    if (section.heightUnits > 0) return { units: section.heightUnits, source: 'section' };
    // D113 (owner, 2026-08-27): a BASE is a product with its own height — the
    // space height is the tall/ceiling datum, and "a base unit would not be
    // stretched to the ceiling (just use a standard tall section or stacked
    // unit)". So the space rung does not apply to countertop-anchored types;
    // an untouched base stands at its type height whatever the room is.
    if (state.spaceHeightUnits > 0 && preset?.anchor !== 'countertop') {
      return { units: state.spaceHeightUnits, source: 'space' };
    }
    if (preset?.defaultHeightUnits > 0) return { units: preset.defaultHeightUnits, source: 'type' };
    return { units: rules.sectionDefaultHeightUnits, source: 'default' };
  }

  /**
   * D113: a base's height MAXIMUM — a maximum, not a lock (only wall beds
   * are truly fixed, D102/ADR 0099). The number is DERIVED, never a
   * constant: the tallest priced height in the type's mapped catalog class
   * (class B today: 35"). The "~48" once seen in coordination notes was the
   * drawer-stack interior rule, an unrelated concept. Null for non-base
   * anchors and for unmapped types — no cap is invented; an unmapped type's
   * needs-pricing line is already the visible signal.
   */
  function sectionHeightMaxUnits(section) {
    const preset = (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(section?.typeCode ?? '').toUpperCase());
    if (preset?.anchor !== 'countertop') return null;
    const row = (rules.cvTypeMapping?.rows ?? [])
      .find((r) => String(r.code).toUpperCase() === String(section?.typeCode ?? '').toUpperCase());
    if (!row?.class) return null;
    let max = null;
    for (const item of rules.cvSectionCatalog?.sections ?? []) {
      if (item.class === row.class && item.heightIn > 0 && (max === null || item.heightIn > max)) max = item.heightIn;
    }
    return max;
  }

  /** Whether a section's type pins its height (see `sectionHeightSource`). */
  function sectionHeightIsFixed(section) {
    const preset = (rules.sectionTypePresets ?? []).find((p) => String(p.code).toUpperCase() === String(section?.typeCode ?? '').toUpperCase());
    return preset?.fixedHeight === true && preset.defaultHeightUnits > 0;
  }

  /** A section's height on the elevation. Delegates, so the number and the
   *  reported source can never disagree. */
  function sectionHeightUnits(section) {
    return sectionHeightSource(section).units;
  }

  /**
   * Swaps a section with its neighbour along the wall (dir = -1 toward the
   * wall start, +1 toward the end). The shared/double JOIN override stays with
   * the POSITION, not the moved section: `fullPanelAfter` describes the
   * boundary after slot i, and reordering the units on either side of a
   * boundary doesn't move the boundary — so the flags swap along with the
   * orders to stay put.
   */
  function moveSection(id, dir) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    const at = sectionsForWall(section.wallId).findIndex((s) => s.id === id);
    return reorderSection(id, at + (dir < 0 ? -1 : 1));
  }

  /**
   * Moves a section to a specific SLOT on its wall (0 = wall start). This is
   * what a drag on the plan/elevation lands on: the drop position says which
   * slot, not which neighbour to swap with. The boundary flags are lifted off
   * the old arrangement by POSITION and re-applied by position afterwards, so
   * a double-panel join stays where the designer put it while units shuffle
   * around it. Returns false for an unknown id or a no-op slot.
   */
  function reorderSection(id, slotIndex) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    const siblings = sectionsForWall(section.wallId);
    const from = siblings.findIndex((s) => s.id === id);
    const to = Math.max(0, Math.min(Number.isFinite(slotIndex) ? Math.round(slotIndex) : from, siblings.length - 1));
    if (to === from) return false;
    const joins = siblings.slice(0, -1).map((s) => s.fullPanelAfter === true);
    siblings.splice(from, 1);
    siblings.splice(to, 0, section);
    siblings.forEach((s, i) => {
      s.order = i;
      s.fullPanelAfter = i < joins.length ? joins[i] : false;
    });
    return true;
  }

  function removeSection(id) {
    const before = state.sections.length;
    // Anything stacked ON this section goes with it (ADR 0041) — a stacked unit
    // has no horizontal slot of its own, so it cannot outlive what it rests on.
    const doomed = new Set([id, ...stackChainFor(id).map((s) => s.id)]);
    const removed = state.sections.filter((s) => doomed.has(s.id));
    state.sections = state.sections.filter((s) => !doomed.has(s.id));
    // A section CONVERTED from a bucket card returns its unit to the tray on
    // any removal path (ADR 0059's unplace semantics — cheap and forgiving).
    for (const s of removed) returnUnitToBucket(s);
    return state.sections.length !== before;
  }

  /**
   * Storage capacity summary across the WHOLE sketch (owner, 2026-07-27):
   * what the drawn sections actually hold, from their SOLVED opening widths.
   *
   * Every figure reads the section's EFFECTIVE COMPOSITION (ADR 0057, plan
   * §5/4d) — sectionInterior(), which derives a legacy section's from its
   * type preset, so old sketches report unchanged:
   *
   * - hang: linear inches = opening × rod ELEMENTS (a double hang's two rods
   *   count its width twice), reported per hang CATEGORY (`hang:short`… —
   *   ADR 0051 step 2, the rod element's category, falling back to the
   *   type's). NO substitution between categories (owner decision, ADR 0051).
   * - shoes: pairs = shoeShelves counts × floor(opening / pair width),
   *   whole pairs per shelf — a 4" remnant holds no pair. Plain shelf
   *   elements do NOT count as shoe shelves.
   * - drawers: the faces composed across every drawer bank.
   *
   * Returns { shared, persons, rows } where each row is { code, name,
   * kind: 'hang'|'shoes'|'drawers', unit, total, byPerson: [a, b] } — hang
   * rows first, rows [] when the sketch has no sections. In a SHARED space
   * (owner, 2026-07-27) assigned sections also land in their person's column;
   * unassigned ones count toward the total only. Recomputed from the live
   * layouts every call, so re-measuring a wall updates the summary.
   */
  // ---- client storage requirements (ADR 0043) --------------------------------

  /** The slots a requirement can be entered against. */
  const NEED_SLOTS = ['space', 'a', 'b'];

  /**
   * The single footwear row's key. `ALL` (not `*`) because the stored-document
   * layer validates need keys against `^(hang|shoes|drawers):[A-Za-z0-9_-]+$`
   * and a punctuation code would be dropped on load without a word of warning.
   */
  const SHOES_NEED_KEY = 'shoes:ALL';

  /** An empty requirement entry — every slot explicitly "not stated". */
  const emptyNeed = () => ({
    existing: { space: null, a: null, b: null },
    needed: { space: null, a: null, b: null },
    shoeGroups: [],
  });

  /**
   * The storage rows a requirement can be captured against, using the SAME
   * keys `storageSummary()` produces, so the two always line up.
   *
   * HANG rows are the hang CATEGORIES (ADR 0051 step 2, executed in ADR
   * 0057): one row per `hang_categories` entry (`hang:short`, `hang:long`…),
   * every category offered whether or not any placed type feeds it yet — a
   * client can need very-short hang for kids' clothes before any section
   * provides it, and the summary then shows the 100% shortfall.
   *
   * SHOES collapse to ONE row (`shoes:ALL`), the way drawers already collapse
   * to `drawers:DRW` — a pair of shoes is a pair whichever shelf holds it, and
   * a client's footwear count is a fact about the CLIENT, not about which
   * catalog product will end up holding it. This row used to be emitted per
   * shoes-flagged TYPE; once a second such type existed (`ESS84` Elite shoe
   * shelves alongside `SS` Shoe shelves) the panel drew two identical
   * "Footwear shelving needs" cards, which is what the owner reported on
   * 2026-08-30 ("why twice?"). Deleting one card would have left the other
   * type to reproduce it, so the collapse happens HERE, at the one place the
   * rows are named.
   *
   * @returns {Array<{key:string, kind:string, code:string, name:string, unit:string}>}
   */
  function storageNeedTypes() {
    const out = [];
    for (const cat of rules.hangCategories ?? []) {
      const key = String(cat?.key ?? '');
      if (!key) continue;
      out.push({ key: `hang:${key}`, kind: 'hang', code: key, name: cat.label ?? key, unit: 'in' });
    }
    let hasDrawers = false;
    let hasShoes = false;
    for (const preset of rules.sectionTypePresets ?? []) {
      const flags = Array.isArray(preset.flags) ? preset.flags : [];
      const code = String(preset.code ?? '').toUpperCase();
      if (!code) continue;
      if (flags.includes('shoes')) hasShoes = true;
      if (flags.includes('drawers')) hasDrawers = true;
    }
    if (hasShoes) out.push({ key: SHOES_NEED_KEY, kind: 'shoes', code: 'ALL', name: 'Footwear shelving', unit: 'pairs' });
    if (hasDrawers) out.push({ key: 'drawers:DRW', kind: 'drawers', code: 'DRW', name: 'Drawers', unit: 'drawers' });
    const order = { hang: 0, shoes: 1, drawers: 2 };
    // The field form reads ordinary hang categories top-to-bottom, with the
    // specialty Very short category immediately beneath Long.
    const hangOrder = { short: 0, medium: 1, long: 2, very_short: 3 };
    return out.sort((x, y) => order[x.kind] - order[y.kind]
      || (x.kind === 'hang' ? (hangOrder[x.code] ?? 99) - (hangOrder[y.code] ?? 99) : 0));
  }

  /**
   * Records one requirement figure. Blank clears it back to "not stated" — the
   * distinction matters, because a cleared `needed` falls back to `existing`
   * while a zero means the client genuinely wants none of that type.
   *
   * Hang requirements are LENGTHS (so `120 1/2` parses like every other length
   * field); pairs and drawers are whole counts.
   *
   * @param {string} key a `storageNeedTypes()` key
   * @param {'existing'|'needed'} field
   * @param {'space'|'a'|'b'} slot whole-space, or person A / B when shared
   * @returns {boolean} false when the value could not be read — never a guess
   */
  function setStorageNeed(key, field, slot, rawValue) {
    if (field !== 'existing' && field !== 'needed') return false;
    if (!NEED_SLOTS.includes(slot)) return false;
    const type = storageNeedTypes().find((t) => t.key === key);
    if (!type) return false;
    const text = String(rawValue ?? '').trim();
    let value = null;
    if (text !== '') {
      value = type.kind === 'hang' ? parseLength(text) : Number(text);
      if (value === null || !Number.isFinite(value) || value < 0) return false;
      if (type.kind !== 'hang') {
        if (!Number.isInteger(value)) return false; // half a drawer is not a thing
      }
    }
    const entry = state.storageNeeds[key] ?? emptyNeed();
    entry[field] = { ...entry[field], [slot]: value };
    state.storageNeeds[key] = entry;
    if (state.storageNeedsSource?.kind === 'client') {
      state.storageNeedsSource = { ...state.storageNeedsSource, designerEditedAt: new Date().toISOString() };
    }
    return true;
  }

  const recalcShoeTotals = (entry) => {
    const groups = Array.isArray(entry.shoeGroups) ? entry.shoeGroups : [];
    for (const slot of ['space', 'a', 'b']) {
      const inSlot = groups.filter((group) => (group.slot ?? 'space') === slot);
      const existing = inSlot.filter((group) => group.existingPairs !== null);
      const neededStated = inSlot.some((group) => group.neededPairs !== null);
      entry.existing = { ...entry.existing, [slot]: existing.length ? existing.reduce((sum, group) => sum + group.existingPairs, 0) : null };
      entry.needed = { ...entry.needed, [slot]: neededStated ? inSlot.reduce((sum, group) => sum + (group.neededPairs ?? group.existingPairs ?? 0), 0) : null };
    }
  };

  /** Replaces a shoe row's free-form footwear measurements after validation. */
  function setShoeInventoryGroups(key, groups) {
    const type = storageNeedTypes().find((item) => item.key === key && item.kind === 'shoes');
    if (!type || !Array.isArray(groups)) return false;
    const normalized = [];
    for (const [index, group] of groups.entries()) {
      const label = String(group?.label ?? '').trim().slice(0, 80);
      const count = (value) => value == null || value === '' ? null : Number(value);
      const length = (value) => value == null || value === '' ? null
        : Number.isFinite(value) ? value : parseLength(String(value));
      const item = {
        id: String(group?.id ?? makeId()).slice(0, 80),
        label,
        slot: ['a', 'b'].includes(group?.slot) ? group.slot : 'space',
        existingPairs: count(group?.existingPairs),
        neededPairs: count(group?.neededPairs),
        widthUnits: length(group?.widthUnits ?? group?.width),
        lengthUnits: length(group?.lengthUnits ?? group?.length),
      };
      const stated = label || [item.existingPairs, item.neededPairs, item.widthUnits, item.lengthUnits].some((value) => value !== null);
      if (!stated) continue;
      if (!label || !item.id || [item.existingPairs, item.neededPairs].some((value) => value !== null && (!Number.isInteger(value) || value < 0))
        || [item.widthUnits, item.lengthUnits].some((value) => value !== null && (!Number.isFinite(value) || value <= 0))) return false;
      normalized.push({ ...item, id: item.id || `${key}:${index + 1}` });
    }
    const entry = state.storageNeeds[key] ?? emptyNeed();
    entry.shoeGroups = normalized;
    recalcShoeTotals(entry);
    state.storageNeeds[key] = entry;
    if (state.storageNeedsSource?.kind === 'client') {
      state.storageNeedsSource = { ...state.storageNeedsSource, designerEditedAt: new Date().toISOString() };
    }
    return true;
  }

  function addShoeInventoryGroup(key, slot = 'space') {
    const type = storageNeedTypes().find((item) => item.key === key && item.kind === 'shoes');
    if (!type) return null;
    const entry = state.storageNeeds[key] ?? emptyNeed();
    const id = makeId();
    entry.shoeGroups = [...(entry.shoeGroups ?? []), {
      id, label: '', slot: ['a', 'b'].includes(slot) ? slot : 'space', existingPairs: null, neededPairs: null, widthUnits: null, lengthUnits: null,
    }];
    state.storageNeeds[key] = entry;
    if (state.storageNeedsSource?.kind === 'client') {
      state.storageNeedsSource = { ...state.storageNeedsSource, designerEditedAt: new Date().toISOString() };
    }
    return id;
  }

  function updateShoeInventoryGroup(key, id, field, rawValue) {
    const entry = state.storageNeeds[key];
    const group = entry?.shoeGroups?.find((item) => item.id === id);
    if (!group || !['label', 'slot', 'existingPairs', 'neededPairs', 'widthUnits', 'lengthUnits'].includes(field)) return false;
    if (field === 'label') group.label = String(rawValue ?? '').trim().slice(0, 80);
    else if (field === 'slot') group.slot = ['a', 'b'].includes(rawValue) ? rawValue : 'space';
    else {
      const text = String(rawValue ?? '').trim();
      const value = text === '' ? null : field.endsWith('Pairs') ? Number(text) : parseLength(text);
      if (value !== null && (!Number.isFinite(value) || value < 0 || (field.endsWith('Pairs') && !Number.isInteger(value))
        || (!field.endsWith('Pairs') && value <= 0))) return false;
      group[field] = value;
    }
    recalcShoeTotals(entry);
    if (state.storageNeedsSource?.kind === 'client') {
      state.storageNeedsSource = { ...state.storageNeedsSource, designerEditedAt: new Date().toISOString() };
    }
    return true;
  }

  function removeShoeInventoryGroup(key, id) {
    const entry = state.storageNeeds[key];
    if (!entry?.shoeGroups?.some((group) => group.id === id)) return false;
    entry.shoeGroups = entry.shoeGroups.filter((group) => group.id !== id);
    recalcShoeTotals(entry);
    if (state.storageNeedsSource?.kind === 'client') {
      state.storageNeedsSource = { ...state.storageNeedsSource, designerEditedAt: new Date().toISOString() };
    }
    return true;
  }

  /**
   * Stamps the client response that supplied Storage needs. Opportunity-level
   * imports also retain the selected client space names, mappings, and notes so
   * an unusual item remains visible after the response link is closed.
   */
  function markStorageNeedsClientProvided({ requestId, sourceSketchId, submittedAt, opportunity = null, importedSpaces = [] } = {}) {
    if (![requestId, sourceSketchId, submittedAt].every((value) => typeof value === 'string' && value.trim())) return false;
    state.storageNeedsSource = {
      kind: 'client', requestId: requestId.trim(), sourceSketchId: sourceSketchId.trim(),
      submittedAt, importedAt: new Date().toISOString(), designerEditedAt: null,
      opportunity: opportunity && typeof opportunity.client === 'string' && typeof opportunity.opportunity === 'string'
        ? { client: opportunity.client.trim(), opportunity: opportunity.opportunity.trim() } : null,
      importedSpaces: (Array.isArray(importedSpaces) ? importedSpaces : []).slice(0, 30).map((space) => ({
        id: String(space?.id ?? '').slice(0, 80),
        name: String(space?.name ?? '').trim().slice(0, 120),
        mapping: ['space', 'a', 'b', 'ab', 'ba'].includes(space?.mapping) ? space.mapping : 'space',
        notes: String(space?.notes ?? '').trim().slice(0, 2000),
      })).filter((space) => space.id && space.name),
    };
    return true;
  }

  /** One row's raw requirement entry (never null — an unset row reads as empty). */
  function storageNeedFor(key) {
    const entry = state.storageNeeds[key];
    if (!entry) return emptyNeed();
    return {
      existing: { ...emptyNeed().existing, ...entry.existing },
      needed: { ...emptyNeed().needed, ...entry.needed },
      shoeGroups: Array.isArray(entry.shoeGroups) ? entry.shoeGroups.map((group) => ({ ...group })) : [],
    };
  }

  /**
   * A row's RESOLVED requirement: what the design is measured against.
   *
   * `needed` wins where it is stated, else `existing` stands in (owner: the
   * default is that they need what they already have). In a shared space the
   * per-person figures are authoritative and the total is their sum; a figure
   * entered before the space was shared still counts as the total, so turning
   * sharing on does not silently drop it.
   *
   * @returns {{space:number|null, a:number|null, b:number|null, total:number|null}}
   */
  function resolvedStorageNeed(key, mode = state.storageCompareTo) {
    const { existing, needed } = storageNeedFor(key);
    // CURRENT reads `existing` alone — with no fallback, because what a client
    // has today is either stated or it isn't. NEED keeps the documented
    // fallback, where a blank `needed` means "the same as they have now".
    const pick = mode === 'current'
      ? (slot) => existing[slot]
      : (slot) => (needed[slot] === null ? existing[slot] : needed[slot]);
    const space = pick('space');
    const a = pick('a');
    const b = pick('b');
    if (!state.sharedSpace.enabled) return { space, a, b, total: space };
    const total = a === null && b === null ? space : (a ?? 0) + (b ?? 0);
    return { space, a, b, total };
  }

  /**
   * Switches the summary between measuring the design against the client's
   * CURRENT storage and their NEED. Rejects anything else rather than falling
   * back to a default, so a typo can't silently change what the box means.
   */
  function setStorageCompareTo(mode) {
    if (mode !== 'current' && mode !== 'need') return false;
    state.storageCompareTo = mode;
    return true;
  }

  // ---- space pricing choices (ADR 0058, plan §5/4e) -------------------------

  /**
   * The material LEVEL this space prices at (default 1). Stored rules-blind
   * (any whole level ≥ 1) — the UI offers only the levels the multiplier rule
   * defines, and an unknown level prices at factor 1 rather than crashing,
   * the same tolerance every other rule read has.
   */
  function setPricingLevel(level) {
    const n = Number(level);
    if (!Number.isInteger(n) || n < 1) return false;
    state.pricing = { ...state.pricing, level: n };
    return true;
  }

  /** The space's countertop MATERIAL pick (a materialCode from the countertop
   *  rule), or null = none — with none picked, countertop lines show a visible
   *  "pick a material" prompt rather than a silent zero. */
  function setPricingCountertopMaterial(code) {
    const cleaned = typeof code === 'string' && code.trim() ? code.trim() : null;
    state.pricing = { ...state.pricing, countertopMaterial: cleaned };
    return true;
  }

  /**
   * Stamps the last computed space totals onto the doc (ADR 0058): the
   * pricing panel derives the full breakdown fresh every render; this small
   * summary is what serialize persists so the PREFERRED version of a space
   * carries its priced range (ADR 0021 — the ballpark hook; the cross-repo
   * transport itself is a follow-up ADR).
   */
  function setPricingTotals({ low = null, high = null, needsPricing = 0 } = {}) {
    state.pricing = {
      ...state.pricing,
      low: Number.isFinite(low) ? low : null,
      high: Number.isFinite(high) ? high : null,
      needsPricing: Number.isInteger(needsPricing) && needsPricing >= 0 ? needsPricing : 0,
    };
    return true;
  }

  // ---- Basic mode: bucket cards (ADR 0059) ----------------------------------

  /** Basic ↔ Detailed is a view toggle only — no data moves either way. */
  function setBasicMode(mode) {
    if (mode !== 'basic' && mode !== 'detailed') return false;
    if (state.basic.mode === mode) return false;
    state.basic = { ...state.basic, mode };
    return true;
  }

  /** The space's TYPE (closet/office/…), validated against the rule — it
   *  filters the bucket palette via the availability matrix. */
  function setBasicSpaceType(key) {
    const valid = (rules.basicSpaceTypes ?? []).some((t) => t?.key === key);
    if (!valid) return false;
    state.basic = { ...state.basic, spaceType: key };
    return true;
  }

  /** The resolved span bounds (the sketch's own, else the rule default). */
  function basicBounds() {
    const b = state.basic.bounds;
    const d = rules.basicDefaultBounds ?? {};
    const n = (v, fb) => (Number.isFinite(v) && v > 0 ? v : fb);
    return {
      lowHeightIn: n(b?.lowHeightIn, n(d.lowHeightIn, 84)),
      lowDepthIn: n(b?.lowDepthIn, n(d.lowDepthIn, 12)),
      highHeightIn: n(b?.highHeightIn, n(d.highHeightIn, 95)),
      highDepthIn: n(b?.highDepthIn, n(d.highDepthIn, 16)),
    };
  }

  /**
   * Edits ONE bound (lowHeightIn / lowDepthIn / highHeightIn / highDepthIn),
   * parsing the same length grammar every other field takes (30 1/2, 8'6").
   * Rejects a value that would invert its axis (low above high) — the bounds
   * always describe a real range.
   */
  function setBasicBound(field, raw) {
    const fields = ['lowHeightIn', 'lowDepthIn', 'highHeightIn', 'highDepthIn'];
    if (!fields.includes(field)) return false;
    const parsed = parseLength(String(raw ?? '').trim());
    if (!(parsed > 0)) return false;
    const next = { ...basicBounds(), [field]: parsed };
    if (next.lowHeightIn > next.highHeightIn || next.lowDepthIn > next.highDepthIn) return false;
    state.basic = { ...state.basic, bounds: next };
    return true;
  }

  /**
   * Adds `qty` units of a bucket to the tray, merging into the existing
   * UNASSIGNED card of that bucket. Returns the card's id, or null.
   *
   * ⚠ The merge key is (bucket, unassigned, DRAWER COUNT). The drawer adder is
   * a PER-UNIT figure, so folding a 0-drawer card into a 4-drawer one would
   * silently reprice every unit on both sides. Cards that differ in drawers
   * stay separate rows — which is also how a designer says "three plain
   * shelving units and two with four drawers each".
   */
  function addBucketCard(bucketKey, qty = 1, drawers = 0) {
    const key = typeof bucketKey === 'string' ? bucketKey : '';
    if (!key || !(rules.basicBuckets ?? []).some((b) => b?.key === key)) return null;
    const n = Number.isInteger(qty) && qty > 0 ? qty : 1;
    const d = Number.isInteger(drawers) && drawers > 0 ? drawers : 0;
    const cards = state.basic.cards.map((c) => ({ ...c }));
    const existing = cards.find((c) => c.bucketKey === key && !c.wallId && cardDrawers(c) === d);
    if (existing) {
      existing.qty += n;
      state.basic = { ...state.basic, cards };
      return existing.id;
    }
    const card = { id: makeId(), bucketKey: key, qty: n, drawers: d, wallId: null };
    state.basic = { ...state.basic, cards: [...cards, card] };
    return card.id;
  }

  /** A card's per-unit drawer count, tolerant of cards stored before the
   *  adder existed (they simply have none). */
  function cardDrawers(card) {
    return Number.isInteger(card?.drawers) && card.drawers > 0 ? card.drawers : 0;
  }

  /**
   * Sets a card's DRAWER ADDER count — drawers per UNIT, not per card (owner:
   * "4 drawers added to the shelving unit"), so the billed count is this × qty.
   * 0 clears the adder. Changing it can collide with a sibling card of the same
   * bucket/wall that already carries that count, so the two MERGE — the same
   * rule addBucketCard and assignBucketCard use, applied to the third field the
   * merge key is made of.
   */
  function setBucketCardDrawers(id, drawers) {
    const n = Number(drawers);
    if (!Number.isInteger(n) || n < 0) return false;
    const card = state.basic.cards.find((c) => c.id === id);
    if (!card || cardDrawers(card) === n) return false;
    let cards = state.basic.cards.map((c) => (c.id === id ? { ...c, drawers: n } : { ...c }));
    const twin = cards.find((c) => c.id !== id && c.bucketKey === card.bucketKey
      && (c.wallId ?? null) === (card.wallId ?? null) && cardDrawers(c) === n);
    if (twin) {
      twin.qty += card.qty;
      cards = cards.filter((c) => c.id !== id);
    }
    state.basic = { ...state.basic, cards };
    return true;
  }

  /** Sets a card's quantity; 0 removes the card. */
  function setBucketCardQty(id, qty) {
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 0) return false;
    const card = state.basic.cards.find((c) => c.id === id);
    if (!card) return false;
    const cards = n === 0
      ? state.basic.cards.filter((c) => c.id !== id)
      : state.basic.cards.map((c) => (c.id === id ? { ...c, qty: n } : c));
    state.basic = { ...state.basic, cards };
    return true;
  }

  /**
   * ASSIGNS a card to a wall (or back to unassigned with null) — spatial
   * context only: per-wall counts and the elevation glyph, never a change to
   * the card's span (assignment is not placement). Cards of the same bucket
   * on the same wall merge.
   */
  function assignBucketCard(id, wallId) {
    const card = state.basic.cards.find((c) => c.id === id);
    if (!card) return false;
    const host = wallId == null ? null : resolveHost(wallId);
    if (wallId != null && !host) return false;
    const target = wallId ?? null;
    if ((card.wallId ?? null) === target) return false;
    let cards = state.basic.cards.map((c) => (c.id === id ? { ...c, wallId: target } : { ...c }));
    // Same merge key as addBucketCard — drawers included, so assigning a
    // 4-drawer card to a wall never folds it into the plain card sitting there.
    const twin = cards.find((c) => c.id !== id && c.bucketKey === card.bucketKey
      && (c.wallId ?? null) === target && cardDrawers(c) === cardDrawers(card));
    if (twin) {
      twin.qty += card.qty;
      cards = cards.filter((c) => c.id !== id);
    }
    state.basic = { ...state.basic, cards };
    return true;
  }

  /**
   * CONVERSION (ADR 0059): places one unit of a card as a REAL section on a
   * wall — addSection stamps the bucket's representative type's preset, the
   * section carries `fromBucket` as light provenance, and the card loses one
   * unit (the range can only narrow). Returns the new section id, or null
   * when the card is empty, the bucket has no placeable sketch type, or the
   * host is invalid.
   *
   * ⚠ The card's DRAWER ADDER does not follow the unit. The adder is a Basic
   * ballpark device — a flat per-drawer band that exists so Basic never has to
   * ask the catalog for a DRW4-vs-DRW5 row. Once a unit is placed it is a real
   * section priced from its real interior, and stamping drawers into that
   * interior would be exactly the detailed section-modelling this tool is
   * meant to avoid. The remaining units on the card keep their adder; the card
   * row says on screen that a placed unit's drawers get drawn, not added flat.
   */
  function placeBucketUnit(id, wallId) {
    const card = state.basic.cards.find((c) => c.id === id);
    if (!card || !(card.qty > 0)) return null;
    const bucket = (rules.basicBuckets ?? []).find((b) => b?.key === card.bucketKey);
    const type = typeof bucket?.representativeType === 'string' ? bucket.representativeType : null;
    if (!type) return null;
    const hasPreset = (rules.sectionTypePresets ?? [])
      .some((p) => String(p.code).toUpperCase() === type.toUpperCase());
    if (!hasPreset) return null;
    const sectionId = addSection(wallId, type);
    if (!sectionId) return null;
    const section = state.sections.find((s) => s.id === sectionId);
    section.fromBucket = card.bucketKey;
    setBucketCardQty(id, card.qty - 1);
    return sectionId;
  }

  /**
   * UNPLACE (ADR 0059's chosen semantics): removing a section that carries
   * `fromBucket` provenance returns one unit to the matching bucket card —
   * preferring a card of that bucket assigned to the section's wall, else
   * the unassigned card, else a fresh unassigned card. Runs inside
   * removeSection so EVERY removal path (Basic unplace button, Detailed
   * delete, a doomed stack) is one mechanism; deleting the returned unit
   * from the tray afterwards is the designer's explicit second step.
   *
   * ⚠ The unit comes back with NO drawer adder, and the card it lands on must
   * be a 0-drawer one. A placed section is priced from its real interior, so
   * the section being unplaced never carried an adder; dropping it onto a
   * 4-drawer card would invent $900–$1,300 of drawers that were never drawn.
   */
  function returnUnitToBucket(section) {
    const key = section?.fromBucket;
    if (typeof key !== 'string' || !key) return;
    const cards = state.basic.cards.map((c) => ({ ...c }));
    const plain = (c) => c.bucketKey === key && cardDrawers(c) === 0;
    const card = cards.find((c) => plain(c) && c.wallId === section.wallId)
      ?? cards.find((c) => plain(c) && !c.wallId);
    if (card) {
      card.qty += 1;
      state.basic = { ...state.basic, cards };
    } else {
      state.basic = {
        ...state.basic,
        cards: [...cards, { id: makeId(), bucketKey: key, qty: 1, drawers: 0, wallId: null }],
      };
    }
  }

  function storageSummary() {
    const presets = new Map((rules.sectionTypePresets ?? []).map((p) => [String(p.code).toUpperCase(), p]));
    const pairWidth = rules.shoePairWidthUnits > 0 ? rules.shoePairWidthUnits : 8;
    const hosts = [...new Set(state.sections.map((s) => s.wallId))];
    const acc = new Map(); // `${kind}:${code}` -> row
    const add = (kind, code, name, unit, value, person) => {
      const key = `${kind}:${code}`;
      const row = acc.get(key) ?? { code, name, kind, unit, total: 0, byPerson: [0, 0] };
      row.total += value;
      if (person === 0 || person === 1) row.byPerson[person] += value;
      acc.set(key, row);
    };
    for (const wallId of hosts) {
      const layout = sectionLayoutForWall(wallId);
      if (!layout) continue;
      const secs = sectionsForWall(wallId);
      layout.items.forEach((item, i) => {
        // A run section AND everything stacked on it all hold storage, and a
        // stacked unit spans the same opening width as the run section below it
        // (ADR 0041) — so each counts against the same `item`.
        for (const sec of [secs[i], ...stackChainFor(secs[i].id)]) {
          const preset = presets.get(String(sec.typeCode).toUpperCase());
          const person = sec.assignedTo === 0 || sec.assignedTo === 1 ? sec.assignedTo : null;
          // The section's EFFECTIVE composition is the source of truth (ADR
          // 0057, plan §5/4d) — a legacy section derives its preset's
          // composition, so old sketches report exactly as before, while a
          // composed one reports what is actually inside it.
          const interior = sectionInterior(sec) ?? [];
          // Hang: opening × rods, exactly the old preset.hangRods basis, but
          // counting the ROD ELEMENTS — bucketed per rod CATEGORY (ADR 0051
          // step 2): a DH's two short rods land as 2× width of short hang.
          const rodsByCategory = new Map();
          for (const el of interior) {
            if (el.kind !== 'rod') continue;
            const key = rodCategoryKey(el, preset);
            rodsByCategory.set(key, (rodsByCategory.get(key) ?? 0) + 1);
          }
          for (const [key, rods] of rodsByCategory) {
            add('hang', key, hangCategoryLabel(key), 'in', item.openingUnits * rods, person);
          }
          // Shoes: pairs per SHOE shelf × whole pairs across the opening. Only
          // shoeShelves elements count — a plain shelf added to an SS section
          // is a shelf, not seven inches of footwear (the old mirror conflated
          // them). Keyed to the ONE footwear row, matching storageNeedTypes():
          // a pair is a pair whether an SS or an ESS84 holds it, and the needs
          // side has a single row to compare against.
          const shoeShelves = interior.reduce((n, el) => n + (el.kind === 'shoeShelves' ? el.count : 0), 0);
          if (shoeShelves > 0) {
            // The SECTION's own pair width wins (her 8" vs his 9½"), falling
            // back to the global rule.
            const pw = sec.pairWidthUnits > 0 ? sec.pairWidthUnits : pairWidth;
            add('shoes', 'ALL', 'Footwear shelving', 'pairs', shoeShelves * Math.floor(item.openingUnits / pw), person);
          }
          // Drawers: the faces actually composed, across every bank — a
          // custom 6-drawer bank counts 6, whatever the preset default says.
          const drawers = interior.reduce((n, el) => n + (el.kind === 'drawerBank' ? el.drawers.length : 0), 0);
          if (drawers > 0) add('drawers', 'DRW', 'Drawers', 'drawers', drawers, person);
        }
      });
    }
    const order = { hang: 0, shoes: 1, drawers: 2 };
    // Every row the CLIENT stated a requirement for must appear even when the
    // design provides none of it — a missing row would read as "nothing needed"
    // when it actually means a 100% shortfall (ADR 0043).
    for (const type of storageNeedTypes()) {
      if (acc.has(type.key)) continue;
      if (!(resolvedStorageNeed(type.key).total > 0)) continue;
      acc.set(type.key, { code: type.code, name: type.name, kind: type.kind, unit: type.unit, total: 0, byPerson: [0, 0] });
    }
    const rows = [...acc.values()]
      .map((row) => {
        const need = resolvedStorageNeed(`${row.kind}:${row.code}`);
        // Difference is HAVE − NEED: negative is a shortfall, positive is spare
        // capacity, null where the client stated no requirement (so the UI can
        // show nothing rather than a meaningless surplus).
        const diff = (have, want) => (want === null ? null : have - want);
        return {
          ...row,
          need,
          diff: {
            total: diff(row.total, need.total),
            a: diff(row.byPerson[0], need.a),
            b: diff(row.byPerson[1], need.b),
          },
        };
      })
      .filter((r) => r.total > 0 || r.need.total > 0)
      .sort((a, b) => order[a.kind] - order[b.kind]);
    return {
      shared: state.sharedSpace.enabled === true,
      persons: [...state.sharedSpace.persons],
      // Which requirement the deltas are against, so the box can say so.
      compareTo: state.storageCompareTo === 'current' ? 'current' : 'need',
      // True when anything has a stated requirement — lets the summary box stay
      // as it was for a designer who never uses this.
      hasNeeds: rows.some((r) => r.need.total !== null),
      rows,
    };
  }

  /**
   * A wall's RUN sections in layout order (left to right along the wall).
   *
   * Stacked units (ADR 0041) are deliberately EXCLUDED: they rest on a run
   * section rather than occupying their own horizontal slot, and the layout
   * solver's `items[i]` must stay 1:1 with this list. Callers that need the
   * vertical dimension use stackChainFor()/stacksForWall().
   */
  function sectionsForWall(wallId) {
    return state.sections
      .filter((s) => s.wallId === wallId && !s.stackOnId)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Every wall section paired with the units stacked on it and their SOLVED
   * vertical placement (ADR 0041) — the single source both drawings use.
   *
   * Each entry is `{ section, item, units }` where `units[k]` is
   * `{ section, anchor, bottomUnits, topUnits, heightUnits, hasLegs, overflow }`,
   * index 0 being the run section itself. A unit's height INCLUDES its capshelf
   * (a ¾" shelf built onto an upper's bottom, so a 36" upper is 36¾" overall);
   * the only join gap is a base's countertop. "To the ceiling" autosizes the
   * TOP unit.
   */
  function stacksForWall(wallId) {
    const layout = sectionLayoutForWall(wallId);
    if (!layout) return [];
    const runSections = sectionsForWall(wallId);
    const platformUnits = state.platformHeightUnits > 0 ? state.platformHeightUnits : 0;
    const totalTopUnits = stackTopUnits(wallId);
    const soffits = soffitSpansForWall(wallId);
    return runSections.map((section, i) => {
      const chain = stackChainFor(section.id);
      const all = [section, ...chain];
      const inputs = all.map((s, k) => {
        const below = k > 0 ? all[k - 1] : null;
        return {
          anchor: sectionAnchor(s),
          // The capshelf is part of the unit, not a gap under it.
          heightUnits: sectionHeightUnits(s) + capshelfUnitsFor(s),
          gapBelowUnits: below
            ? stackGapUnits({ anchor: sectionAnchor(below), countertopUnits: countertopThicknessFor(below) })
            : 0,
          // A "to height" pinned on this unit (owner, 2026-08-04) — what makes
          // a MIDDLE unit's top settable in a three-tier stack.
          targetTopUnits: s.targetTopUnits > 0 ? s.targetTopUnits : 0,
        };
      });
      // A SOFFIT over this section replaces the datum at the top of the height
      // chain (ADR 0053): its underside is the ceiling for anything beneath it.
      // It caps whether or not "to target height" is on — a soffit is a
      // physical obstruction, not a design preference, and cabinetry that runs
      // through one cannot be built.
      const item = layout.items[i] ?? null;
      const soffitBottom = item
        ? soffitCapOver({ startUnits: item.startUnits, endUnits: item.startUnits + item.openingUnits }, soffits)
        : null;
      const cappedTop = soffitBottom === null
        ? totalTopUnits
        : Math.max(0, soffitBottom - crownAllowanceUnits());
      const placed = placeVerticalStack(inputs, {
        platformUnits,
        tallTopUnits: rules.sectionDefaultHeightUnits,
        // Only a real stack autosizes to the ceiling — a lone run section keeps
        // its own height.
        totalTopUnits: chain.length > 0 ? cappedTop : 0,
      });
      // Horizontal span per unit: the run section fills its opening; each
      // stacked unit is free over a base (own width + designer offset) and
      // inherits above anything else (ADR 0041).
      const runWidth = layout.items[i]?.openingUnits ?? 0;
      const spans = [];
      all.forEach((s, k) => {
        spans.push(k === 0
          ? { widthUnits: runWidth, offsetUnits: 0 }
          : stackedSpan(s, all[k - 1], runWidth, spans[k - 1]));
      });
      return {
        section,
        item,
        // The underside of the soffit over this section, or null. The panel
        // shows it so a shortened run reads as a consequence, not a glitch.
        soffitBottomUnits: soffitBottom,
        units: placed.map((p, k) => ({
          ...p,
          section: all[k],
          // Structural class survives placement so renderers and pricing can
          // enforce per-unit trim eligibility without re-reading the catalog.
          anchor: inputs[k].anchor,
          // Depth follows the stacking rule: free over a base, inherited above
          // anything else (ADR 0041).
          depthUnits: k === 0
            ? (all[0].depthUnits > 0 ? all[0].depthUnits : rules.sectionDefaultDepthUnits)
            : stackedDepthUnits(all[k], all[k - 1]),
          widthUnits: spans[k].widthUnits,
          offsetUnits: spans[k].offsetUnits,
          shelves: unitShelves(p, {
            isStackBase: k === 0,
            platformUnits,
            capshelfUnits: capshelfUnitsFor(all[k]),
            anchor: sectionAnchor(all[k]),
            includeBottom: all[k].bottomShelf !== false,
          }),
        })),
      };
    });
  }

  /**
   * The ¾" top and bottom shelves every unit carries (owner, 2026-07-28).
   *
   * The top shelf is always the topmost ¾" of the unit. The bottom shelf sits
   * at the bottom of the panel — EXCEPT for a unit standing on the FLOOR, where
   * it is raised to leave a clear gap for base trim to pass behind it
   * (`floor_shelf_gap_units`, 2½" — "3 inches off the floor" nominally, which is
   * where the 3" helper line sits). On a platform the bottom shelf sits
   * directly on the platform, at the bottom of the panel.
   *
   * @param {{bottomUnits:number, topUnits:number}} placed
   * @param {boolean} isStackBase only the unit standing on the floor/platform
   * @returns {{bottom:{fromUnits:number,toUnits:number}, top:{fromUnits:number,toUnits:number}}|null}
   */
  function unitShelves(placed, opts = {}) {
    const t = rules.sectionShelfThicknessUnits > 0 ? rules.sectionShelfThicknessUnits : 0.75;
    const height = placed.topUnits - placed.bottomUnits;
    if (!(height > 0)) return null;
    // A CAPSHELF is built onto the very bottom of an upper to hide construction
    // seams (owner, 2026-07-28). It is IN ADDITION to the unit's own bottom
    // shelf, which therefore sits directly above it.
    const capT = opts.capshelfUnits > 0 ? opts.capshelfUnits : 0;
    const cap = capT > 0 ? { fromUnits: placed.bottomUnits, toUnits: placed.bottomUnits + capT } : null;
    // Only a unit actually STANDING on the floor leaves the base-trim gap — a
    // wall-hung upper never does, even when it is the only unit on its run.
    const standsOnFloor = opts.isStackBase && opts.anchor !== 'top';
    const gap = standsOnFloor && !(opts.platformUnits > 0)
      ? (rules.floorShelfGapUnits > 0 ? rules.floorShelfGapUnits : 0)
      : 0;
    const bottomFrom = placed.bottomUnits + capT + gap;
    // A unit too short to hold both shelves keeps the top one only.
    const bottom = opts.includeBottom !== false && bottomFrom + t <= placed.topUnits - t
      ? { fromUnits: bottomFrom, toUnits: bottomFrom + t }
      : null;
    return { cap, bottom, top: { fromUnits: placed.topUnits - t, toUnits: placed.topUnits } };
  }

  /**
   * True for an L-SHELF section — an L-shaped corner unit (owner, 2026-07-28).
   * Driven by the type definition's `cornerLShelf` flag, set in Admin.
   */
  function isLShelf(section) {
    if (!section) return false;
    const preset = (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(section.typeCode).toUpperCase());
    return preset?.cornerLShelf === true;
  }

  // ---- L-shelves (ADR 0042) -------------------------------------------------
  //
  // An L-shelf is ONE section that occupies a corner shared by TWO walls. It
  // lives in its host wall's run (so it lays out, panels and stacks like any
  // other section) and reserves a leg's worth of the SECOND wall's run at the
  // shared corner, so nothing else is placed into the same space.
  //
  // The designer picks the item and then the two walls (owner, 2026-07-28) —
  // explicit beats inferred, and it handles corners a chain walk cannot.

  /** The longest a single L-shelf leg may be (owner: 36"). */
  const L_SHELF_MAX_LEG_UNITS = 36;

  /**
   * Where two walls meet, or null when they do not form a usable corner.
   * Requires a shared vertex AND a real turn — the same physical test the hang
   * clearance uses (ADR 0038), so a straight-through seam or the open side of
   * an unclosed chain can never be mistaken for a corner.
   *
   * @returns {{aEnd:'start'|'end', bEnd:'start'|'end'}|null}
   */
  function sharedCorner(wallAId, wallBId) {
    const a = state.walls.find((w) => w.id === wallAId);
    const b = state.walls.find((w) => w.id === wallBId);
    if (!a || !b || a === b) return null;
    if ((a.segmentKind ?? 'wall') !== 'wall' || (b.segmentKind ?? 'wall') !== 'wall') return null;
    const unit = (w) => {
      const dx = w.end.x - w.start.x;
      const dy = w.end.y - w.start.y;
      const len = Math.hypot(dx, dy) || 1;
      return { x: dx / len, y: dy / len };
    };
    const ua = unit(a);
    const ub = unit(b);
    if (Math.abs(ua.x * ub.x + ua.y * ub.y) > 0.75) return null; // no real turn
    for (const aEnd of ['start', 'end']) {
      for (const bEnd of ['start', 'end']) {
        const pa = aEnd === 'start' ? a.start : a.end;
        const pb = bEnd === 'start' ? b.start : b.end;
        if (Math.hypot(pa.x - pb.x, pa.y - pb.y) <= 0.5) return { aEnd, bEnd };
      }
    }
    return null;
  }

  /**
   * Attaches an L-shelf to its second wall (the first is the run it lives in).
   * Rejected when the two walls do not share a real corner, so an L-shelf can
   * never be anchored to geometry that isn't a corner.
   */
  function setLShelfSecondWall(id, secondWallId) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || !isLShelf(section)) return false;
    if (!secondWallId) { section.secondWallId = null; return true; }
    if (!sharedCorner(section.wallId, secondWallId)) return false;
    section.secondWallId = secondWallId;
    return true;
  }

  /**
   * Sets one of an L-shelf's leg dimensions. `which` is 'host' (the leg lying
   * in this section's own run — its width) or 'second' (the leg reaching along
   * the attached wall). Both legs are independent in LENGTH and DEPTH (owner),
   * and each length is capped at L_SHELF_MAX_LEG_UNITS.
   */
  function setLShelfLeg(id, which, field, rawValue) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || !isLShelf(section)) return false;
    const text = String(rawValue ?? '').trim();
    const parsed = text === '' ? null : parseLength(text);
    if (text !== '' && (parsed === null || parsed <= 0)) return false;
    if (field === 'length' && parsed !== null && parsed > L_SHELF_MAX_LEG_UNITS) return false;
    if (which === 'host') {
      if (field === 'length') return setSectionWidth(id, text);
      section.depthUnits = parsed ?? rules.sectionDefaultDepthUnits;
      return true;
    }
    if (field === 'length') section.legTwoUnits = parsed;
    else section.legTwoDepthUnits = parsed;
    return true;
  }

  /** An L-shelf's two legs, resolved: each { wallId, lengthUnits, depthUnits }. */
  function lShelfLegs(section) {
    if (!isLShelf(section)) return null;
    const hostLen = section.widthUnits > 0 ? section.widthUnits : null;
    return {
      host: {
        wallId: section.wallId,
        lengthUnits: hostLen,
        depthUnits: section.depthUnits > 0 ? section.depthUnits : rules.sectionDefaultDepthUnits,
      },
      second: {
        wallId: section.secondWallId ?? null,
        lengthUnits: section.legTwoUnits > 0 ? section.legTwoUnits : null,
        depthUnits: section.legTwoDepthUnits > 0 ? section.legTwoDepthUnits : rules.sectionDefaultDepthUnits,
      },
    };
  }

  /**
   * The L-shelf legs that lie along `wallId` because the shelf is ATTACHED to
   * it (its host run is the other wall) — in wall-local units, ready to draw.
   * Mirrors connectingShelvesForWall: `fromUnits`/`toUnits` along the wall,
   * `depthUnits` into the room.
   *
   * `place` carries the leg's VERTICAL extent, so the attached wall's elevation
   * draws the same board the plan does. It is the shelf's own placement in its
   * HOST run (ADR 0040 platform anchor + ADR 0041 shelves), never re-derived
   * here: one leg of one physical shelf cannot sit at two heights. It is null
   * only when the host run can't be solved (e.g. the host wall lost its length).
   */
  function lShelfSecondLegsForWall(wallId) {
    const wall = resolveHost(wallId);
    if (!wall) return [];
    const L = wallLengthUnits(wall);
    const out = [];
    for (const s of state.sections) {
      if (!isLShelf(s) || s.secondWallId !== wallId) continue;
      const leg = s.legTwoUnits > 0 ? s.legTwoUnits : 0;
      if (!(leg > 0)) continue;
      const corner = sharedCorner(s.wallId, wallId);
      if (!corner) continue;
      const depth = s.legTwoDepthUnits > 0 ? s.legTwoDepthUnits : rules.sectionDefaultDepthUnits;
      const place = lShelfPlacement(s);
      const span = corner.bEnd === 'start'
        ? { fromUnits: 0, toUnits: Math.min(leg, L), cornerAt: 'from' }
        : { fromUnits: Math.max(0, L - leg), toUnits: L, cornerAt: 'to' };
      // The leg's ONLY panel is at its far end (owner, 2026-08-28: an L-shelf
      // has exactly two end panels; the corner side is continuous, closed by
      // the ¾" backer that turns the corner). Precomputed here so both
      // renderers draw the identical board.
      const pt = rules.sectionPanelThicknessUnits > 0 ? rules.sectionPanelThicknessUnits : 0.75;
      const endPanel = span.cornerAt === 'from'
        ? { fromUnits: Math.max(span.fromUnits, span.toUnits - pt), toUnits: span.toUnits }
        : { fromUnits: span.fromUnits, toUnits: Math.min(span.toUnits, span.fromUnits + pt) };
      // The shelf boards WRAP the corner (owner, 2026-08-28): one physical
      // board per shelf, so the leg carries the HOST interior's solved boards
      // — read from the same solve the host elevation draws, never
      // re-derived, so the two walls can never disagree about a height.
      // Shelves only: a rod or a drawer bank does not turn a corner.
      const stack = stacksForWall(s.wallId).find((st) => st.section?.id === s.id);
      const unit = stack?.units?.[0];
      const feed = unit ? interiorForUnit(unit) : null;
      const shelfBoards = [];
      for (const el of feed?.solved?.elements ?? []) {
        if (el.kind === 'shelf') {
          shelfBoards.push({
            atUnits: el.toUnits,
            thicknessUnits: el.thicknessUnits > 0 ? el.thicknessUnits : el.toUnits - el.fromUnits,
          });
        } else if (el.kind === 'shelfGroup') {
          const t = el.thicknessUnits > 0 ? el.thicknessUnits : 0.75;
          for (const at of el.shelfLines ?? []) {
            if (Number.isFinite(at)) shelfBoards.push({ atUnits: at, thicknessUnits: t });
          }
        }
      }
      out.push({
        id: s.id,
        typeCode: s.typeCode,
        depthUnits: depth,
        place,
        endPanel,
        shelfBoards,
        hostDepthUnits: s.depthUnits > 0 ? s.depthUnits : rules.sectionDefaultDepthUnits,
        ...span,
      });
    }
    return out;
  }

  /**
   * What the PLAN needs to draw an L-shelf as ONE unit inside its host run
   * (owner, 2026-08-28: "could the L shelves render as a single section?"):
   * whether it has a backer (every L-shelf does — ¾", both wall lengths), and
   * where its attached leg turns off, so the host's front face stops at the
   * turn instead of running through the leg's interior. Null for anything
   * that is not an L-shelf.
   */
  function sectionLShelfInfo(section) {
    if (!isLShelf(section)) return null;
    let join = null;
    if (section.secondWallId && section.legTwoUnits > 0) {
      const corner = sharedCorner(section.wallId, section.secondWallId);
      if (corner) {
        join = {
          end: corner.aEnd,
          legDepthUnits: section.legTwoDepthUnits > 0 ? section.legTwoDepthUnits : rules.sectionDefaultDepthUnits,
        };
      }
    }
    return {
      backer: true,
      backerUnits: rules.sectionPanelThicknessUnits > 0 ? rules.sectionPanelThicknessUnits : 0.75,
      join,
    };
  }

  /**
   * Where an L-shelf sits vertically: the placement of the section itself in
   * its host wall's stack (the stack's bottom unit). Anything STACKED on an
   * L-shelf is drawn on the host elevation only — the second leg is one board,
   * not a run, so there is nothing on the attached wall for a stacked unit to
   * rest on (ADR 0042).
   *
   * @returns {{bottomUnits:number, topUnits:number, hasLegs:boolean, shelves:object|null}|null}
   */
  function lShelfPlacement(section) {
    const stack = stacksForWall(section.wallId).find((st) => st.section?.id === section.id);
    const unit = stack?.units?.[0];
    if (!unit) return null;
    return {
      bottomUnits: unit.bottomUnits,
      topUnits: unit.topUnits,
      hasLegs: unit.hasLegs === true,
      shelves: unit.shelves ?? null,
    };
  }

  /**
   * Nudges this section's ELEVATION caption vertically (owner, 2026-08-28).
   * Horizontal caption drag REORDERS (ADR 0037); the vertical component is
   * pure label placement — stored in room units (+ = down) so two colliding
   * captions can be pulled apart and stay apart across reloads. Zero/null
   * clears. A value beyond ±240 units is REJECTED rather than clamped —
   * nothing legitimate moves a caption two ceilings away, so a huge number
   * is bad data, not intent (the ADR 0042 leg-cap rule).
   */
  function setSectionElevCodeOffset(id, dyUnits) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    if (dyUnits === null || dyUnits === 0) { section.elevCodeDyUnits = null; return true; }
    if (!Number.isFinite(dyUnits) || Math.abs(dyUnits) > 240) return false;
    section.elevCodeDyUnits = dyUnits;
    return true;
  }

  /** The walls an L-shelf could attach to: those sharing a real corner. */
  function lShelfCandidateWalls(section) {
    if (!isLShelf(section)) return [];
    return state.walls
      .filter((w) => (w.segmentKind ?? 'wall') === 'wall' && w.id !== section.wallId && sharedCorner(section.wallId, w.id))
      .map((w) => ({ id: w.id, label: `W${w.wallNumber ?? '?'}` }));
  }

  /**
   * How much of `wallId`'s run each end must reserve for an L-shelf that lives
   * on a NEIGHBOURING wall and reaches into this one at the shared corner.
   * Derived, never stored — retype or delete the L-shelf and this wall re-flows.
   *
   * @returns {{startUnits:number, endUnits:number}}
   */
  function lShelfReservations(wallId) {
    let startUnits = 0;
    let endUnits = 0;
    for (const s of state.sections) {
      if (!isLShelf(s) || s.secondWallId !== wallId) continue;
      const leg = s.legTwoUnits > 0 ? s.legTwoUnits : 0;
      if (!(leg > 0)) continue;
      const corner = sharedCorner(s.wallId, wallId);
      if (!corner) continue; // geometry changed under it — reserve nothing
      if (corner.bEnd === 'start') startUnits = Math.max(startUnits, leg);
      else endUnits = Math.max(endUnits, leg);
    }
    return { startUnits, endUnits };
  }

  /** A section type's platform anchor (ADR 0040): floor | countertop | top. */
  function sectionAnchor(section) {
    const preset = (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(section.typeCode).toUpperCase());
    const anchor = preset?.anchor;
    return anchor === 'countertop' || anchor === 'top' ? anchor : 'floor';
  }

  /** A base's countertop thickness: its own override → the space setting → 0. */
  function countertopThicknessFor(section) {
    if (section.countertopThicknessUnits > 0) return section.countertopThicknessUnits;
    return state.countertopThicknessUnits > 0 ? state.countertopThicknessUnits : 0;
  }

  /** An upper's capshelf thickness, from its type definition (ADR 0041). */
  function capshelfUnitsFor(section) {
    if (sectionAnchor(section) !== 'top') return 0;
    const preset = (rules.sectionTypePresets ?? [])
      .find((p) => String(p.code).toUpperCase() === String(section.typeCode).toUpperCase());
    return preset?.capshelfUnits > 0 ? preset.capshelfUnits : 0;
  }

  /**
   * The height a stack tops out at in "to target height" mode: the target datum
   * less the crown-molding allowance. 0 when the mode is off, which leaves every
   * unit at its own height.
   *
   * Crown is still subtracted from the target the way it was from the ceiling —
   * the target is the REFERENCE the units run to (typically the shortest wall),
   * not the finished top of the cabinetry. The panel states the arithmetic
   * ("tops out at 107″ — target 110″ − crown 3″") so the two can't be confused.
   */
  function stackTopUnits(wallId = null) {
    if (state.toTargetHeight !== true) return 0;
    const target = stackTargetHeightUnits(wallId);
    const top = target - crownAllowanceUnits();
    return top > 0 ? top : 0;
  }

  /**
   * Hanging-clearance constraints on a wall's two ends (ADR 0038). A HANGING
   * section (one whose composed interior holds a rod — ADR 0057) needs clear
   * space in front of it for clothes; when one sits at the corner end of a
   * NEIGHBOUR wall's
   * run, this wall must keep everything (panels included) at least the
   * clearance rule away from that neighbour's face. Derived from the chain:
   * this wall's start corner touches the previous segment's end (so the
   * neighbour's LAST section is the corner one), its end corner touches the
   * next segment's start (the neighbour's FIRST section). A door/partition
   * footprint between them is an `opening` segment, which naturally breaks
   * the abutment. Returns { start, end }, each null or
   * { clearanceUnits, hangDepthUnits } (the depth sizes the connecting shelf).
   */
  /**
   * The neighbour wall's CORNER section that physically FACES this wall across
   * the shared corner at the given end ('start'|'end'), as
   * `{ wall, section, gapUnits }`, or null. `gapUnits` is how far that section
   * actually sits from the shared corner — being first/last in the run is not
   * the same as touching it. Encapsulates the shared-vertex + real-turn test
   * (ADR 0038): the chain array wraps
   * first↔last, but the wraparound pair is only a real corner in a CLOSED room,
   * and a hanging section's clothes only project across the run where the two
   * walls actually TURN — a straight-through seam faces nothing.
   */
  function cornerNeighbourInfo(wallId, end) {
    const at = state.walls.findIndex((w) => w.id === wallId);
    if (at < 0) return null; // partition faces have no chain corners
    const me = state.walls[at];
    const n = state.walls.length;
    const unit = (w) => {
      const dx = w.end.x - w.start.x;
      const dy = w.end.y - w.start.y;
      const len = Math.hypot(dx, dy) || 1;
      return { x: dx / len, y: dy / len };
    };
    const neighbour = end === 'start' ? state.walls[(at - 1 + n) % n] : state.walls[(at + 1) % n];
    if (!neighbour || neighbour === me || (neighbour.segmentKind ?? 'wall') !== 'wall') return null;
    const myCorner = end === 'start' ? me.start : me.end;
    const theirCorner = end === 'start' ? neighbour.end : neighbour.start;
    if (Math.hypot(myCorner.x - theirCorner.x, myCorner.y - theirCorner.y) > 0.5) return null; // no shared vertex
    if (Math.abs(unit(me).x * unit(neighbour).x + unit(me).y * unit(neighbour).y) > 0.75) return null; // no real turn
    const secs = sectionsForWall(neighbour.id);
    if (!secs.length) return null;
    // My start corner meets the neighbour's END (its LAST section); my end
    // corner meets the neighbour's START (its FIRST section).
    const atEnd = end === 'start';
    const section = atEnd ? secs[secs.length - 1] : secs[0];
    return { wall: neighbour, section, gapUnits: cornerGapUnits(neighbour, atEnd, section) };
  }

  /** Just the facing section — the shape most callers want. */
  function cornerNeighbourSection(wallId, end) {
    return cornerNeighbourInfo(wallId, end)?.section ?? null;
  }

  /**
   * How far a wall's corner-most SECTION actually sits from that corner.
   *
   * Being first or last in the run does NOT mean touching the corner: the
   * width solver caps auto sections (`section_max_opening_units`), so a wall
   * with one 36"-capped section and 100" of length leaves ~60" of bare wall at
   * the far end. That section is simultaneously first and last, and treating
   * it as "at the corner" is what reserved a phantom hang clearance on the
   * perpendicular wall (owner, 2026-08-04). Measured to the OPENING edge —
   * clothes hang in the opening, not on the panel.
   */
  function cornerGapUnits(wall, atEnd, section) {
    const layout = bareLayoutForWall(wall.id);
    const item = layout?.items?.find((it) => it.id === section.id);
    if (!item) return 0; // no layout to judge by: assume it reaches, as before
    return atEnd
      ? Math.max(0, layout.runUnits - (item.startUnits + item.openingUnits))
      : Math.max(0, item.startUnits);
  }

  function sectionEndConstraints(wallId) {
    if (state.walls.findIndex((w) => w.id === wallId) < 0) return { start: null, end: null };
    // Hanging derives from the section's COMPOSITION (ADR 0057, plan §5/4d):
    // clearance is needed iff the interior actually holds a rod. Legacy
    // sections derive their preset composition, so every hanging-flagged type
    // still hangs — but removing the rods in the editor releases the corner.
    const isHang = (sec) => !!sec && sectionHasHang(sec);
    // A hanging neighbour imposes clearance here — even when IT is diving into
    // the corner: the perpendicular wall keeps its clearance and its connecting
    // shelf, and that shelf is what bridges to the extended section (owner,
    // 2026-07-27: "only one connecting shelf is removed, the other remains").
    const clearance = rules.sectionHangClearanceUnits > 0 ? rules.sectionHangClearanceUnits : 0;

    /**
     * How deep MY run reaches into the corner at `end`. The neighbour's clothes
     * occupy a band running along the neighbour's wall; my sections only enter
     * it if they are DEEPER than the neighbour's hang sits far from the corner.
     * Only my sections inside the clearance band can be in the way at all —
     * anything further along the wall is already clear of it. Read from my
     * CLEARANCE-FREE layout, which is where those sections sit at their closest.
     */
    const reachAtCorner = (end) => {
      const layout = bareLayoutForWall(wallId);
      const depthOf = (s) => (s?.depthUnits > 0 ? s.depthUnits : rules.sectionDefaultDepthUnits);
      if (!layout?.items?.length) return rules.sectionDefaultDepthUnits > 0 ? rules.sectionDefaultDepthUnits : 0;
      const byId = new Map(sectionsForWall(wallId).map((s) => [s.id, s]));
      const inBand = layout.items.filter((it) => (end === 'start'
        ? it.startUnits < clearance
        : it.startUnits + it.openingUnits > layout.runUnits - clearance));
      // No section in the neighbour's projection band means there is nothing
      // at this corner to collide with or connect to. Falling back to the
      // nearest (but distant) section created phantom connecting shelves.
      if (!inBand.length) return 0;
      return Math.max(...inBand.map((it) => depthOf(byId.get(it.id))));
    };

    /**
     * A corner neighbour constrains this wall only when the two actually
     * COLLIDE (owner, 2026-08-04). One rule for every non-hanging section —
     * a base is not special; it simply reaches further into the corner than a
     * 12" shelf does, because it is deeper.
     *
     * BOTH kinds of neighbour constrain (ADR 0069). Until then this returned
     * null for anything that did not hang, which reserved NOTHING at a corner
     * between two non-hanging units — so this wall's run drove straight into
     * the neighbour's carcass and the two overlapped in plan (owner,
     * 2026-08-09: "getting section overlap in corner when neither is hanging").
     *
     * The two cases differ only in the CLEARANCE, and deliberately so:
     *
     *   - HANGING: `section_hang_clearance_units` (24") is a TOTAL from the
     *     corner. It can be, because clothes project further than any carcass
     *     is deep, so the total already covers the neighbour's depth.
     *   - NOT HANGING: nothing projects, so a total would have to be compared
     *     against the neighbour's depth and would UNDER-reserve for anything
     *     deeper than it. Instead the neighbour's real depth is reserved and
     *     `section_corner_gap_units` (12") sits on top — the open space that
     *     the connecting shelf then bridges (owner, 2026-08-09: "a non-hanging
     *     corner gap should be set on the admin maintenance table, with a
     *     current default of 12\"", and "need open space and CSs for when two
     *     units in a corner do not include a hanging unit").
     *
     * Both feed the SAME `{ clearanceUnits, hangDepthUnits }` shape, so the
     * connecting-shelf builder below is untouched: it already spans whatever
     * is left of the clearance once the neighbour's depth is taken out.
     */
    const constraintFrom = (info, end) => {
      if (!info) return null;
      if (info.gapUnits >= reachAtCorner(end) - 1e-6) return null; // nowhere near this corner
      const depthUnits = info.section.depthUnits > 0
        ? info.section.depthUnits : rules.sectionDefaultDepthUnits;
      if (isHang(info.section)) {
        return { clearanceUnits: sectionWalkwayDepthUnits(info.section), hangDepthUnits: depthUnits, hanging: true };
      }
      const gap = Number.isFinite(rules.sectionCornerGapUnits) && rules.sectionCornerGapUnits >= 0
        ? rules.sectionCornerGapUnits : 0;
      if (!(gap > 0)) return null; // an admin can switch the behaviour off entirely
      return { clearanceUnits: depthUnits + gap, hangDepthUnits: depthUnits, hanging: false };
    };

    /**
     * An attached L-shelf OWNS its corner (owner, 2026-08-28): it is built to
     * abut flush, so neither the hang clearance nor the ADR 0069 corner gap
     * applies at that corner — on the host wall (a clearance would push the
     * shelf off its own corner) or on the attached wall (the leg reservation
     * already holds exactly the space the leg occupies). With no constraint
     * there is also no connecting shelf: "connecting sections/shelves are not
     * necessary for l-shelves".
     */
    const lShelfOwnsCorner = (end) => state.sections.some((s) => {
      if (!isLShelf(s) || !s.secondWallId) return false;
      if (s.wallId === wallId) return sharedCorner(s.wallId, s.secondWallId)?.aEnd === end;
      if (s.secondWallId === wallId) return sharedCorner(s.wallId, wallId)?.bEnd === end;
      return false;
    });

    const startInfo = cornerNeighbourInfo(wallId, 'start');
    const endInfo = cornerNeighbourInfo(wallId, 'end');
    const startNbr = startInfo?.section ?? null;
    const endNbr = endInfo?.section ?? null;
    const raw = {
      start: lShelfOwnsCorner('start') ? null : constraintFrom(startInfo, 'start'),
      end: lShelfOwnsCorner('end') ? null : constraintFrom(endInfo, 'end'),
    };

    // MY corner section can dive too: a hanging section with the override
    // extends to the corner, waiving the incoming clearance at that end — BUT
    // only one side of a corner may extend. Two facing hanging sections both
    // diving would occupy the same space (owner, 2026-07-27), so if BOTH claim
    // the override neither extends: the layout falls back to two clearances and
    // two shelves rather than a collision. (setSectionIntoHanging also revokes
    // the facing override on enable, so the UI never stores that state; this is
    // the defensive backstop for legacy/synced data.)
    const secs = sectionsForWall(wallId);
    const dives = (sec, end) => isHang(sec) && sectionIntoHangingAt(sec, end);
    const diving = (sec, end, facing, facingEnd, constraint) => constraint?.hanging === true
      && isHang(facing) && dives(sec, end) && !dives(facing, facingEnd);
    return {
      start: diving(secs[0], 'start', startNbr, 'end', raw.start) ? null : raw.start,
      end: diving(secs[secs.length - 1], 'end', endNbr, 'start', raw.end) ? null : raw.end,
      // The un-waived view, for the UI to offer/explain the override.
      rawStart: raw.start,
      rawEnd: raw.end,
    };
  }

  /**
   * Turns hanging-into-hanging on/off for a section (owner, 2026-07-27): the
   * hanging section extends to the corner, partially blocked by the
   * perpendicular hang — allowed but not advised, so callers show a warning
   * while it is on. Only meaningful on a hanging section at a corner; stored
   * regardless (it simply has no effect elsewhere).
   */
  function sectionIntoHangingAt(idOrSection, end) {
    const section = typeof idOrSection === 'object' && idOrSection !== null
      ? idOrSection : state.sections.find((s) => s.id === idOrSection);
    if (!section || !['start', 'end'].includes(end)) return false;
    const key = end === 'start' ? 'intoHangingStart' : 'intoHangingEnd';
    const hasEndpointState = typeof section.intoHangingStart === 'boolean'
      || typeof section.intoHangingEnd === 'boolean';
    return hasEndpointState ? section[key] === true : section.intoHanging === true;
  }

  function setSectionIntoHanging(id, endOrOn, maybeOn) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    const explicitEnd = ['start', 'end'].includes(endOrOn) ? endOrOn : null;
    const on = explicitEnd ? !!maybeOn : !!endOrOn;
    const secs = sectionsForWall(section.wallId);
    const cornerInfo = cornerHangInfo(section.wallId);
    const ends = explicitEnd ? [explicitEnd] : ['start', 'end'].filter((end) => (
      (end === 'start' ? secs[0]?.id === id : secs[secs.length - 1]?.id === id)
      && (!on || cornerInfo[end] === true)
    ));
    // First endpoint edit migrates an old section-wide flag without changing
    // its other corner. A new section already carries explicit false values.
    if (typeof section.intoHangingStart !== 'boolean' && typeof section.intoHangingEnd !== 'boolean') {
      section.intoHangingStart = section.intoHanging === true;
      section.intoHangingEnd = section.intoHanging === true;
    }
    for (const end of ends) {
      const key = end === 'start' ? 'intoHangingStart' : 'intoHangingEnd';
      section[key] = on;
    }
    section.intoHanging = section.intoHangingStart === true || section.intoHangingEnd === true;
    if (on) {
      // Only ONE side of a corner may extend into it — two facing hanging
      // sections both diving would be built in the same space (owner,
      // 2026-07-27). Enabling this override revokes it on whichever section
      // faces this one across each corner it dives into (mutual exclusion,
      // last-choice-wins — like a radio button spanning the corner).
      for (const end of ends) {
        const facing = cornerNeighbourSection(section.wallId, end);
        if (!facing) continue;
        if (typeof facing.intoHangingStart !== 'boolean' && typeof facing.intoHangingEnd !== 'boolean') {
          facing.intoHangingStart = facing.intoHanging === true;
          facing.intoHangingEnd = facing.intoHanging === true;
        }
        const facingKey = end === 'start' ? 'intoHangingEnd' : 'intoHangingStart';
        facing[facingKey] = false;
        facing.intoHanging = facing.intoHangingStart === true || facing.intoHangingEnd === true;
      }
    }
    return true;
  }

  /**
   * Turns a section's BOTTOM SHELF on/off. The flag feeds unitShelves(), so the
   * elevation/interior band and the base-trim calculation share one physical
   * truth: no bottom shelf means no rendered board and no base trim.
   */
  function setSectionBottomShelf(id, on) {
    const section = state.sections.find((s) => s.id === id);
    if (!section) return false;
    section.bottomShelf = !!on;
    return true;
  }

  function setConnectingShelf(id, end, change = {}) {
    const section = state.sections.find((s) => s.id === id);
    if (!section || !['start', 'end'].includes(end)) return false;
    const key = end === 'start' ? 'connectingShelfStart' : 'connectingShelfEnd';
    const current = section[key] && typeof section[key] === 'object'
      ? section[key] : { top: true, bottom: true, depthUnits: null };
    section[key] = {
      top: change.top === undefined ? current.top !== false : change.top === true,
      bottom: change.bottom === undefined ? current.bottom !== false : change.bottom === true,
      depthUnits: change.depthUnits === undefined
        ? (current.depthUnits > 0 ? current.depthUnits : null)
        : (change.depthUnits > 0 ? change.depthUnits : null),
    };
    return true;
  }

  /**
   * Turns CROWN on/off for the whole sketch (owner, 2026-08-10: "the old
   * Ballpark had this autocalced so it could be turned on/off"). The old tool
   * also let you adjust a SECTION COUNT because it had no drawing to measure;
   * here the linear feet come off the real section widths, so the count is
   * derived rather than typed.
   */
  function setCrownEnabled(on) {
    state.crownEnabled = !!on;
    return state.crownEnabled;
  }

  /** Turns BASE TRIM on/off for the whole sketch. Sections whose bottom shelf
   *  is off are excluded from its linear feet even when this is on. */
  function setBaseTrimEnabled(on) {
    state.baseTrimEnabled = !!on;
    return state.baseTrimEnabled;
  }

  /**
   * The END PANELS of a NEIGHBOURING wall's run that stand in front of this
   * wall, for its elevation (owner, 2026-08-05).
   *
   * A run's outermost panel is a board perpendicular to its own wall — which
   * makes it PARALLEL to the wall around the corner, standing in front of it
   * by the section's depth. So on this wall's elevation it is seen face-on: a
   * rectangle as wide as that section is deep, hard against the corner.
   *
   * Drawing it answers a question the elevation could not otherwise: whether an
   * outlet near the corner is actually reachable, or sits behind the cabinetry
   * on the wall next to it. It is an outline, not a section — nothing here is
   * built on THIS wall.
   *
   * Only drawn when the neighbour's run actually REACHES the corner. A run that
   * stops short leaves its panel far down the other wall; it would project onto
   * the same place in this view while blocking nothing, which is worse than
   * silence. That is the same proximity test the hang clearance uses (ADR 0048).
   *
   * @returns {Array<{atEnd:string, fromUnits:number, toUnits:number,
   *   bottomUnits:number, topUnits:number, depthUnits:number,
   *   wallId:string, sectionId:string}>}
   */
  function abuttingPanelsForWall(wallId) {
    const wall = resolveHost(wallId);
    if (!wall) return [];
    const runUnits = wallLengthUnits(wall);
    if (!(runUnits > 0)) return [];
    const out = [];
    for (const end of ['start', 'end']) {
      const info = cornerNeighbourInfo(wallId, end);
      if (!info?.section) continue;
      // How far the neighbour's run may stop short and still count. A business
      // rule (`abutting_panel_reach_units`), because the working figure is the
      // shop's to set: past some distance the panel plainly blocks nothing, and
      // where that line falls is a trade judgement, not arithmetic.
      //
      // The rule is stated the way a designer thinks: CLEAR SPACE between this
      // wall and the OUTSIDE FACE of the neighbour's end panel. `gapUnits` is
      // measured to the OPENING edge, one panel further in, so the panel
      // thickness is added here rather than mentally by whoever sets the rule
      // (owner, 2026-08-05 — they had to do that arithmetic by hand, which is
      // the tell that the rule was in the wrong terms). A run that fills its
      // wall has clear space = just the scribe reveal, so any rule value at or
      // above the reveal keeps it visible.
      const panel = rules.sectionPanelThicknessUnits ?? 0.75;
      const stated = Number.isFinite(rules.abuttingPanelReachUnits)
        ? rules.abuttingPanelReachUnits : 0;
      // Floor at the reveal: below it even a corner-tight run would vanish,
      // since its scribe gap alone exceeds the allowance.
      const reveal = rules.sectionEndRevealUnits ?? 0.75;
      const reach = Math.max(stated, reveal) + panel + 0.01;
      if (info.gapUnits > reach) continue; // its run stops short of this corner
      const depth = info.section.depthUnits > 0
        ? info.section.depthUnits
        : rules.sectionDefaultDepthUnits;
      if (!(depth > 0)) continue;
      // How tall the panel is: the CONTINUOUS carcass over that opening — the
      // run section plus every flush unit stacked on it — which is exactly the
      // extent one unbroken board spans (ADR 0041).
      const stack = stacksForWall(info.wall.id).find((s) => s.section?.id === info.section.id);
      if (!stack) continue;
      const extent = carcassExtent(stack.units, stack.item?.openingUnits);
      if (!extent || !(extent.topUnits > extent.bottomUnits)) continue;
      const width = Math.min(depth, runUnits);
      // HANG CLEARANCE at the corner (RS-4b). If the abutting section holds a
      // rod, what it consumes of THIS wall's end is not its carcass depth — it
      // is the clear space the clothes need, `section_hang_clearance_units`,
      // the same rule the plan already draws (ADR 0094) and the same rule the
      // corner solver already reserves along the run.
      //
      // Reported ALONGSIDE the panel, never instead of it: `depthUnits` is the
      // real board and drives the panel's own extent, the takeoff and the
      // corner arithmetic. The drawing then says both true things — the board
      // is this wide, the space it needs is that wide.
      //
      // Composition, not type code (ADR 0057): a designer who strips the rods
      // out of a DH stops needing the clearance, and one who adds a rod to an
      // SH starts.
      const clearance = sectionWalkwayDepthUnits(info.section);
      const reserves = sectionHasHang(info.section)
        && Number.isFinite(clearance) && clearance > depth + 1e-9;
      const clearWidth = reserves ? Math.min(clearance, runUnits) : 0;
      out.push({
        atEnd: end,
        fromUnits: end === 'start' ? 0 : runUnits - width,
        toUnits: end === 'start' ? width : runUnits,
        bottomUnits: extent.bottomUnits,
        topUnits: extent.topUnits,
        depthUnits: depth,
        // The band BEYOND the panel, or null when nothing hangs there. Given as
        // wall coordinates so the renderer does no arithmetic about which end.
        clearanceUnits: reserves ? clearance : null,
        clearanceFromUnits: reserves ? (end === 'start' ? width : runUnits - clearWidth) : null,
        clearanceToUnits: reserves ? (end === 'start' ? clearWidth : runUnits - width) : null,
        wallId: info.wall.id,
        sectionId: info.section.id,
      });
    }
    return out;
  }

  /** The clearance situation at a wall's ends, for the section-row UI. */
  function cornerHangInfo(wallId) {
    const cons = sectionEndConstraints(wallId);
    return { start: cons.rawStart?.hanging === true, end: cons.rawEnd?.hanging === true };
  }

  /**
   * Connecting shelves this wall must carry (ADR 0038): where a neighbour's
   * hanging section reserves a clearance zone here, a shelf bridges the corner
   * from the hanging section's FRONT edge to this wall's outermost panel —
   * clearance − hangDepth wide, as deep as this wall's corner section. Only
   * present when this wall actually has a section run to connect to. Wall-
   * local coordinates: fromUnits/toUnits along the wall, depthUnits inward.
   */
  function connectingShelvesForWall(wallId) {
    const secs = sectionsForWall(wallId);
    const wall = state.walls.find((w) => w.id === wallId);
    if (!wall || !secs.length) return [];
    const cons = sectionEndConstraints(wallId);
    const L = wallLengthUnits(wall);
    const depthOf = (s) => (s.depthUnits > 0 ? s.depthUnits : rules.sectionDefaultDepthUnits);
    const shelves = [];
    const optionsFor = (section, end) => {
      const value = section?.[end === 'start' ? 'connectingShelfStart' : 'connectingShelfEnd'];
      return value && typeof value === 'object'
        ? { top: value.top !== false, bottom: value.bottom !== false, depthUnits: value.depthUnits > 0 ? value.depthUnits : null }
        : { top: true, bottom: true, depthUnits: null };
    };
    if (cons.start && cons.start.clearanceUnits - cons.start.hangDepthUnits > 0.01) {
      const options = optionsFor(secs[0], 'start');
      shelves.push({
        end: 'start', sectionId: secs[0].id,
        fromUnits: cons.start.hangDepthUnits,
        toUnits: cons.start.clearanceUnits,
        widthUnits: cons.start.clearanceUnits - cons.start.hangDepthUnits,
        depthUnits: options.depthUnits ?? depthOf(secs[0]),
        depthOverrideUnits: options.depthUnits,
        topShelf: options.top,
        bottomShelf: options.bottom,
      });
    }
    if (cons.end && cons.end.clearanceUnits - cons.end.hangDepthUnits > 0.01) {
      const options = optionsFor(secs[secs.length - 1], 'end');
      shelves.push({
        end: 'end', sectionId: secs[secs.length - 1].id,
        fromUnits: L - cons.end.clearanceUnits,
        toUnits: L - cons.end.hangDepthUnits,
        widthUnits: cons.end.clearanceUnits - cons.end.hangDepthUnits,
        depthUnits: options.depthUnits ?? depthOf(secs[secs.length - 1]),
        depthOverrideUnits: options.depthUnits,
        topShelf: options.top,
        bottomShelf: options.bottom,
      });
    }
    return shelves;
  }

  /**
   * The solved layout for a wall's sections — positions for every panel and
   * opening plus the narrow/wide/overflow flags — or null when the wall has
   * none. Derived fresh from the wall's CURRENT length every call, which is
   * the point: re-measure the wall (or change the NEIGHBOUR's corner section,
   * which drives the hang clearances) and the sections re-flow.
   */
  function sectionLayoutForWall(wallId) {
    const cons = sectionEndConstraints(wallId);
    // An L-shelf on a neighbouring wall reaches into THIS run at the shared
    // corner, so that space is reserved exactly like a hang clearance — the
    // larger of the two wins when both apply at one end (ADR 0042).
    const lRes = lShelfReservations(wallId);
    return layoutWallRun(
      wallId,
      Math.max(cons.start?.clearanceUnits ?? 0, lRes.startUnits),
      Math.max(cons.end?.clearanceUnits ?? 0, lRes.endUnits),
    );
  }

  /**
   * The wall's run laid out under the clearances the CALLER states, rather than
   * the ones the model derives. Split out of `sectionLayoutForWall` so the
   * clearance derivation itself can ask for a clearance-FREE layout without
   * recursing into itself — see `bareLayoutForWall`.
   */
  function layoutWallRun(wallId, startClearanceUnits, endClearanceUnits) {
    const wall = resolveHost(wallId);
    if (!wall) return null;
    // OPEN sections (rule section_open_codes) are deliberate empty space: the
    // solver skips the panels that would exist solely to bound them and
    // exempts them from the width cap / narrow warnings. Each section also
    // carries its TYPE's minimum width when defined (ADR 0039).
    const openCodes = new Set((rules.sectionOpenCodes ?? []).map((c) => String(c).toUpperCase()));
    const presets = new Map((rules.sectionTypePresets ?? []).map((p) => [String(p.code).toUpperCase(), p]));
    const run = sectionsForWall(wallId);
    const secs = run.map((s, i) => ({
      ...s,
      open: openCodes.has(String(s.typeCode).toUpperCase()),
      minOpeningUnits: presets.get(String(s.typeCode).toUpperCase())?.minWidthUnits ?? null,
      // An L-shelf NEVER shares a panel (owner, 2026-07-28): there must be two
      // panels between it and any neighbour, including another L-shelf. The
      // join AFTER this section is doubled when either side of it is an
      // L-shelf, on top of any per-join override the designer set.
      fullPanelAfter: s.fullPanelAfter === true || isLShelf(s) || isLShelf(run[i + 1]),
      // An L-shelf attached across a corner abuts it (owner, 2026-08-28): the
      // solver lays the run's slack before a trailing end-pinned shelf so it
      // sits flush against the wall it reaches onto.
      pinEnd: isLShelf(s) && s.secondWallId
        ? sharedCorner(s.wallId, s.secondWallId)?.aEnd ?? null
        : null,
    }));
    return layoutSections(wallLengthUnits(wall), secs, rules, {
      startClearanceUnits,
      endClearanceUnits,
    });
  }

  /**
   * The wall's run with NO end clearances at all.
   *
   * This is the escape from an otherwise circular question: wall A's clearance
   * depends on where wall B's sections sit, which depends on B's clearance,
   * which depends on A. A clearance only ever pushes a run AWAY from a corner,
   * so the clearance-free layout is where the run sits at its CLOSEST to each
   * corner. Judging proximity from it can only ever over-reserve, never
   * under-reserve, and it terminates immediately.
   */
  function bareLayoutForWall(wallId) {
    return layoutWallRun(wallId, 0, 0);
  }

  // ---- plan depth helper lines (ADR 0024) ----------------------------------

  /**
   * Adds a depth line at `offsetUnits` from a wall's inside face. Returns the
   * new line's id, or null (unknown/opening wall, or a non-positive offset).
   * Duplicates at the same offset on the same wall are ignored.
   */
  function addPlanHelperLine(wallId, offsetUnits) {
    const wall = state.walls.find((w) => w.id === wallId);
    if (!wall || (wall.segmentKind ?? 'wall') !== 'wall') return null;
    const offset = Number(offsetUnits);
    if (!Number.isFinite(offset) || offset <= 0) return null;
    if (state.planHelperLines.some((h) => h.wallId === wallId && Math.abs(h.offsetUnits - offset) < 1e-6)) return null;
    const line = { id: makeId(), wallId, offsetUnits: offset };
    state.planHelperLines.push(line);
    return line.id;
  }

  function removePlanHelperLine(id) {
    const before = state.planHelperLines.length;
    state.planHelperLines = state.planHelperLines.filter((h) => h.id !== id);
    return state.planHelperLines.length !== before;
  }

  /** Depth lines on a wall, nearest-first. */
  function planHelperLinesFor(wallId) {
    return state.planHelperLines.filter((h) => h.wallId === wallId).sort((a, b) => a.offsetUnits - b.offsetUnits);
  }

  /** Show/hide the to-scale grid. Omit `visible` to toggle. */
  function setGridVisible(visible) {
    state.gridVisible = visible === undefined ? !state.gridVisible : Boolean(visible);
    return state.gridVisible;
  }

  /** Show/hide the elevation's SHELF SPACING dimensions. Omit to toggle.
   *  (ADR 0061 item E — "toggle on/off the display of spacing between shelves
   *  as well as drawer face heights v. requiring designers to open and click
   *  on each item".) */
  function setShelfSpacingDimsVisible(visible) {
    state.shelfSpacingDimsVisible = visible === undefined ? !state.shelfSpacingDimsVisible : Boolean(visible);
    return state.shelfSpacingDimsVisible;
  }

  /** Show/hide the elevation's DRAWER FACE height labels. Omit to toggle. */
  function setDrawerFaceDimsVisible(visible) {
    state.drawerFaceDimsVisible = visible === undefined ? !state.drawerFaceDimsVisible : Boolean(visible);
    return state.drawerFaceDimsVisible;
  }

  /** Show/hide the storage-summary table. Omit `visible` to toggle. */
  function setStorageSummaryVisible(visible) {
    state.storageSummaryVisible = visible === undefined ? !state.storageSummaryVisible : Boolean(visible);
    return state.storageSummaryVisible;
  }

  /** Show/hide ALL depth lines without deleting them. Omit to toggle. */
  function setPlanHelperLinesVisible(visible) {
    state.planHelperLinesVisible = visible === undefined ? !state.planHelperLinesVisible : Boolean(visible);
    return state.planHelperLinesVisible;
  }

  /** Display length for a wall: the measured value, else the solved px length. */
  function displayLength(wall) {
    // A partition's "length" is how far it sticks into the room — its depth —
    // not the footprint's thickness. Its thickness shows in the THICK column.
    if (isPartition(wall.id)) {
      const d = partitionDepthFor(wall.id);
      return d != null ? formatLength(d, rules.measurementLabelFormat) : null;
    }
    if (wall.isMeasured && wall.lengthUnits != null) {
      return formatLength(wall.lengthUnits, rules.measurementLabelFormat);
    }
    return null; // solver-derived; the table shows a placeholder rather than px
  }

  /**
   * The solver-derived length for an UNMEASURED wall, formatted for display in
   * the length input's placeholder. This lets the designer see the length the
   * closure solver auto-generated instead of guessing at it — while the input's
   * value stays empty, so the wall remains a free (flexing) wall until they
   * actually type a fixed length. Null for measured walls, partitions, and
   * openings (which have a real displayLength), and before the room is solved.
   */
  function autoLength(wall) {
    if (wall.isMeasured) return null;
    if (isPartition(wall.id)) return null;
    if ((wall.segmentKind ?? 'wall') !== 'wall') return null;
    if (!state.pxPerUnit || state.pxPerUnit <= 0) return null;
    return formatLength(wallLengthUnits(wall), rules.measurementLabelFormat);
  }

  /**
   * Display angle for a wall, in the Cabinet Vision signed convention — the
   * canonical stored value (ADR 0004) and what a designer needs to reconstruct
   * the shape from the table alone.
   */
  function displayAngle(wall) {
    if (wall.angleDeg == null) return '';
    return `${wall.angleDeg}°`;
  }

  return {
    state,
    allPoints,
    beginStroke,
    extendStroke,
    endStroke,
    addStroke,
    undoStroke,
    clear,
    cleanup,
    selectWall,
    setWallLength,
    setWallMeasuredLength,
    confirmWallLength,
    setWallAngle,
    setSnapClosed,
    setWallSnapLocked,
    setWallOne,
    clearWallOne,
    isWallOnePinned,
    addDoor,
    removeDoor,
    deleteWall,
    wallDeletionImpact,
    selectedSegment,
    toggleElevation,
    setWallThickness,
    setCeilingHeight,
    setCeilingFlat,
    cornerHeightUnits,
    setCornerHeight,
    wallHeightProfileFor,
    wallHeightBreaksFor,
    addWallHeightBreak,
    removeWallHeightBreak,
    uniformWallHeightUnits,
    setDoorHeight,
    setHelperLineVisible,
    isHelperLineVisible,
    customHelperLines,
    setCustomHelperLine,
    setCustomHelperVisible,
    addCeilingOpening,
    updateCeilingOpening,
    removeCeilingOpening,
    addObstruction,
    setWallOpening,
    moveObstruction,
    setObstructionLocked,
    setObstructionReferenceEdge,
    obstructionReferenceOffset,
    updateObstructionReferenceOffset,
    removeObstruction,
    updateObstruction,
    addAnnotation,
    addTextAnnotation,
    setTextAnnotationSize,
    moveTextAnnotation,
    annotationsFor,
    removeAnnotation,
    clearStrokeAnnotations,
    clearAnnotations,
    addPlanHelperLine,
    removePlanHelperLine,
    planHelperLinesFor,
    addWallLabel,
    moveWallLabel,
    removeWallLabel,
    wallLabelsForWall,
    addSection,
    addSectionComponent,
    setSectionWidth,
    unlockSection,
    setSectionAllowWide,
    toggleSectionJoin,
    setSectionDim,
    setSectionCount,
    // Composable interiors (ADR 0054); interiorForUnit is the 4b elevation
    // feed (ADR 0055).
    sectionInterior,
    sectionHasHang,
    sectionWalkwayDepthUnits,
    sectionHasShoeShelves,
    sectionIntoHangingAt,
    interiorForUnit,
    setSectionInterior,
    setSectionDoors,
    setSectionRequiredHolesOnly,
    setSectionIntoHanging,
    setSectionBottomShelf,
    setConnectingShelf,
    setCrownEnabled,
    setBaseTrimEnabled,
    cornerHangInfo,
    setSpaceHeight,
    setPlatformHeight,
    // Vertical stacking (ADR 0041)
    stackUnit,
    isStackableType,
    presetFlagsFor,
    unstackUnit,
    stackChainFor,
    stacksForWall,
    sectionAnchor,
    isLShelf,
    // L-shelves (ADR 0042)
    sharedCorner,
    setLShelfSecondWall,
    setSectionElevCodeOffset,
    setLShelfLeg,
    lShelfLegs,
    lShelfReservations,
    lShelfSecondLegsForWall,
    sectionLShelfInfo,
    lShelfCandidateWalls,
    L_SHELF_MAX_LEG_UNITS,
    stackedDepthUnits,
    stackedDimsAreFree,
    setStackOffset,
    setCountertopThickness,
    setSectionCountertop,
    setCrownAllowance,
    setCrownMouldingHeight,
    crownMouldingHeightUnits,
    setBaseTrimHeight,
    baseTrimHeightUnits,
    crownAllowanceUnits,
    stackTopUnits,
    setToTargetHeight,
    setStackTargetHeight,
    stackTargetHeightUnits,
    setWallStackTarget,
    wallStackTargetUnits,
    setStackTargetTop,
    sectionHeightUnits,
    sectionHeightSource,
    sectionHeightIsFixed,
    sectionHeightMaxUnits,
    systemPanelHeight,
    setSharedSpace,
    setPersonName,
    assignSection,
    moveSection,
    reorderSection,
    removeSection,
    sectionsForWall,
    sectionLayoutForWall,
    connectingShelvesForWall,
    abuttingPanelsForWall,
    storageSummary,
    storageNeedTypes,
    setStorageNeed,
    setShoeInventoryGroups,
    addShoeInventoryGroup,
    updateShoeInventoryGroup,
    removeShoeInventoryGroup,
    markStorageNeedsClientProvided,
    storageNeedFor,
    resolvedStorageNeed,
    setStorageCompareTo,
    // Space pricing choices + totals stamp (ADR 0058, plan §5/4e).
    setPricingLevel,
    setPricingCountertopMaterial,
    setPricingTotals,
    // Basic mode: staged bucket cards (ADR 0059).
    setBasicMode,
    setBasicSpaceType,
    basicBounds,
    setBasicBound,
    addBucketCard,
    setBucketCardQty,
    setBucketCardDrawers,
    assignBucketCard,
    placeBucketUnit,
    addPartition,
    removePartition,
    setPartitionDepth,
    setPartitionElevation,
    partitionElevationFor,
    isPartition,
    partitionDepthFor,
    partitionSegments,
    partitionFaceWalls,
    setGridVisible,
    setShelfSpacingDimsVisible,
    setDrawerFaceDimsVisible,
    setStorageSummaryVisible,
    setPlanHelperLinesVisible,
    obstructionsForWall,
    obstructionCodes,
    obstructionCode,
    measurementSheet,
    wallLengthUnits,
    resolve,
    displayLength,
    autoLength,
    displayAngle,
    displayThickness,
  };
}
